
import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Polygon as LeafletPolygon, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import axios from 'axios';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { FaCalendarAlt, FaClock, FaEdit, FaExpand, FaFileAlt, FaFlag, FaInfoCircle, FaLink, FaMapMarkedAlt, FaMapMarkerAlt, FaMinus, FaPlus, FaRoad, FaRoute, FaSave, FaSearch, FaTable, FaTimes, FaTrash } from 'react-icons/fa';
import './MonitoramentoModule.css';
import PoligonoEditor from './PoligonoEditor';
import AlarmeVeiculoParado from './AlarmeVeiculoParado';
import { formatarDataSemTimezone, formatarHoraSemTimezone } from './dateUtils';

import TabelaLocalizacaoCarretas from './TabelaLocalizacaoCarretas';
import TrackingModal from './TrackingModal';

// Hook para mapear placa => array de poligonos/tempos
function usePoligonosTempoParadoPorPlaca(dadosParados: any[]): Map<string, any[]> {
  return React.useMemo(() => {
    const map = new Map<string, any[]>();
    for (const v of dadosParados) {
      if (v && v.placa) map.set(v.placa, v.poligonos || []);
    }
    return map;
  }, [dadosParados]);
}

// Hook para buscar placas paradas em polígono
export function usePlacasParadasPoligono() {
  const [dados, setDados] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(!document.hidden);
  const fetchDados = async () => {
    setLoading(true);
    try {
      const resp = await axios.get('/api/monitoramento/placas-paradas-poligono');
      setDados(resp.data || []);
    } catch {
      setDados([]);
    }
    setLoading(false);
  };
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);
  React.useEffect(() => {
    if (!isVisible) return;
    fetchDados();
    const interval = setInterval(fetchDados, 30000);
    return () => clearInterval(interval);
  }, [isVisible]);
  return { dados, loading, refetch: fetchDados };
}

// Fix para ícones do Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const origemMarkerIcon = L.divIcon({
  className: 'rota-ponto-icon rota-ponto-origem',
  html: '<span>O</span>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const destinoMarkerIcon = L.divIcon({
  className: 'rota-ponto-icon rota-ponto-destino',
  html: '<span>D</span>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

interface AgendaFixaTvItem {
  dia: string;
  fornecedor: string;
  horario: string;
}

const TV_DIAS_AGENDA = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
const TV_AGENDA_FIXA: AgendaFixaTvItem[] = [
  { dia: 'Segunda', fornecedor: 'Estampo Tec', horario: '14h na Estampo' },
  { dia: 'Segunda', fornecedor: 'Brose Pinhais', horario: '07h na Brose SJP' },
  { dia: 'Terça', fornecedor: 'Autoplas', horario: '19h na Autoplas • Entrega em Jarinu às 19h' },
  { dia: 'Terça', fornecedor: 'Brose Jarinu', horario: '11h em Jarinu' },
  { dia: 'Quarta', fornecedor: 'Estampo Tec', horario: '09h na Estampo' },
  { dia: 'Quarta', fornecedor: 'Pro Metal', horario: '11h na Prometal' },
  { dia: 'Quarta', fornecedor: 'Brose Pinhais', horario: '07h na Brose SJP' },
  { dia: 'Quinta', fornecedor: 'Brose Pinhais', horario: '07h na Brose SJP' },
  { dia: 'Quinta', fornecedor: 'Autoplas', horario: '19h na Autoplas • Entrega em Jarinu às 19h' },
  { dia: 'Sexta', fornecedor: 'Brose Goiana', horario: 'Janela às 06h' },
  { dia: 'Sábado', fornecedor: 'Autoplas', horario: '08h na Autoplas' },
];

interface PontoRotaEdicao {
  lat: string;
  lng: string;
  endereco?: string;
}

interface RouteDragSession {
  insertIndex: number;
  current: [number, number];
  start: [number, number];
  end: [number, number];
}

interface PontoAncoraRota {
  lat: number;
  lng: number;
  tecnico: boolean;
}

function RouteMapAutoFit({ pontos }: { pontos: Array<[number, number]> }) {
  const map = useMap();

  useEffect(() => {
    if (!pontos.length) return;

    const timeout = window.setTimeout(() => {
      if (pontos.length === 1) {
        map.setView(pontos[0], 12);
      } else {
        map.fitBounds(pontos, { padding: [30, 30] });
      }
      map.invalidateSize();
    }, 100);

    return () => window.clearTimeout(timeout);
  }, [map, pontos]);

  return null;
}

function RouteLineDragEvents({
  dragSession,
  onMove,
  onEnd,
}: {
  dragSession: RouteDragSession | null;
  onMove: (lat: number, lng: number) => void;
  onEnd: () => void;
}) {
  const map = useMap();

  useEffect(() => {
    if (dragSession) {
      map.dragging.disable();
    } else {
      map.dragging.enable();
    }

    return () => {
      map.dragging.enable();
    };
  }, [dragSession, map]);

  useMapEvents({
    mousemove(e) {
      if (!dragSession) return;
      onMove(e.latlng.lat, e.latlng.lng);
    },
    mouseup() {
      if (!dragSession) return;
      onEnd();
    },
    mouseout() {
      if (!dragSession) return;
      onEnd();
    },
  });

  return null;
}

function EditableRouteMap({
  pontos,
  cor,
  onChange,
  onDetailedGeometryChange,
}: {
  pontos: PontoRotaEdicao[];
  cor: string;
  onChange: (pontos: PontoRotaEdicao[]) => void;
  onDetailedGeometryChange?: (pontosDetalhados: Array<{ lat: number; lng: number }>) => void;
}) {
  const pontosValidos = useMemo(
    () =>
      pontos
        .map((ponto, index) => ({
          index,
          lat: Number(ponto.lat),
          lng: Number(ponto.lng),
        }))
        .filter(ponto => Number.isFinite(ponto.lat) && Number.isFinite(ponto.lng)),
    [pontos]
  );

  const center = useMemo<[number, number]>(() => {
    if (pontosValidos.length > 0) {
      const lat = pontosValidos.reduce((sum, ponto) => sum + ponto.lat, 0) / pontosValidos.length;
      const lng = pontosValidos.reduce((sum, ponto) => sum + ponto.lng, 0) / pontosValidos.length;
      return [lat, lng];
    }

    return [-23.5505, -46.6333];
  }, [pontosValidos]);

  const [routeGeometry, setRouteGeometry] = useState<Array<[number, number]>>([]);
  const [routeGeometryLoading, setRouteGeometryLoading] = useState(false);
  const [pontosAncora, setPontosAncora] = useState<PontoAncoraRota[]>([]);
  const [dragSession, setDragSession] = useState<RouteDragSession | null>(null);

  useEffect(() => {
    setPontosAncora(
      pontosValidos.map(ponto => ({
        lat: ponto.lat,
        lng: ponto.lng,
        tecnico: false,
      }))
    );
    setDragSession(null);
  }, [pontosValidos]);

  useEffect(() => {
    let cancelled = false;

    const carregarGeometria = async () => {
      if (pontosAncora.length < 2) {
        setRouteGeometry([]);
        return;
      }

      setRouteGeometryLoading(true);

      try {
        const coords = pontosAncora.map(ponto => `${ponto.lng},${ponto.lat}`).join(';');
        const response = await fetch(
          `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`
        );
        const data = await response.json();

        const geometry = data?.routes?.[0]?.geometry?.coordinates;
        if (!cancelled && Array.isArray(geometry) && geometry.length >= 2) {
          setRouteGeometry(geometry.map(([lng, lat]: [number, number]) => [lat, lng]));
        } else if (!cancelled) {
          setRouteGeometry(pontosAncora.map(ponto => [ponto.lat, ponto.lng]));
        }
      } catch (error) {
        if (!cancelled) {
          setRouteGeometry(pontosAncora.map(ponto => [ponto.lat, ponto.lng]));
        }
      } finally {
        if (!cancelled) {
          setRouteGeometryLoading(false);
        }
      }
    };

    carregarGeometria();

    return () => {
      cancelled = true;
    };
  }, [pontosAncora]);

  useEffect(() => {
    if (!onDetailedGeometryChange) return;
    onDetailedGeometryChange(routeGeometry.map(([lat, lng]) => ({ lat, lng })));
  }, [routeGeometry, onDetailedGeometryChange]);

  const removerPonto = (index: number) => {
    const next = pontos.filter((_, pontoIndex) => pontoIndex !== index);
    onChange(next.length > 0 ? next : [{ lat: '', lng: '', endereco: '' }]);
  };

  const calcularDistanciaSegmento = (
    p: [number, number],
    a: [number, number],
    b: [number, number]
  ) => {
    const [px, py] = p;
    const [ax, ay] = a;
    const [bx, by] = b;
    const abx = bx - ax;
    const aby = by - ay;
    const apx = px - ax;
    const apy = py - ay;
    const ab2 = abx * abx + aby * aby;
    if (ab2 === 0) return Math.hypot(px - ax, py - ay);
    const t = Math.max(0, Math.min(1, (apx * abx + apy * aby) / ab2));
    const projx = ax + t * abx;
    const projy = ay + t * aby;
    return Math.hypot(px - projx, py - projy);
  };

  const getSegmentoMaisProximo = (lat: number, lng: number) => {
    if (pontosAncora.length < 2) return null;

    let menorDistancia = Number.POSITIVE_INFINITY;
    let melhorIndice = 0;

    for (let i = 0; i < pontosAncora.length - 1; i += 1) {
      const atual = pontosAncora[i];
      const proximo = pontosAncora[i + 1];
      const distancia = calcularDistanciaSegmento(
        [lat, lng],
        [atual.lat, atual.lng],
        [proximo.lat, proximo.lng]
      );

      if (distancia < menorDistancia) {
        menorDistancia = distancia;
        melhorIndice = i;
      }
    }

    const inicio = pontosAncora[melhorIndice];
    const fim = pontosAncora[melhorIndice + 1];
    if (!inicio || !fim) return null;

    return {
      insertIndex: melhorIndice + 1,
      start: [inicio.lat, inicio.lng] as [number, number],
      end: [fim.lat, fim.lng] as [number, number],
    };
  };

  const iniciarAjusteLinha = (lat: number, lng: number) => {
    const segmento = getSegmentoMaisProximo(lat, lng);
    if (!segmento) return;

    setDragSession({
      insertIndex: segmento.insertIndex,
      current: [lat, lng],
      start: segmento.start,
      end: segmento.end,
    });
  };

  const finalizarAjusteLinha = () => {
    if (!dragSession) return;

    setPontosAncora(prev => {
      const next = [...prev];
      next.splice(dragSession.insertIndex, 0, {
        lat: dragSession.current[0],
        lng: dragSession.current[1],
        tecnico: true,
      });
      return next;
    });

    setDragSession(null);
  };

  return (
    <div className="rota-editor-map">
      <div className="rota-editor-map-header">
        <strong>Pré-visualização da rota</strong>
        <span>
          {routeGeometryLoading
            ? 'Calculando caminho real da via...'
            : 'Clique e arraste a linha da rota para forçar a passagem por um local, sem criar parada visível.'}
        </span>
      </div>
      <div className="rota-editor-map-canvas">
        <MapContainer center={center} zoom={12} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <RouteMapAutoFit pontos={pontosValidos.map(ponto => [ponto.lat, ponto.lng])} />
          <RouteLineDragEvents
            dragSession={dragSession}
            onMove={(lat, lng) => setDragSession(prev => prev ? { ...prev, current: [lat, lng] } : prev)}
            onEnd={finalizarAjusteLinha}
          />

          {pontosValidos.map((ponto, order) => (
            (() => {
              const isOrigem = order === 0;
              const isDestino = order === pontosValidos.length - 1;
              const labelTipo = isOrigem ? 'Origem' : isDestino ? 'Destino' : `Ponto ${order + 1}`;
              const icon = isOrigem ? origemMarkerIcon : isDestino ? destinoMarkerIcon : undefined;

              return (
            <Marker
              key={`${ponto.index}-${ponto.lat}-${ponto.lng}`}
              position={[ponto.lat, ponto.lng]}
              draggable={false}
              bubblingMouseEvents={false}
              icon={icon}
            >
              <Popup>
                <div className="marker-popup">
                  <strong>{labelTipo}</strong>
                  <div>{ponto.lat.toFixed(6)}, {ponto.lng.toFixed(6)}</div>
                  {!isOrigem && !isDestino && (
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ marginTop: 8 }}
                      onClick={() => removerPonto(ponto.index)}
                    >
                      Remover ponto
                    </button>
                  )}
                </div>
              </Popup>
            </Marker>
              );
            })()
          ))}

          {dragSession && (
            <Polyline
              positions={[dragSession.start, dragSession.current, dragSession.end]}
              color="#ff7043"
              weight={4}
              opacity={0.8}
              dashArray="8, 8"
            />
          )}

          {routeGeometry.length >= 2 && (
            <>
              <Polyline
                positions={routeGeometry}
                color={cor}
                weight={5}
                opacity={0.9}
              />
              <Polyline
                positions={routeGeometry}
                color={cor}
                weight={22}
                opacity={0}
                eventHandlers={{
                  mousedown: event => {
                    event.originalEvent.preventDefault();
                    event.originalEvent.stopPropagation();
                    iniciarAjusteLinha(event.latlng.lat, event.latlng.lng);
                  }
                }}
              />
            </>
          )}
        </MapContainer>
      </div>
    </div>
  );
}

// Componente auxiliar para redimensionar o mapa quando expandido
function MapResizer({ trigger }: { trigger: boolean }) {
  const map = useMap();
  React.useEffect(() => {
    setTimeout(() => {
      map.invalidateSize();
    }, 100);
  }, [trigger, map]);
  return null;
}

function MapAutoFit({ pontos }: { pontos: Array<[number, number]> }) {
  const map = useMap();

  useEffect(() => {
    const pontosValidos = pontos.filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));
    if (!pontosValidos.length) return;

    const timeout = window.setTimeout(() => {
      if (pontosValidos.length === 1) {
        map.setView(pontosValidos[0], 12);
      } else {
        map.fitBounds(pontosValidos, { padding: [48, 48] });
      }
      map.invalidateSize();
    }, 120);

    return () => window.clearTimeout(timeout);
  }, [map, pontos]);

  return null;
}

function MapFocusPoint({ ponto, zoom }: { ponto: [number, number] | null; zoom: number }) {
  const map = useMap();

  useEffect(() => {
    if (!ponto) return;
    const timeout = window.setTimeout(() => {
      map.setView(ponto, zoom, { animate: true });
      map.invalidateSize();
    }, 160);

    return () => window.clearTimeout(timeout);
  }, [map, ponto, zoom]);

  return null;
}

interface Veiculo {
  id: number;
  placa: string;
  placaCarreta?: string;
  sistema?: string;
  motorista: string;
  dataCVA?: string;
  cva?: string;
  telefones?: string[];
  cpfMotorista?: string;
  latitude?: number;
  longitude?: number;
  responsavel?: string;
  status: string;
  velocidade: number;
  localizacao?: { lat: number; lng: number; endereco?: string };
  ultimaAtualizacao?: string;
  ultimaComunicacao?: string;
  ultimaAtualizacaoOmnilink?: string;
  ultimaComunicacaoOmnilink?: string;
  ultimaAtualizacaoTraccar?: string;
  ultimaComunicacaoTraccar?: string;
  tempoParado?: number;
  operacao?: string;
  poligono?: string;
  sentidoRota?: 'ida' | 'volta' | 'desconhecido';
  confiancaSentido?: number;
  problemaRastreador?: boolean;
  emManutencao?: boolean;
  dtEntradaManutencao?: string;
  obsManutencao?: string;
  distanciaRestante?: number | string;
  ultimoPoligono?: string;
  horarioSaidaUltimoPoligono?: string;
  statusCarregamento?: string;
  tipoComunicacao?: string; // 'GPS' ou 'SATELITE'
  numeroManifesto?: string;
  dataManifesto?: string;
  numeroContrato?: string;
}

const LocalizacaoSemanalTv: React.FC<{
  lat?: number | null;
  lng?: number | null;
  localizacaoInicial?: string;
  fonte?: string;
  comunicacao?: string;
}> = ({ lat, lng, localizacaoInicial = '-', fonte = '-', comunicacao = '-' }) => {
  const [localizacao, setLocalizacao] = useState(localizacaoInicial);

  useEffect(() => {
    setLocalizacao(localizacaoInicial);
    if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return;
    if (localizacaoInicial && localizacaoInicial !== '-') return;

    const controller = new AbortController();
    axios.get('/api/monitoramento/reverse-geocode', {
      params: { lat, lng },
      signal: controller.signal
    }).then(resposta => {
      const address = resposta.data?.address || {};
      const cidade = address.city || address.town || address.municipality || address.village || '';
      const codigoEstado = String(address['ISO3166-2-lvl4'] || '').split('-').pop();
      const endereco = cidade
        ? `${cidade}${codigoEstado ? `/${codigoEstado}` : ''}`
        : String(resposta.data?.endereco || '').trim();
      setLocalizacao(endereco || '-');
    }).catch(erro => {
      if (erro?.code !== 'ERR_CANCELED') setLocalizacao('-');
    });

    return () => controller.abort();
  }, [lat, lng, localizacaoInicial]);

  return <>
    <strong>{localizacao}</strong>
    <span>Fonte: {fonte} • Atualizado: {comunicacao}</span>
  </>;
};

interface TvDadosViagem {
  placa: string;
  placaCarreta?: string | null;
  motorista?: string | null;
  telefone?: string | null;
  cpfMotorista?: string | null;
  empresaColeta?: string | null;
  enderecoColeta?: string | null;
  empresaDestino?: string | null;
  enderecoDestino?: string | null;
  ordemColeta?: number | string | null;
  dataColeta?: string | null;
  horarioChegadaColeta?: string | null;
  tipoHorarioChegadaColeta?: 'realizado' | 'estimado' | null;
  motivoHorarioChegadaColeta?: string | null;
  previsaoEntrega?: string | null;
  numeroManifesto?: string | null;
  dataManifesto?: string | null;
  numeroContrato?: string | null;
  dataContrato?: string | null;
  distanciaOrigemDestinoKm?: number | null;
  distanciaAteDestinoKm?: number | null;
  velocidadeAtual?: number | null;
  transitTimePrevisto?: string | null;
  transitTimeReal?: string | null;
  tipoHorarioChegadaDestino?: 'realizado' | 'estimado' | null;
  tempoAteDestino?: string | null;
  motivoTransitTimePrevisto?: string | null;
  motivoTransitTimeReal?: string | null;
  motivoTempoAteDestino?: string | null;
}


interface VeiculoTimeline {
  placa: string;
  motorista: string;
  distanciaRestante: number;
  carrocerias: number;
  velocidadeMedia: number;
  tempoEstimado: string;
  horaChegada: string;
  ultimaAtualizacao: string;
  poligono?: string;
  statusVeiculo?: string;
  horarioSaidaOrigem?: string;
  cva?: string;
  motoristaNucci?: string;
}

interface Estatisticas {
  totalVeiculos: number;
  emMovimento: number;
  parados: number;
  foraDeRota: number;
  problemaRastreador: number;
  emManutencao: number;
  ultimaAtualizacao: string;
}

interface Rota {
  id: number;
  nome: string;
  origem: string;
  destino: string;
  pontosParada?: any;
  pontos: Array<{ lat: number; lng: number }>;
  pontosDetalhados?: Array<{ lat: number; lng: number }>;
  distancia: number;
  tempoEstimado: number;
  cor: string;
}

interface Operacao {
  id?: number;
  nome: string;
  descricao?: string;
  setorOperacao?: string;
  rotas: number[];
  veiculos: string[]; // Changed from number[] to string[] to store placas
  poligonoOrigem?: number;
  poligonoDestino?: number;
  ativa: boolean;
}

interface Poligono {
  id?: number;
  nome: string;
  tipo: 'patio' | 'cliente' | 'posto' | 'oficina' | 'outro';
  cnpjCliente?: string;
  descricao?: string;
  coordenadas: Array<{ lat: number; lng: number }>;
  cor: string;
  ativa?: boolean;
}

interface PoligonoParadaSelecionado {
  id?: number | string;
  nome: string;
  tipo?: string;
  lat: number;
  lng: number;
}

interface DeparaPlaca {
  cd_depara: number;
  nm_placa_origem: string;
  nm_placa_destino: string;
  cd_ativo: number;
  dt_cadastro?: string;
  dt_atualizacao?: string;
}

interface MonitoramentoModuleProps {
  userAccessType?: string;
  userSector?: string;
  publicTvMode?: boolean;
}

type VisualizacaoFrota = 'cavalos' | 'carretas';

const TV_PLACAS_STORAGE_KEY = 'monitoramento.tv.placasSelecionadas';
const TV_OPERACAO_STORAGE_KEY = 'monitoramento.tv.operacaoFiltro';
const TV_TROCA_SEGUNDOS = 15;
const TV_CONFIG_SYNC_INTERVAL_MS = 3000;
const TV_DADOS_REFRESH_INTERVAL_MS = 60000;
const TV_PROGRAMACAO_REFRESH_INTERVAL_MS = 10000;
const TV_TABELA_SCROLL_INTERVAL_MS = 12000;
const TV_VEICULOS_SEMANA_POR_PAGINA = 6;

const carregarTvPlacasSalvas = () => {
  try {
    const raw = window.localStorage.getItem(TV_PLACAS_STORAGE_KEY);
    const placas = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(placas)) return [];

    return Array.from(new Set(
      placas
        .map(placa => String(placa || '').trim().toUpperCase())
        .filter(Boolean)
    ));
  } catch {
    return [];
  }
};

const carregarTvOperacaoSalva = () => {
  try {
    return window.localStorage.getItem(TV_OPERACAO_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

interface CarretaPainel {
  placa: string;
  marca?: string;
  modelo?: string;
  responsavel?: string;
  tipoCarreta?: string;
  classificacaoInterna?: number;
  cavalo?: string;
  sistema?: string;
  motorista?: string;
  cva?: string;
  telefones?: string[];
  status?: string;
  velocidade?: number;
  operacao?: string;
  localizacao?: Veiculo['localizacao'];
  poligono?: string;
  ultimaAtualizacao?: string;
  ultimaComunicacao?: string;
  ultimaAtualizacaoOmnilink?: string;
  ultimaComunicacaoOmnilink?: string;
  ultimaAtualizacaoTraccar?: string;
  ultimaComunicacaoTraccar?: string;
  tempoParado?: number;
  tipoComunicacao?: string;
  statusCarregamento?: string;
  ultimoPoligono?: string;
  horarioSaidaUltimoPoligono?: string;
  sentidoRota?: Veiculo['sentidoRota'];
  confiancaSentido?: number;
  veiculoVinculado?: Veiculo | null;
}

interface CarretaNucci {
  placa: string;
  marca?: string;
  modelo?: string;
  responsavel?: string;
  tipoCarreta?: string;
  classificacaoInterna?: number;
  sistema?: string;
}

interface ProgramacaoTvItem {
  id: string;
  dia: string;
  diaSemana: string;
  fornecedor: string;
  horarioColeta: string;
  horarioChegada: string;
  tipo: string;
  cavalo: string;
  carreta: string;
  motorista: string;
  observacao: string;
}

interface ProgramacaoTvLinha extends ProgramacaoTvItem {
  origemAutomatica?: boolean;
  oc?: number | string | null;
  cotacao?: number | string | null;
  origem?: string;
  destino?: string;
  codViagem?: number | string | null;
  roundtrip?: number | string | null;
  vinculoViagem?: number | string | null;
}

interface ProgramacaoAutomaticaTvItem {
  cotacao: number | string;
  coleta?: number | string | null;
  dataColeta?: string | null;
  previsaoEntrega?: string | null;
  cnpj?: string | null;
  empresaColeta?: string | null;
  enderecoColeta?: string | null;
  cnpjDestino?: string | null;
  empresaDestino?: string | null;
  enderecoDestino?: string | null;
  contrato?: number | string | null;
  dataContrato?: string | null;
  statusContrato?: number | string | null;
  placa?: string | null;
  placaCarreta?: string | null;
  numeroManifesto?: number | string | null;
  dataManifesto?: string | null;
  motorista?: string | null;
  cpfMotorista?: string | null;
  telefoneMotorista?: string | null;
  codViagem?: number | string | null;
  ordemViagem?: string | null;
  roundtrip?: number | string | null;
  vinculoViagem?: number | string | null;
  sincronizadoEm?: string | null;
  quantidadeColetas?: number;
  viagemIdaVolta?: boolean;
  dataColetaRetorno?: string | null;
}

const abreviarNomeEmpresaProgramacao = (nome?: string | null) => {
  const valor = String(nome || '').trim();
  if (!valor) return '-';
  const termosGenericos = new Set([
    'IND', 'INDUSTRIA', 'INDUSTRIAL', 'COM', 'COMERCIO', 'IMP', 'IMPORTACAO',
    'EXP', 'EXPORTACAO', 'LTDA', 'LIMITADA', 'SA', 'S/A', 'ME', 'EPP',
    'E', 'DE', 'DA', 'DO', 'DAS', 'DOS'
  ]);
  const relevantes = valor.split(/\s+/).filter(termo => {
    const normalizado = termo.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Za-z0-9/]/g, '').toUpperCase();
    return normalizado && !termosGenericos.has(normalizado);
  });
  return (relevantes.length ? relevantes : valor.split(/\s+/)).slice(0, 2).join(' ');
};

const PROGRAMACAO_TV_STORAGE_KEY = 'monitoramento-tv-programacao';
const PROGRAMACAO_TV_VAZIA: Omit<ProgramacaoTvItem, 'id'> = {
  dia: '',
  diaSemana: '',
  fornecedor: '',
  horarioColeta: '',
  horarioChegada: '',
  tipo: '',
  cavalo: '',
  carreta: '',
  motorista: '',
  observacao: ''
};

const obterDiaSemana = (data: string): string => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(data)) return '';
  const [ano, mes, dia] = data.split('-').map(Number);
  const diasSemana = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
  return diasSemana[new Date(Date.UTC(ano, mes - 1, dia)).getUTCDay()];
};

const obterInicioSemanaProgramacao = (data: string): string => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(data)) return '';
  const [ano, mes, dia] = data.split('-').map(Number);
  const valor = new Date(Date.UTC(ano, mes - 1, dia));
  const deslocamento = valor.getUTCDay() === 0 ? -6 : 1 - valor.getUTCDay();
  valor.setUTCDate(valor.getUTCDate() + deslocamento);
  return valor.toISOString().slice(0, 10);
};

const formatarDataProgramacao = (data: string): string => data ? data.split('-').reverse().join('/') : '';

const formatarDataHoraProgramacao = (valor?: string | null): string => {
  if (!valor) return '-';
  const [data, horario] = valor.split(' ');
  return `${formatarDataProgramacao(data)}${horario ? ` ${horario.slice(0, 5)}` : ''}`;
};

const obterFimSemanaProgramacao = (inicio: string): string => {
  const [ano, mes, dia] = inicio.split('-').map(Number);
  const fim = new Date(Date.UTC(ano, mes - 1, dia));
  fim.setUTCDate(fim.getUTCDate() + 5);
  return fim.toISOString().slice(0, 10);
};

const obterPeriodoSemanaAtual = () => {
  const hoje = new Date();
  const inicio = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  inicio.setDate(inicio.getDate() + (inicio.getDay() === 0 ? -6 : 1 - inicio.getDay()));
  const fim = new Date(inicio);
  fim.setDate(fim.getDate() + 5);
  const dataLocal = (data: Date) => `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
  return { dataInicial: dataLocal(inicio), dataFinal: dataLocal(fim) };
};

const PROGRAMACAO_TV_ITENS_POR_PAGINA = 8;

const carregarProgramacaoTv = (): ProgramacaoTvItem[] => {
  try {
    const dados = localStorage.getItem(PROGRAMACAO_TV_STORAGE_KEY);
    return dados ? JSON.parse(dados) : [];
  } catch {
    return [];
  }
};

const MonitoramentoModule: React.FC<MonitoramentoModuleProps> = ({ userAccessType, userSector, publicTvMode = false }) => {
  const normalizeText = (value?: string) => (value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const accessType = normalizeText(userAccessType);
  const isOperacionalAccess = accessType === 'operacional' || accessType === 'operacao';
  const isGerencialAccess = accessType === 'gerencial';
  const isAdminAccess = accessType === 'admin';
  const setoresOperacionais = ['Operação Volks', 'Operação Brose', 'Operação Eldorado'];
  const setoresOperacionaisUsuario = useMemo(() => {
    const setoresUsuarioNormalizados = new Set(
      (userSector || '')
        .split(/[,;|]/)
        .map(setor => normalizeText(setor))
        .filter(Boolean)
    );

    return setoresOperacionais.filter(setor => setoresUsuarioNormalizados.has(normalizeText(setor)));
  }, [userSector]);
  // Restringe DADOS (veículos/operações) apenas quando o usuário é de um setor operacional
  const isSetorScopedAccess = setoresOperacionaisUsuario.length > 0 && (isOperacionalAccess || isGerencialAccess || isAdminAccess);
  // Botões de gerenciamento são definidos exclusivamente pelo tipo de acesso, independente do setor
  const canManageFullMonitoring = isAdminAccess;
  const canManageOperations = isAdminAccess || isGerencialAccess;
  const canViewEditRoutes = canManageFullMonitoring || isGerencialAccess;
  // Hook para placas paradas em polígono e mapeamento por placa
  const { dados: dadosParadosPoligono } = usePlacasParadasPoligono();
  const poligonosTempoPorPlaca = usePoligonosTempoParadoPorPlaca(dadosParadosPoligono);
  // ...existing code...
  const [mostrarOverlayParados, setMostrarOverlayParados] = useState(true);
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [rotas, setRotas] = useState<Rota[]>([]);
  const [estatisticas, setEstatisticas] = useState<Estatisticas>({
    totalVeiculos: 0,
    emMovimento: 0,
    parados: 0,
    foraDeRota: 0,
    problemaRastreador: 0,
    emManutencao: 0,
    ultimaAtualizacao: new Date().toLocaleString('pt-BR')
  });
  const [veiculoSelecionado, setVeiculoSelecionado] = useState<Veiculo | null>(null);
  const [coordenadasCopiadas, setCoordenadasCopiadas] = useState(false);
  const [visualizacaoFrota, setVisualizacaoFrota] = useState<VisualizacaoFrota>('cavalos');
  const [filtroSistema, setFiltroSistema] = useState<string>('todos');
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');
  const [filtroSentido, setFiltroSentido] = useState<string>('todos');
  // const [filtroResponsavel, setFiltroResponsavel] = useState<string[]>([]);
  const [filtroOperacao, setFiltroOperacao] = useState<number | null>(null);
  const [filtroPlaca, setFiltroPlaca] = useState<string>('');
  const [filtroPlacaDebounced, setFiltroPlacaDebounced] = useState<string>(''); // Filtro com debounce
  const [modoTv, setModoTv] = useState(publicTvMode);
  const [tvExpandido, setTvExpandido] = useState(false);
  const [tvBusca, setTvBusca] = useState('');
  const [tvOperacaoFiltro, setTvOperacaoFiltro] = useState(carregarTvOperacaoSalva);
  const [programacaoTvAberta, setProgramacaoTvAberta] = useState(false);
  const [programacaoTvVisaoAutomatica, setProgramacaoTvVisaoAutomatica] = useState(false);
  const [programacaoTv, setProgramacaoTv] = useState<ProgramacaoTvItem[]>(carregarProgramacaoTv);
  const [programacaoTvForm, setProgramacaoTvForm] = useState<Omit<ProgramacaoTvItem, 'id'>>(PROGRAMACAO_TV_VAZIA);
  const [programacaoTvEditandoId, setProgramacaoTvEditandoId] = useState<string | null>(null);
  const [programacaoTvSalvando, setProgramacaoTvSalvando] = useState(false);
  const [programacaoTvSemana, setProgramacaoTvSemana] = useState('');
  const [programacaoTvDiaSemana, setProgramacaoTvDiaSemana] = useState('');
  const [programacaoTvPagina, setProgramacaoTvPagina] = useState(1);
  const [programacaoTvViagensExpandidas, setProgramacaoTvViagensExpandidas] = useState<Set<string>>(() => new Set());
  const [programacaoAutomaticaTv, setProgramacaoAutomaticaTv] = useState<ProgramacaoAutomaticaTvItem[]>([]);
  const [programacaoAutomaticaPeriodo, setProgramacaoAutomaticaPeriodo] = useState(obterPeriodoSemanaAtual);
  const [programacaoAutomaticaPeriodoAplicado, setProgramacaoAutomaticaPeriodoAplicado] = useState(obterPeriodoSemanaAtual);
  const [programacaoAutomaticaPagina, setProgramacaoAutomaticaPagina] = useState(1);
  const [programacaoAutomaticaViagensExpandidas, setProgramacaoAutomaticaViagensExpandidas] = useState<Set<string>>(() => new Set());
  const [programacaoAutomaticaLoading, setProgramacaoAutomaticaLoading] = useState(false);
  const [programacaoAutomaticaErro, setProgramacaoAutomaticaErro] = useState('');
  const [programacaoAutomaticaAviso, setProgramacaoAutomaticaAviso] = useState('');
  const [programacaoAutomaticaSincronizadaEm, setProgramacaoAutomaticaSincronizadaEm] = useState<string | null>(null);
  const [tvProgramacaoAutomatica, setTvProgramacaoAutomatica] = useState<ProgramacaoAutomaticaTvItem[]>([]);
  const programacaoAutomaticaComoCadastro = useMemo<ProgramacaoTvLinha[]>(() => programacaoAutomaticaTv.map(item => {
    const dataColeta = String(item.dataColeta || '');
    const previsaoEntrega = String(item.previsaoEntrega || '');
    const empresaColeta = String(item.empresaColeta || '').trim();
    const empresaDestino = String(item.empresaDestino || '').split('→')[0].trim();
    const coletaNaFks = empresaColeta.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes('fks logistics');
    return {
      id: `auto-${item.cotacao}-${item.coleta}`,
      dia: dataColeta.slice(0, 10),
      diaSemana: obterDiaSemana(dataColeta.slice(0, 10)),
      oc: item.coleta,
      cotacao: item.cotacao,
      fornecedor: abreviarNomeEmpresaProgramacao(coletaNaFks ? empresaDestino : empresaColeta),
      origem: abreviarNomeEmpresaProgramacao(empresaColeta),
      horarioColeta: dataColeta.includes(' ') ? dataColeta.split(' ')[1].slice(0, 5) : '-',
      destino: abreviarNomeEmpresaProgramacao(item.empresaDestino),
      horarioChegada: previsaoEntrega.includes(' ') ? previsaoEntrega.split(' ')[1].slice(0, 5) : '-',
      tipo: '-',
      cavalo: String(item.placa || '-'),
      carreta: String(item.placaCarreta || '-'),
      motorista: String(item.motorista || '-'),
      observacao: '-',
      origemAutomatica: true,
      codViagem: item.codViagem,
      roundtrip: item.roundtrip,
      vinculoViagem: item.vinculoViagem
    };
  }), [programacaoAutomaticaTv]);
  const programacaoCadastro = useMemo<ProgramacaoTvLinha[]>(
    () => programacaoAutomaticaComoCadastro,
    [programacaoAutomaticaComoCadastro]
  );
  const programacaoTvSemanas = useMemo(() => {
    const inicios = Array.from(new Set(programacaoCadastro.map(item => obterInicioSemanaProgramacao(item.dia)).filter(Boolean))).sort();
    return inicios.map((inicio, index) => ({
      inicio,
      fim: obterFimSemanaProgramacao(inicio),
      numero: index + 1
    }));
  }, [programacaoCadastro]);
  const programacaoTvOrdenada = useMemo(() => [...programacaoCadastro]
    .sort((a, b) => {
      const numeroA = Number.parseInt(String(a.codViagem || ''), 10) || Number.MAX_SAFE_INTEGER;
      const numeroB = Number.parseInt(String(b.codViagem || ''), 10) || Number.MAX_SAFE_INTEGER;
      return a.dia.localeCompare(b.dia)
        || numeroA - numeroB
        || Number(a.roundtrip || 0) - Number(b.roundtrip || 0)
        || a.horarioColeta.localeCompare(b.horarioColeta)
        || a.id.localeCompare(b.id);
    }), [programacaoCadastro]);
  const programacaoTvGruposCompletos = useMemo(() => {
    const grupos: Array<{ chave: string; ida: ProgramacaoTvLinha; voltas: ProgramacaoTvLinha[] }> = [];
    const ehIda = (item: ProgramacaoTvLinha) => /_IDA_/i.test(String(item.codViagem || '')) || !Number(item.roundtrip);
    const numeroCodigo = (item: ProgramacaoTvLinha) => String(item.codViagem || '').split('_')[0];
    const rotaInvertida = (ida: ProgramacaoTvLinha, volta: ProgramacaoTvLinha) =>
      normalizeText(ida.origem) === normalizeText(volta.destino)
      && normalizeText(ida.destino) === normalizeText(volta.origem);

    for (const item of programacaoTvOrdenada) {
      if (ehIda(item)) {
        grupos.push({
          chave: `compacta-${item.vinculoViagem || item.cotacao}-${item.codViagem || item.id}`,
          ida: item,
          voltas: []
        });
        continue;
      }

      const candidatas = grupos.filter(grupo => ehIda(grupo.ida));
      const mesmoVinculo = [...candidatas].reverse().find(grupo =>
        String(grupo.ida.vinculoViagem || '') !== ''
        && String(grupo.ida.vinculoViagem) === String(item.vinculoViagem)
        && (rotaInvertida(grupo.ida, item) || numeroCodigo(grupo.ida) === numeroCodigo(item))
      );
      const mesmaRotaECodigo = [...candidatas].reverse().find(grupo =>
        numeroCodigo(grupo.ida) === numeroCodigo(item) && rotaInvertida(grupo.ida, item)
      );
      // Alguns retornos chegam da Nucci com vinculo e numero divergentes. A
      // rota invertida identifica o par e permite varias coletas de retorno.
      const mesmaRota = [...candidatas].reverse().find(grupo => rotaInvertida(grupo.ida, item));
      const grupoIda = mesmoVinculo || mesmaRotaECodigo || mesmaRota;
      if (grupoIda) grupoIda.voltas.push(item);
      else grupos.push({ chave: `compacta-retorno-${item.vinculoViagem || item.cotacao}-${item.codViagem || item.id}`, ida: item, voltas: [] });
    }
    return grupos;
  }, [programacaoTvOrdenada]);
  const programacaoTvGrupos = useMemo(() => programacaoTvGruposCompletos.flatMap(grupo => {
    const linhasFiltradas = [grupo.ida, ...grupo.voltas].filter(item => {
      const correspondeSemana = !programacaoTvSemana || obterInicioSemanaProgramacao(item.dia) === programacaoTvSemana;
      const correspondeDia = !programacaoTvDiaSemana || item.diaSemana === programacaoTvDiaSemana;
      return correspondeSemana && correspondeDia;
    });
    if (!linhasFiltradas.length) return [];
    return [{ ...grupo, ida: linhasFiltradas[0], voltas: linhasFiltradas.slice(1) }];
  }), [programacaoTvGruposCompletos, programacaoTvSemana, programacaoTvDiaSemana]);
  const programacaoTvFiltrada = useMemo(
    () => programacaoTvGrupos.flatMap(grupo => [grupo.ida, ...grupo.voltas]),
    [programacaoTvGrupos]
  );
  const programacaoTvPaginas = useMemo(() => {
    const paginas: typeof programacaoTvGrupos[] = [];
    for (const grupo of programacaoTvGrupos) {
      const paginaAtual = paginas.at(-1);
      if (!paginaAtual || paginaAtual.length >= PROGRAMACAO_TV_ITENS_POR_PAGINA) paginas.push([grupo]);
      else paginaAtual.push(grupo);
    }
    return paginas.length ? paginas : [[]];
  }, [programacaoTvGrupos]);
  const programacaoTvTotalPaginas = programacaoTvPaginas.length;
  const programacaoTvGruposPaginados = programacaoTvPaginas[programacaoTvPagina - 1] || [];
  const programacaoTvPaginada = useMemo(() => programacaoTvGruposPaginados.flatMap(grupo => [grupo.ida, ...grupo.voltas]), [programacaoTvGruposPaginados]);
  const programacaoTvQuantidadeViagens = programacaoTvGrupos.length;
  const programacaoAutomaticaOrdenada = useMemo(() => [...programacaoAutomaticaTv].sort((a, b) =>
    String(a.dataColeta || '').localeCompare(String(b.dataColeta || ''))
    || Number(a.roundtrip || 0) - Number(b.roundtrip || 0)
  ), [programacaoAutomaticaTv]);
  const programacaoAutomaticaGruposCompletos = useMemo(() => {
    const grupos: Array<{ chave: string; ida: ProgramacaoAutomaticaTvItem; voltas: ProgramacaoAutomaticaTvItem[] }> = [];
    const ehIda = (item: ProgramacaoAutomaticaTvItem) => /_IDA_/i.test(String(item.codViagem || '')) || !Number(item.roundtrip);
    const numeroCodigo = (item: ProgramacaoAutomaticaTvItem) => String(item.codViagem || '').split('_')[0];
    const rotaInvertida = (ida: ProgramacaoAutomaticaTvItem, volta: ProgramacaoAutomaticaTvItem) => {
      const origemIda = String(ida.cnpj || '').replace(/\D/g, '');
      const destinoIda = String(ida.cnpjDestino || '').replace(/\D/g, '');
      const origemVolta = String(volta.cnpj || '').replace(/\D/g, '');
      const destinoVolta = String(volta.cnpjDestino || '').replace(/\D/g, '');
      return Boolean(origemIda && destinoIda && origemIda === destinoVolta && destinoIda === origemVolta);
    };

    for (const item of programacaoAutomaticaOrdenada) {
      if (ehIda(item)) {
        grupos.push({
          chave: `auto-${item.vinculoViagem || item.cotacao}-${item.codViagem || item.coleta}`,
          ida: item,
          voltas: []
        });
        continue;
      }

      const candidatas = grupos.filter(grupo => ehIda(grupo.ida));
      const mesmoVinculo = [...candidatas].reverse().find(grupo =>
        String(grupo.ida.vinculoViagem || '') !== ''
        && String(grupo.ida.vinculoViagem) === String(item.vinculoViagem)
        && (rotaInvertida(grupo.ida, item) || numeroCodigo(grupo.ida) === numeroCodigo(item))
      );
      const mesmaRotaECodigo = [...candidatas].reverse().find(grupo =>
        numeroCodigo(grupo.ida) === numeroCodigo(item) && rotaInvertida(grupo.ida, item)
      );
      // Fallback operacional para vinculos/codigos inconsistentes: associa ao
      // trajeto inverso mais recente, inclusive quando ha varias voltas/coletas.
      const mesmaRota = [...candidatas].reverse().find(grupo => rotaInvertida(grupo.ida, item));
      const grupoIda = mesmoVinculo || mesmaRotaECodigo || mesmaRota;
      if (grupoIda) grupoIda.voltas.push(item);
      else grupos.push({ chave: `auto-retorno-${item.vinculoViagem || item.cotacao}-${item.codViagem || item.coleta}`, ida: item, voltas: [] });
    }

    return grupos;
  }, [programacaoAutomaticaOrdenada]);
  const programacaoAutomaticaGrupos = useMemo(() => programacaoAutomaticaGruposCompletos.flatMap(grupo => {
    const linhasFiltradas = [grupo.ida, ...grupo.voltas].filter(item => {
      const dia = String(item.dataColeta || '').slice(0, 10);
      return dia >= programacaoAutomaticaPeriodoAplicado.dataInicial && dia <= programacaoAutomaticaPeriodoAplicado.dataFinal;
    });
    if (!linhasFiltradas.length) return [];
    return [{ ...grupo, ida: linhasFiltradas[0], voltas: linhasFiltradas.slice(1) }];
  }), [programacaoAutomaticaGruposCompletos, programacaoAutomaticaPeriodoAplicado]);
  const programacaoAutomaticaFiltrada = useMemo(
    () => programacaoAutomaticaGrupos.flatMap(grupo => [grupo.ida, ...grupo.voltas]),
    [programacaoAutomaticaGrupos]
  );
  const programacaoAutomaticaPaginas = useMemo(() => {
    const paginas: typeof programacaoAutomaticaGrupos[] = [];
    for (const grupo of programacaoAutomaticaGrupos) {
      const paginaAtual = paginas.at(-1);
      if (!paginaAtual || paginaAtual.length >= PROGRAMACAO_TV_ITENS_POR_PAGINA) paginas.push([grupo]);
      else paginaAtual.push(grupo);
    }
    return paginas.length ? paginas : [[]];
  }, [programacaoAutomaticaGrupos]);
  const programacaoAutomaticaTotalPaginas = programacaoAutomaticaPaginas.length;
  const programacaoAutomaticaGruposPaginados = programacaoAutomaticaPaginas[programacaoAutomaticaPagina - 1] || [];
  const [tvPlacasSelecionadas, setTvPlacasSelecionadas] = useState<string[]>(carregarTvPlacasSalvas);
  const [tvVeiculoAtivoIndex, setTvVeiculoAtivoIndex] = useState(0);
  const [tvSegundosTroca, setTvSegundosTroca] = useState(TV_TROCA_SEGUNDOS);
  const [tvProximaTrocaMs, setTvProximaTrocaMs] = useState(Date.now() + TV_TROCA_SEGUNDOS * 1000);
  const [tvTempoAgoraMs, setTvTempoAgoraMs] = useState(Date.now());
  const [tvTempoReferenciaMs, setTvTempoReferenciaMs] = useState(Date.now());
  const [tvDadosViagem, setTvDadosViagem] = useState<Record<string, TvDadosViagem>>({});
  const [tvDadosLoading, setTvDadosLoading] = useState(false);
  const [tvConfiguracaoCarregada, setTvConfiguracaoCarregada] = useState(false);
  const [tvAgendaFixa, setTvAgendaFixa] = useState<AgendaFixaTvItem[]>(TV_AGENDA_FIXA);
  const [tvAgendaRascunho, setTvAgendaRascunho] = useState<AgendaFixaTvItem[]>(TV_AGENDA_FIXA);
  const [tvAgendaEditando, setTvAgendaEditando] = useState(false);
  const [tvAgendaSalvando, setTvAgendaSalvando] = useState(false);
  const [tvAgendaErro, setTvAgendaErro] = useState('');
  const [tvVeiculosSemanaPagina, setTvVeiculosSemanaPagina] = useState(0);
  const tvTabelaWrapRef = React.useRef<HTMLDivElement | null>(null);
  const tvTabelaRowRefs = React.useRef<Record<string, HTMLTableRowElement | null>>({});
  const tvKmResumoRef = React.useRef<HTMLDivElement | null>(null);
  const tvKmItemRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const [carretasNucci, setCarretasNucci] = useState<CarretaNucci[]>([]);
  const [showCadastroRota, setShowCadastroRota] = useState(false);
  const [rotaEmEdicao, setRotaEmEdicao] = useState<number | null>(null);
  const [showRotas, setShowRotas] = useState(false);
  const [alarmeAtivo, setAlarmeAtivo] = useState(true); // Som do alarme ativo por padrão (alertas sempre aparecem)
  const [veiculosEmAlerta, setVeiculosEmAlerta] = useState(0); // Contador de veículos em alerta
  const [mostrarModalAlertas, setMostrarModalAlertas] = useState(false); // Forçar exibição do modal
  // Reverse geocoding: cache persistente e estado reativo para exibição na tabela
  const geocodeCacheRef = React.useRef<Record<string, string>>({});
  const geocodePendingRef = React.useRef<Record<string, boolean>>({});
  const [enderecos, setEnderecos] = useState<Record<string, string>>({});

  // Sempre que a página recarregar, garantir que showRotas volte para false (veículos/tabela visível)
  useEffect(() => {
    setShowRotas(false);
  }, []);
  const [rotasSelecionadas, setRotasSelecionadas] = useState<number[]>([]);
  const [mapaRef, setMapaRef] = useState<any>(null);
  const markerRefs = React.useRef<{ [key: number]: any }>({});
  const mapContainerRef = React.useRef<any>(null);
  const criarNovaRotaVazia = () => ({
    nome: '',
    descricao: '',
    linkMaps: '',
    origem: '',
    destino: '',
    pontosParada: '',
    distancia: '',
    tempoEstimado: '',
    tipoRota: 'ida' as 'ida' | 'ida_volta',
    cor: '#2196f3',
    pontos: [{ lat: '', lng: '', endereco: '' }],
    pontosDetalhados: [] as Array<{ lat: number; lng: number }>,
    poligonosParada: [] as PoligonoParadaSelecionado[],
  });
  const [novaRota, setNovaRota] = useState(criarNovaRotaVazia());
  const [filtroPoligonoParada, setFiltroPoligonoParada] = useState('');
  const [rotaSelecionada, setRotaSelecionada] = useState<Rota | null>(null);
  const [rotasDetalhadas, setRotasDetalhadas] = useState<{ [key: number]: Array<[number, number]> }>({});
  const [operacoes, setOperacoes] = useState<Operacao[]>([]);
  const [showCadastroOperacao, setShowCadastroOperacao] = useState(false);
  const [showListaOperacoes, setShowListaOperacoes] = useState(false);
  const [operacaoEmEdicao, setOperacaoEmEdicao] = useState<number | null>(null);
  const [filtroRotasModal, setFiltroRotasModal] = useState<string>('');
  const [filtroVeiculosModal, setFiltroVeiculosModal] = useState<string>('');
  const [novaOperacao, setNovaOperacao] = useState<Operacao>({
    nome: '',
    descricao: '',
    setorOperacao: '',
    rotas: [],
    veiculos: [],
    poligonoOrigem: undefined,
    poligonoDestino: undefined,
    ativa: true
  });
  const operacoesDisponiveis = useMemo(() => {
    if (!isSetorScopedAccess) return operacoes;
    return operacoes.filter(op =>
      setoresOperacionaisUsuario.some(setor => normalizeText(op.setorOperacao) === normalizeText(setor))
    );
  }, [isSetorScopedAccess, setoresOperacionaisUsuario, operacoes]);
  const operacoesDisponiveisAtivas = useMemo(() => {
    return operacoesDisponiveis.filter(op => op.ativa !== false);
  }, [operacoesDisponiveis]);

  useEffect(() => {
    try {
      window.localStorage.setItem(TV_PLACAS_STORAGE_KEY, JSON.stringify(tvPlacasSelecionadas));
    } catch (err) {
      console.warn('[TV] Nao foi possivel salvar placas selecionadas:', err);
    }
  }, [tvPlacasSelecionadas]);

  useEffect(() => {
    try {
      if (tvOperacaoFiltro) {
        window.localStorage.setItem(TV_OPERACAO_STORAGE_KEY, tvOperacaoFiltro);
      } else {
        window.localStorage.removeItem(TV_OPERACAO_STORAGE_KEY);
      }
    } catch (err) {
      console.warn('[TV] Nao foi possivel salvar operacao selecionada:', err);
    }
  }, [tvOperacaoFiltro]);

  useEffect(() => {
    let ativo = true;

    const aplicarConfiguracaoCompartilhada = (config: any) => {
      if (!ativo || !config?.configurada) return;
      const placas = Array.from(new Set(
        (Array.isArray(config.placas) ? config.placas : [])
          .map((placa: unknown) => String(placa || '').trim().toUpperCase())
          .filter(Boolean)
      ));
      setTvPlacasSelecionadas(atual =>
        JSON.stringify(atual) === JSON.stringify(placas) ? atual : placas
      );
      const operacao = String(config.operacao || '');
      setTvOperacaoFiltro(atual => atual === operacao ? atual : operacao);
      const agendaRecebida = Array.isArray(config.agenda)
        ? config.agenda.map((item: any) => ({
            dia: String(item?.dia || ''),
            fornecedor: String(item?.fornecedor || ''),
            horario: String(item?.horario || '')
          }))
        : [];
      const agenda = config.agendaConfigurada ? agendaRecebida : TV_AGENDA_FIXA;
      setTvAgendaFixa(atual => JSON.stringify(atual) === JSON.stringify(agenda) ? atual : agenda);
    };

    const carregarConfiguracao = async (inicial = false) => {
      try {
        const response = await axios.get('/api/monitoramento/tv/configuracao');
        if (!ativo) return;

        if (response.data?.configurada) {
          aplicarConfiguracaoCompartilhada(response.data);
        } else if (inicial && tvPlacasSelecionadas.length > 0) {
          await axios.put('/api/monitoramento/tv/configuracao', {
            placas: tvPlacasSelecionadas,
            operacao: tvOperacaoFiltro,
            agenda: TV_AGENDA_FIXA
          });
        }
      } catch (err) {
        console.error('[TV] Erro ao sincronizar configuracao compartilhada:', err);
      } finally {
        if (ativo && inicial) setTvConfiguracaoCarregada(true);
      }
    };

    carregarConfiguracao(true);
    const intervalo = window.setInterval(() => carregarConfiguracao(false), TV_CONFIG_SYNC_INTERVAL_MS);

    return () => {
      ativo = false;
      window.clearInterval(intervalo);
    };
    // A carga inicial deve usar apenas os valores salvos localmente na montagem.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicTvMode]);

  useEffect(() => {
    if (!tvConfiguracaoCarregada) return;

    const timeout = window.setTimeout(() => {
      axios.put('/api/monitoramento/tv/configuracao', {
        placas: tvPlacasSelecionadas,
        operacao: tvOperacaoFiltro,
        agenda: tvAgendaFixa
      }).catch(err => {
        console.error('[TV] Erro ao salvar configuracao compartilhada:', err);
      });
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [tvConfiguracaoCarregada, tvPlacasSelecionadas, tvOperacaoFiltro, tvAgendaFixa]);

  useEffect(() => {
    let ativo = true;
    let primeiraCarga = true;

    const carregarProgramacaoCompartilhada = async () => {
      try {
        const response = await axios.get<ProgramacaoTvItem[]>('/api/monitoramento/tv/programacao', {
          params: { _: Date.now() }
        });
        if (!ativo) return;

        let itensServidor = Array.isArray(response.data) ? response.data : [];
        const itensLocais = primeiraCarga ? carregarProgramacaoTv() : [];

        // Migração automática dos cadastros feitos antes da persistência no banco.
        if (itensServidor.length === 0 && itensLocais.length > 0) {
          const migrados: ProgramacaoTvItem[] = [];
          for (const itemLocal of itensLocais) {
            const cadastro = await axios.post<ProgramacaoTvItem>('/api/monitoramento/tv/programacao', itemLocal);
            migrados.push(cadastro.data);
          }
          itensServidor = migrados;
        }

        if (ativo) setProgramacaoTv(itensServidor);
        window.localStorage.removeItem(PROGRAMACAO_TV_STORAGE_KEY);
      } catch (err) {
        console.error('[TV PROGRAMACAO] Erro ao carregar dados compartilhados:', err);
      } finally {
        primeiraCarga = false;
      }
    };

    carregarProgramacaoCompartilhada();
    const intervalo = window.setInterval(carregarProgramacaoCompartilhada, TV_PROGRAMACAO_REFRESH_INTERVAL_MS);
    const atualizarAoRetomar = () => {
      if (document.visibilityState === 'visible') carregarProgramacaoCompartilhada();
    };
    window.addEventListener('focus', carregarProgramacaoCompartilhada);
    document.addEventListener('visibilitychange', atualizarAoRetomar);

    return () => {
      ativo = false;
      window.clearInterval(intervalo);
      window.removeEventListener('focus', carregarProgramacaoCompartilhada);
      document.removeEventListener('visibilitychange', atualizarAoRetomar);
    };
  }, []);

  useEffect(() => {
    setProgramacaoTvPagina(1);
  }, [programacaoTvSemana, programacaoTvDiaSemana]);

  useEffect(() => {
    if (programacaoTvPagina > programacaoTvTotalPaginas) {
      setProgramacaoTvPagina(programacaoTvTotalPaginas);
    }
  }, [programacaoTvPagina, programacaoTvTotalPaginas]);

  useEffect(() => {
    if (!programacaoTvAberta) return;
    let ativo = true;

    const carregar = async () => {
      setProgramacaoAutomaticaLoading(true);
      try {
        const inicioConsulta = new Date(`${programacaoAutomaticaPeriodoAplicado.dataInicial}T12:00:00`);
        inicioConsulta.setDate(inicioConsulta.getDate() - 7);
        const response = await axios.get<{
          dados: ProgramacaoAutomaticaTvItem[];
          aviso?: string | null;
          sincronizadoEm?: string | null;
        }>('/api/monitoramento/tv/programacao-automatica', {
          params: {
            // Carrega uma margem anterior para localizar a ida quando o periodo
            // selecionado comeca no dia da volta. O filtro visual e aplicado
            // somente depois que a viagem completa estiver agrupada.
            dataInicial: inicioConsulta.toISOString().slice(0, 10),
            dataFinal: programacaoAutomaticaPeriodoAplicado.dataFinal,
            _: Date.now()
          }
        });
        if (!ativo) return;
        setProgramacaoAutomaticaTv(Array.isArray(response.data?.dados) ? response.data.dados : []);
        setProgramacaoAutomaticaAviso(response.data?.aviso || '');
        setProgramacaoAutomaticaSincronizadaEm(response.data?.sincronizadoEm || null);
        setProgramacaoAutomaticaErro('');
      } catch (err) {
        if (!ativo) return;
        console.error('[TV PROGRAMACAO AUTOMATICA] Erro ao carregar:', err);
        setProgramacaoAutomaticaErro(axios.isAxiosError(err) && err.response?.data?.error
          ? String(err.response.data.error)
          : 'Não foi possível carregar a programação automática.');
      } finally {
        if (ativo) setProgramacaoAutomaticaLoading(false);
      }
    };

    carregar();
    const intervalo = window.setInterval(carregar, 60 * 1000);
    return () => {
      ativo = false;
      window.clearInterval(intervalo);
    };
  }, [programacaoTvAberta, programacaoAutomaticaPeriodoAplicado]);

  useEffect(() => {
    if (programacaoAutomaticaPagina > programacaoAutomaticaTotalPaginas) {
      setProgramacaoAutomaticaPagina(programacaoAutomaticaTotalPaginas);
    }
  }, [programacaoAutomaticaPagina, programacaoAutomaticaTotalPaginas]);

  useEffect(() => {
    if (!tvOperacaoFiltro || operacoesDisponiveisAtivas.length === 0) return;
    const operacaoExiste = operacoesDisponiveisAtivas.some(operacao => String(operacao.id) === tvOperacaoFiltro);
    if (!operacaoExiste) {
      setTvOperacaoFiltro('');
    }
  }, [tvOperacaoFiltro, operacoesDisponiveisAtivas]);

  const normalizarIdsRota = (ids: Array<number | string | null | undefined>) => {
    return Array.from(
      new Set(
        (ids || [])
          .map(id => Number(id))
          .filter(id => Number.isFinite(id) && id > 0)
      )
    );
  };

  useEffect(() => {
    if (!filtroOperacao) return;
    const existeOperacao = operacoesDisponiveis.some(op => op.id === filtroOperacao);
    if (!existeOperacao) {
      setFiltroOperacao(null);
    }
  }, [filtroOperacao, operacoesDisponiveis]);
  const placasPermitidasPorAcesso = useMemo(() => {
    if (!isSetorScopedAccess) return null;

    const placas = new Set<string>();
    operacoesDisponiveis.forEach(operacao => {
      operacao.veiculos.forEach(placa => {
        const placaNormalizada = (placa || '').toUpperCase().trim();
        if (placaNormalizada) {
          placas.add(placaNormalizada);
        }
      });
    });

    return placas;
  }, [isSetorScopedAccess, operacoesDisponiveis]);
  const operacoesPermitidasNormalizadas = useMemo(() => {
    return new Set(
      operacoesDisponiveis
        .map(operacao => normalizeText(operacao.nome))
        .filter(Boolean)
    );
  }, [operacoesDisponiveis]);
  const placasPermitidasArray = useMemo(() => {
    if (!placasPermitidasPorAcesso) return undefined;
    return Array.from(placasPermitidasPorAcesso);
  }, [placasPermitidasPorAcesso]);
  const veiculosBasePermitidos = useMemo(() => {
    if (!isSetorScopedAccess) return veiculos;

    return veiculos.filter(veiculo => {
      const placaNormalizada = (veiculo.placa || '').toUpperCase().trim();
      if (placasPermitidasPorAcesso?.has(placaNormalizada)) {
        return true;
      }
      const operacaoNormalizada = normalizeText(veiculo.operacao);
      if (!operacaoNormalizada) {
        return false;
      }
      return Array.from(operacoesPermitidasNormalizadas).some(nomeOperacao => operacaoNormalizada.includes(nomeOperacao));
    });
  }, [
    isSetorScopedAccess,
    setoresOperacionaisUsuario,
    veiculos,
    placasPermitidasPorAcesso,
    operacoesPermitidasNormalizadas
  ]);
  const veiculosEmManutencaoPermitidos = useMemo(() => {
    return veiculosBasePermitidos.filter(veiculo => veiculo.emManutencao);
  }, [veiculosBasePermitidos]);
  const veiculosDisponiveisEdicaoOperacao = useMemo(() => {
    if (!isGerencialAccess) {
      return veiculos;
    }

    const veiculosMap = new Map<string, Veiculo>();
    veiculosBasePermitidos.forEach(veiculo => {
      veiculosMap.set(veiculo.placa, veiculo);
    });

    veiculos
      .filter(veiculo => novaOperacao.veiculos.includes(veiculo.placa))
      .forEach(veiculo => {
        if (!veiculosMap.has(veiculo.placa)) {
          veiculosMap.set(veiculo.placa, veiculo);
        }
      });

    return Array.from(veiculosMap.values());
  }, [isGerencialAccess, veiculos, veiculosBasePermitidos, novaOperacao.veiculos]);
  const [poligonos, setPoligonos] = useState<Poligono[]>([]);
  const [historicoMotoristas, setHistoricoMotoristas] = useState<string[]>([]);
  const [performanceData, setPerformanceData] = useState<any[]>([]);
  const [motoristasSynced, setMotoristasSynced] = useState<boolean>(false);
  const [showListaPoligonos, setShowListaPoligonos] = useState(false);
  const [showTooltipProblemaRastreador, setShowTooltipProblemaRastreador] = useState(false);
  const [showTooltipEmManutencao, setShowTooltipEmManutencao] = useState(false);
  const [showModalManutencao, setShowModalManutencao] = useState(false);
  const [showModalDeparaPlacas, setShowModalDeparaPlacas] = useState(false);
  const [deparaPlacas, setDeparaPlacas] = useState<DeparaPlaca[]>([]);
  const [deparaOrigem, setDeparaOrigem] = useState('');
  const [deparaDestino, setDeparaDestino] = useState('');
  const [salvandoDepara, setSalvandoDepara] = useState(false);
  const [carregandoDepara, setCarregandoDepara] = useState(false);
  const [showTooltipEmMovimento, setShowTooltipEmMovimento] = useState(false);
  const [showTooltipParados, setShowTooltipParados] = useState(false);
  const [showTooltipForaRota, setShowTooltipForaRota] = useState(false);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false); // Indica se está atualizando dados
  const [mapaExpandido, setMapaExpandido] = useState(false);
  const [showModalProximidade, setShowModalProximidade] = useState(false);
  const [showTracking, setShowTracking] = useState(false);
  const [placaProximidade, setPlacaProximidade] = useState('');
  const [veiculoReferencia, setVeiculoReferencia] = useState<Veiculo | null>(null);
  const [veiculosMaisProximos, setVeiculosMaisProximos] = useState<Array<{veiculo: Veiculo, distancia: number}>>([]);
  
  // Cache de dados da Nucci para evitar perda durante atualizações
  const dadosNucciCacheRef = React.useRef<Record<string, any>>({});
  const fetchInProgressRef = React.useRef(false);
  const fetchPendingRef = React.useRef(false);
  const nucciRequestControllerRef = React.useRef<AbortController | null>(null);
  const nucciFetchMetaRef = React.useRef<{ lastKey: string; lastAt: number; inFlight: boolean }>({
    lastKey: '',
    lastAt: 0,
    inFlight: false
  });

  // Função para calcular distância entre dois pontos (Haversine)
  const calcularDistancia = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Raio da Terra em km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Função para buscar os 3 veículos mais próximos com sentido volta
  const buscarVeiculoMaisProximo = () => {
    const placaBusca = placaProximidade.trim().toUpperCase();
    
    if (!placaBusca) {
      alert('Por favor, digite uma placa válida.');
      return;
    }

    // Buscar veículo de referência
    const veiculoRef = veiculosBasePermitidos.find(v => v.placa.toUpperCase() === placaBusca);
    
    if (!veiculoRef) {
      alert(`Veículo com placa ${placaBusca} não encontrado.`);
      return;
    }

    if (!veiculoRef.localizacao || !veiculoRef.localizacao.lat || !veiculoRef.localizacao.lng) {
      alert(`Veículo ${placaBusca} não possui localização disponível.`);
      return;
    }

    // Filtrar veículos com sentido volta e que tenham localização
    const veiculosVolta = veiculosBasePermitidos.filter(v => 
      v.placa !== placaBusca && // Não é o próprio veículo
      v.sentidoRota === 'volta' &&
      v.localizacao && 
      v.localizacao.lat && 
      v.localizacao.lng &&
      !v.problemaRastreador
    );

    if (veiculosVolta.length === 0) {
      alert('Não há veículos com sentido "Volta" disponíveis no momento.');
      setVeiculoReferencia(veiculoRef);
      setVeiculosMaisProximos([]);
      return;
    }

    // Calcular distâncias para todos os veículos
    const veiculosComDistancia = veiculosVolta.map(v => ({
      veiculo: v,
      distancia: calcularDistancia(
        veiculoRef.localizacao!.lat,
        veiculoRef.localizacao!.lng,
        v.localizacao!.lat,
        v.localizacao!.lng
      )
    }));

    // Ordenar por distância e pegar os 3 mais próximos
    const top3 = veiculosComDistancia
      .sort((a, b) => a.distancia - b.distancia)
      .slice(0, 3);

    // Atualizar estados
    setVeiculoReferencia(veiculoRef);
    setVeiculosMaisProximos(top3);

    // Centralizar mapa no veículo de referência
    setVeiculoSelecionado(veiculoRef);
  };

  // Função para limpar busca de proximidade
  const limparProximidade = () => {
    setPlacaProximidade('');
    setVeiculoReferencia(null);
    setVeiculosMaisProximos([]);
    setShowModalProximidade(false);
  };

  // Função para gerar relatório Excel de veículos com problema no rastreador
  const gerarRelatorioProblemaRastreador = () => {
    const veiculosComProblema = veiculosFiltrados.filter(v => v.problemaRastreador);
    
    if (veiculosComProblema.length === 0) {
      alert('Não há veículos com problema no rastreador no momento.');
      return;
    }

    // Preparar dados para o Excel
    const dadosExcel = veiculosComProblema.map(v => ({
      'Placa': v.placa,
      'Responsável': v.responsavel || 'Não informado',
      'Última Comunicação': v.ultimaComunicacao || 'Não disponível'
    }));

    // Criar workbook e worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dadosExcel);

    // Ajustar largura das colunas
    ws['!cols'] = [
      { wch: 12 }, // Placa
      { wch: 30 }, // Responsável
      { wch: 20 }  // Última Comunicação
    ];

    // Adicionar worksheet ao workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Problemas Rastreador');

    // Gerar arquivo e fazer download
    const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    XLSX.writeFile(wb, `Relatorio_Problema_Rastreador_${dataAtual}.xlsx`);
  };

  // Função para remover veículo de manutenção
  const removerDeManutencao = async (placa: string) => {
    try {
      setIsUpdating(true);
      const resp = await axios.post(`/api/monitoramento/manutencao/${placa}`, {
        em_manutencao: false
      });
      if (resp.data && resp.data.success) {
        alert(`Manutenção finalizada para o veículo ${placa}`);
      }
      // Atualizar lista imediatamente
      await fetchDados();
      setIsUpdating(false);
    } catch (error) {
      setIsUpdating(false);
      console.error('Erro ao remover veículo de manutenção:', error);
      alert('Erro ao remover veículo de manutenção');
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const carregarDeparaPlacas = async () => {
    try {
      setCarregandoDepara(true);
      const resp = await axios.get('/api/monitoramento/depara-placas', {
        headers: getAuthHeaders()
      });
      setDeparaPlacas(resp.data || []);
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Erro ao carregar deparas de placas.');
    } finally {
      setCarregandoDepara(false);
    }
  };

  const salvarDeparaPlacas = async () => {
    const placaOrigem = deparaOrigem.trim().toUpperCase();
    const placaDestino = deparaDestino.trim().toUpperCase();

    if (!placaOrigem || !placaDestino) {
      alert('Informe placa de origem e placa de destino.');
      return;
    }

    if (placaOrigem === placaDestino) {
      alert('Placa de origem e destino nao podem ser iguais.');
      return;
    }

    try {
      setSalvandoDepara(true);
      await axios.post('/api/monitoramento/depara-placas', {
        placaOrigem,
        placaDestino
      }, {
        headers: getAuthHeaders()
      });

      setDeparaOrigem('');
      setDeparaDestino('');
      await carregarDeparaPlacas();
      await fetchDados();
      alert('Depara salvo com sucesso.');
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Erro ao salvar depara.');
    } finally {
      setSalvandoDepara(false);
    }
  };

  const excluirDeparaPlacas = async (id: number) => {
    if (!window.confirm('Deseja remover este depara de placa?')) {
      return;
    }

    try {
      await axios.delete(`/api/monitoramento/depara-placas/${id}`, {
        headers: getAuthHeaders()
      });
      await carregarDeparaPlacas();
      await fetchDados();
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Erro ao remover depara.');
    }
  };

  useEffect(() => {
    if (showModalDeparaPlacas) {
      carregarDeparaPlacas();
    }
  }, [showModalDeparaPlacas]);

  // Função para gerar relatório Excel de veículos em manutenção
  const gerarRelatorioEmManutencao = () => {
    const veiculosEmManutencao = veiculosFiltrados.filter(v => v.emManutencao);
    
    if (veiculosEmManutencao.length === 0) {
      alert('Não há veículos em manutenção no momento.');
      return;
    }

    const dadosExcel = veiculosEmManutencao.map(v => {
      let tempoManutencaoFormatado = '-';
      if (v.dtEntradaManutencao) {
        const entrada = new Date(v.dtEntradaManutencao);
        const agora = new Date();
        const diffMs = agora.getTime() - entrada.getTime();
        const diffH = Math.floor(diffMs / (1000 * 60 * 60));
        const diffM = Math.floor((diffMs / (1000 * 60)) % 60);
        tempoManutencaoFormatado = `${diffH}h ${diffM}min`;
      }
      return {
        'Placa': v.placa,
        'Motorista': v.motorista || 'Sem informação',
        'Data Entrada Manutenção': v.dtEntradaManutencao || 'Não disponível',
        'Tempo em Manutenção': tempoManutencaoFormatado,
        'Observações': v.obsManutencao || 'Sem observações'
      };
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dadosExcel);

    ws['!cols'] = [
      { wch: 12 },
      { wch: 25 },
      { wch: 20 },
      { wch: 18 },
      { wch: 40 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Veículos em Manutenção');

    const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    XLSX.writeFile(wb, `Relatorio_Manutencao_${dataAtual}.xlsx`);
  };

  // Função para gerar relatório Excel de veículos em movimento
  const gerarRelatorioEmMovimento = () => {
    const veiculosEmMovimento = veiculosFiltrados.filter(v => v.status === 'em_movimento' && !v.problemaRastreador);
    
    if (veiculosEmMovimento.length === 0) {
      alert('Não há veículos em movimento no momento.');
      return;
    }

    const dadosExcel = veiculosEmMovimento.map(v => ({
      'Placa': v.placa,
      'Motorista': v.motorista || 'Não informado',
      'Responsável': v.responsavel || 'Não informado',
      'Velocidade': `${v.velocidade} km/h`,
      'Localização': v.localizacao?.endereco || 'Não disponível'
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dadosExcel);
    ws['!cols'] = [{ wch: 12 }, { wch: 25 }, { wch: 25 }, { wch: 12 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Veículos em Movimento');

    const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    XLSX.writeFile(wb, `Relatorio_Em_Movimento_${dataAtual}.xlsx`);
  };

  // Função para gerar relatório Excel de veículos parados
  const gerarRelatorioParados = () => {
    const veiculosParados = veiculosFiltrados.filter(v => (v.status === 'parado' || (v.status === 'fora_de_rota' && v.velocidade === 0)) && !v.problemaRastreador);
    
    if (veiculosParados.length === 0) {
      alert('Não há veículos parados no momento.');
      return;
    }

    const dadosExcel = veiculosParados.map(v => {
      let tempoParadoFormatado = '-';
      if (typeof v.tempoParado === 'number' && v.tempoParado > 0) {
        const horas = Math.floor(v.tempoParado / 60);
        const minutos = v.tempoParado % 60;
        tempoParadoFormatado = horas > 0 ? `${horas}h ${minutos}min` : `${minutos}min`;
      }
      return {
        'Placa': v.placa,
        'Motorista': v.motorista || 'Não informado',
        'Responsável': v.responsavel || 'Não informado',
        'Tempo Parado (min)': v.tempoParado,
        'Tempo Parado (formatado)': tempoParadoFormatado,
        'Localização (Polígono)': v.poligono || 'Sem informação',
        'Endereço': v.localizacao?.endereco || 'Não disponível',
        'Em Manutenção': v.emManutencao ? 'Sim' : 'Não'
      };
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dadosExcel);
    ws['!cols'] = [
      { wch: 12 }, { wch: 25 }, { wch: 25 }, { wch: 15 }, { wch: 18 }, { wch: 30 }, { wch: 40 }, { wch: 12 }
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Veículos Parados');

    const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    XLSX.writeFile(wb, `Relatorio_Parados_${dataAtual}.xlsx`);
  };

  // ========== OTIMIZAÇÃO: Debounce no filtro de placa ==========
  useEffect(() => {
    const timer = setTimeout(() => {
      setFiltroPlacaDebounced(filtroPlaca);
    }, 300); // 300ms de delay

    return () => clearTimeout(timer);
  }, [filtroPlaca]);

  // Estado para detectar visibilidade da aba
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden);

  // Ping de sessão: avisa o backend que há usuário ativo no módulo.
  // Sem este ping, a sincronização Omnilink e os crons ficam pausados.
  useEffect(() => {
    const ping = () => {
      if (!document.hidden) {
        axios.post('/api/monitoramento/session/ping').catch(() => {});
      }
    };
    ping(); // imediato ao entrar no módulo
    const pingInterval = setInterval(ping, 30000);
    return () => clearInterval(pingInterval); // para ao sair do módulo
  }, []);

  // Buscar dados reais da API
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    // Não faz polling se a aba não está visível
    if (!isPageVisible) return;
    
    fetchDados();
    
    // Atualizar a cada 30 segundos
    const interval = setInterval(() => {
      fetchDados();
    }, 30000);
    return () => {
      clearInterval(interval);
      if (nucciRequestControllerRef.current) {
        nucciRequestControllerRef.current.abort();
        nucciRequestControllerRef.current = null;
      }
    };
  }, [isPageVisible]);

  const fetchDados = async () => {
    if (fetchInProgressRef.current) {
      fetchPendingRef.current = true;
      return;
    }

    fetchInProgressRef.current = true;

    try {
      setIsUpdating(true); // Marca que está atualizando
      
      // ========== OTIMIZAÇÃO: Buscar dados estáticos em paralelo ==========
      // Dados que mudam com menos frequência podem ser buscados em paralelo
      const [resVeiculos, resStats, resRotas, resOperacoes, resPoligonos, resCarretasNucci] = await Promise.all([
        axios.get('/api/monitoramento/veiculos', { params: { _: Date.now() } }),
        axios.get('/api/monitoramento/estatisticas'),
        axios.get('/api/monitoramento/rotas'),
        axios.get('/api/monitoramento/operacoes'),
        axios.get('/api/monitoramento/poligonos'),
        axios.get('/api/monitoramento/carretas-nucci').catch(() => ({ data: [] }))
      ]);

      const veiculosPrincipais = resVeiculos.data;
      
      // Atualizar dados estáticos imediatamente
      setEstatisticas({
        ...resStats.data,
        ultimaAtualizacao: new Date().toLocaleString('pt-BR')
      });
      setRotas(resRotas.data);
      setOperacoes(resOperacoes.data);
      setPoligonos(resPoligonos.data);
      setCarretasNucci(resCarretasNucci.data || []);

      // Buscar dados Nucci apenas para placas Volks (se houver) - EM PARALELO
      // Log de diagnóstico para FKS1D51
      const veiculoFKS1D51 = veiculosPrincipais.find((v: any) => v.placa === 'FKS1D51');
      if (veiculoFKS1D51) {
        console.log('[DIAGNÓSTICO FKS1D51] Veículo encontrado:', {
          placa: veiculoFKS1D51.placa,
          operacao: veiculoFKS1D51.operacao,
          temVolksNaOperacao: (veiculoFKS1D51.operacao || '').toLowerCase().includes('volks')
        });
      } else {
        console.log('[DIAGNÓSTICO FKS1D51] Veículo NÃO encontrado na lista de veiculosPrincipais');
      }
      
      const placasVolksArray = Array.from(new Set(veiculosPrincipais
        .map((v: any) => String(v.placa || '').trim().toUpperCase())
        .filter(Boolean)))
        .sort();
      const placasVolks = placasVolksArray.join(',');
      
      // Criar um mapa dos veículos anteriores para manter dados de CVA/Motorista durante a atualização
      const veiculosAnterioresMap = new Map();
      veiculos.forEach(v => {
        veiculosAnterioresMap.set(v.placa, {
          cva: v.cva,
          motorista: v.motorista,
          dataCVA: v.dataCVA,
          telefones: v.telefones,
          placaCarreta: v.placaCarreta
        });
      });

      // Processar veículos usando cache de dados da Nucci
      const dadosVeiculos = veiculosPrincipais.map((v: any) => {
        let telefones: string[] = [];
        let motorista = v.motorista || 'Sem informação';
        let cva: string | undefined = v.cva || undefined;
        let dataCVA = undefined;
        let placaCarreta = v.placa_carreta || undefined;
        let distanciaRestante: number | string | undefined = undefined;
        let numeroManifesto: string | undefined = undefined;
        let dataManifesto: string | undefined = undefined;
        let numeroContrato: string | undefined = undefined;

        // Usar cache de dados da Nucci para manter consistência
        const nucciCache = dadosNucciCacheRef.current[v.placa];
        // Sobrescrever com dados da Nucci se disponíveis para essa placa
        if (nucciCache) {
          cva = nucciCache.cva || cva;
          motorista = nucciCache.motorista || motorista;
          dataCVA = nucciCache.dataContrato || dataCVA;
          telefones = Array.isArray(nucciCache.telefones) && nucciCache.telefones.length > 0 ? nucciCache.telefones : telefones;
          placaCarreta = nucciCache.placaCarreta || placaCarreta;
          numeroManifesto = nucciCache.numeroManifesto || undefined;
          dataManifesto = nucciCache.dataManifesto || undefined;
          numeroContrato = nucciCache.numeroContrato || undefined;
          if (nucciCache.distanciaRestante !== undefined && nucciCache.distanciaRestante !== null) {
            distanciaRestante = nucciCache.distanciaRestante;
          } else if (nucciCache.distancia !== undefined && nucciCache.distancia !== null) {
            distanciaRestante = nucciCache.distancia;
          }
        } else {
          // Senão, tentar usar dados anteriores
          const dadosAnteriores = veiculosAnterioresMap.get(v.placa);
          if (dadosAnteriores) {
            cva = dadosAnteriores.cva || cva;
            motorista = dadosAnteriores.motorista || motorista;
            dataCVA = dadosAnteriores.dataCVA;
            telefones = dadosAnteriores.telefones || telefones;
            placaCarreta = dadosAnteriores.placaCarreta || placaCarreta;
            numeroManifesto = dadosAnteriores.numeroManifesto || undefined;
            dataManifesto = dadosAnteriores.dataManifesto || undefined;
            numeroContrato = dadosAnteriores.numeroContrato || undefined;
          }
        }
        
        if (!telefones || telefones.length === 0) {
          if (Array.isArray(v.telefones)) {
            telefones = Array.from(new Set((v.telefones as string[]).filter((t): t is string => Boolean(t))));
          } else {
            telefones = [v.telefone, v.celular].filter((t): t is string => Boolean(t));
            telefones = Array.from(new Set(telefones));
          }
        }
        
        return {
          id: v.id,
          placa: v.placa,
          motorista,
          cva,
          dataCVA,
          telefones,
          latitude: v.latitude ?? v.lat ?? v.localizacao?.lat,
          longitude: v.longitude ?? v.lng ?? v.localizacao?.lng,
          placaCarreta,
          sistema: v.sistema || 'Omnilink',
          responsavel: v.responsavel || '',
          status: v.status || 'parado',
          velocidade: v.velocidade || 0,
          localizacao: v.localizacao || { lat: -23.550520, lng: -46.633308, endereco: 'Localização não disponível' },
          ultimaAtualizacao: formatarHoraSemTimezone(v.ultimaAtualizacao),
          ultimaComunicacao: formatarDataSemTimezone(v.ultimaComunicacao),
          ultimaAtualizacaoOmnilink: formatarDataSemTimezone(v.ultimaAtualizacaoOmnilink),
          ultimaComunicacaoOmnilink: formatarDataSemTimezone(v.ultimaComunicacaoOmnilink),
          ultimaAtualizacaoTraccar: formatarDataSemTimezone(v.ultimaAtualizacaoTraccar),
          ultimaComunicacaoTraccar: formatarDataSemTimezone(v.ultimaComunicacaoTraccar),
          tempoParado: v.tempoParado || 0,
          operacao: v.operacao || '-',
          poligono: v.poligono || '-',
          sentidoRota: v.sentidoRota || 'desconhecido',
          confiancaSentido: v.confiancaSentido || 0,
          problemaRastreador: v.problemaRastreador || false,
          emManutencao: v.emManutencao || false,
          dtEntradaManutencao: v.dtEntradaManutencao || null,
          obsManutencao: v.obsManutencao || null,
          distanciaRestante: distanciaRestante,
          ultimoPoligono: v.ultimoPoligono || null,
          horarioSaidaUltimoPoligono: v.horarioSaidaUltimoPoligono || null,
          statusCarregamento: v.statusCarregamento || null,
          tipoComunicacao: v.tipoComunicacao || 'GPS',
          numeroManifesto,
          dataManifesto,
          numeroContrato
        };
      });
      
      // Atualizar estado imediatamente
      setVeiculos(dadosVeiculos);

      // Buscar dados da Nucci em background e atualizar cache
      if (placasVolks) {
        const now = Date.now();
        const nucciMeta = nucciFetchMetaRef.current;
        const cooldownMs = 60000;
        const deveBuscarNucci = !nucciMeta.inFlight && (
          nucciMeta.lastKey !== placasVolks ||
          (now - nucciMeta.lastAt) >= cooldownMs
        );

        if (deveBuscarNucci) {
          nucciMeta.inFlight = true;
          nucciMeta.lastKey = placasVolks;

          if (nucciRequestControllerRef.current) {
            nucciRequestControllerRef.current.abort();
          }

          const nucciController = new AbortController();
          nucciRequestControllerRef.current = nucciController;

          console.log('[NUCCI Frontend] Buscando dados para placas:', placasVolks);
          axios.get(`/api/monitoramento/volkswagen/dados-nucci?placas=${placasVolks}`, {
            signal: nucciController.signal
          })
          .then((res: any) => {
            const dadosNucci = res.data || {};
            console.log('[NUCCI Frontend] ✅ Dados recebidos:', Object.keys(dadosNucci).length, 'placas');
            console.log('[NUCCI Frontend] Placas com dados:', Object.keys(dadosNucci));
            console.log('[NUCCI Frontend] Conteúdo completo dos dadosNucci:', dadosNucci);
            // Atualizar cache
            Object.keys(dadosNucci).forEach(placa => {
              if (dadosNucci[placa]) {
                console.log(`[NUCCI Frontend] Cache para placa ${placa}:`, dadosNucci[placa]);
                dadosNucciCacheRef.current[placa] = dadosNucci[placa];
              }
            });
            // Atualizar veículos com novos dados da Nucci
            setVeiculos(prevVeiculos => prevVeiculos.map(v => {
              const nucci = dadosNucci[v.placa];
              if (nucci) {
                console.log(`[NUCCI Frontend] Atualizando veículo ${v.placa} com dados da Nucci:`, nucci);
                console.log(`[NUCCI Frontend] Antes do merge:`, v);
                const merged = {
                  ...v,
                  cva: nucci.cva || v.cva,
                  motorista: nucci.motorista || v.motorista,
                  dataCVA: nucci.dataContrato || v.dataCVA,
                  telefones: Array.isArray(nucci.telefones) && nucci.telefones.length > 0 ? nucci.telefones : v.telefones,
                  placaCarreta: nucci.placaCarreta || v.placaCarreta,
                  distanciaRestante: nucci.distanciaRestante ?? nucci.distancia ?? v.distanciaRestante,
                  statusCarregamento: nucci.statusCarregamento || v.statusCarregamento,
                  numeroManifesto: nucci.numeroManifesto || v.numeroManifesto,
                  dataManifesto: nucci.dataManifesto || v.dataManifesto,
                  numeroContrato: nucci.numeroContrato || v.numeroContrato
                };
                console.log(`[NUCCI Frontend] Depois do merge:`, merged);
                return merged;
              }
              return v;
            }));
          })
          .catch((err: any) => {
            if (err?.code === 'ERR_CANCELED') {
              return;
            }
            console.error('[NUCCI Frontend] ❌ Erro ao buscar dados Nucci:', err);
          })
          .finally(() => {
            nucciMeta.inFlight = false;
            nucciMeta.lastAt = Date.now();
            if (nucciRequestControllerRef.current === nucciController) {
              nucciRequestControllerRef.current = null;
            }
          });
        }
      }

      // Buscar dados menos críticos de forma assíncrona (não bloqueia a UI)
      Promise.all([
        axios.get('/api/motoristas/historico')
          .then(res => setHistoricoMotoristas(res.data || []))
          .catch(err => console.warn('Erro ao buscar motoristas historico:', err)),
        
        // Sincronizar motoristas apenas uma vez por sessão
        !motoristasSynced ? 
          axios.post('/api/monitoramento/performance/sync-motoristas')
            .then(() => setMotoristasSynced(true))
            .catch(err => console.warn('Erro ao sincronizar motoristas:', err))
          : Promise.resolve(),
        
        axios.get('/api/monitoramento/performance')
          .then(res => setPerformanceData(res.data || []))
          .catch(err => console.warn('Erro ao buscar performance:', err))
      ]);

      setShowRotas(false);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    } finally {
      setIsUpdating(false); // Finaliza atualização
      fetchInProgressRef.current = false;
      if (fetchPendingRef.current) {
        fetchPendingRef.current = false;
        setTimeout(() => {
          fetchDados();
        }, 0);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'em_movimento': return '#4caf50';
      case 'parado': return '#ff9800';
      case 'fora_de_rota': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'em_movimento': return 'Em Movimento';
      case 'parado': return 'Parado';
      case 'fora_de_rota': return 'Fora de Rota';
      default: return 'Desconhecido';
    }
  };

  // Renderizar badge de tipo de comunicação
  const renderIconeTipoComunicacao = (tipoComunicacao?: string) => {
    // Debug: verificar se está recebendo o valor
    if (tipoComunicacao) {
      // ...existing code...
    }
    
    if (!tipoComunicacao) return null;
    
    let backgroundColor = '#4CAF50'; // Verde padrão para GPRS
    let label = tipoComunicacao.toUpperCase();
    
    // Determinar cor e label baseado no tipo
    if (tipoComunicacao.toUpperCase().includes('SAT')) {
      backgroundColor = '#FFA726'; // Laranja/Amarelo para Satélite
      label = 'Satélite';
    } else if (tipoComunicacao.toUpperCase() === 'GPRS') {
      backgroundColor = '#4CAF50'; // Verde para GPRS
      label = 'GPRS';
    } else if (tipoComunicacao.toUpperCase() === 'GPS') {
      backgroundColor = '#2196F3'; // Azul para GPS
      label = 'GPS';
    } else if (tipoComunicacao.toUpperCase().includes('4G') || tipoComunicacao.toUpperCase().includes('LTE')) {
      backgroundColor = '#00BCD4'; // Ciano para 4G/LTE
      label = tipoComunicacao.toUpperCase();
    }
    
    return (
      <span 
        style={{ 
          display: 'inline-block',
          backgroundColor,
          color: 'white',
          fontSize: '0.65em',
          fontWeight: 700,
          padding: '2px 6px',
          borderRadius: 3,
          marginLeft: 6,
          textTransform: 'uppercase',
          letterSpacing: '0.3px',
          verticalAlign: 'middle'
        }}
        title={`Comunicação ${label}`}
      >
        {label}
      </span>
    );
  };

  // const responsaveis = Array.from(new Set(veiculos.map(v => v.responsavel && v.responsavel.trim() ? v.responsavel.trim() : 'Não informado')));

  // Obtém operação selecionada
  const operacaoSelecionada = filtroOperacao ? operacoesDisponiveis.find(op => op.id === filtroOperacao) : null;

  const matchesSistemaFilter = (sistema?: string | null) => {
    if (filtroSistema === 'todos') {
      return true;
    }

    return String(sistema || '')
      .split('/')
      .map(item => item.trim().toLowerCase())
      .filter(Boolean)
      .includes(filtroSistema);
  };

  // Filtra veículos por status, sentido, operação e placa/CVA/motorista (sem filtro de responsável)
  const veiculosFiltrados = veiculosBasePermitidos.filter(v => {
    const statusOk = filtroStatus === 'todos' || v.status === filtroStatus;
    const sentidoOk = filtroSentido === 'todos' || v.sentidoRota === filtroSentido;
    const sistemaOk = matchesSistemaFilter(v.sistema);
    const operacaoOk = !operacaoSelecionada || operacaoSelecionada.veiculos.includes(v.placa);
    const buscaOk = !filtroPlacaDebounced || 
      v.placa.toUpperCase().includes(filtroPlacaDebounced.toUpperCase()) ||
      (v.cva && v.cva.toUpperCase().includes(filtroPlacaDebounced.toUpperCase())) ||
      (v.motorista && v.motorista.toUpperCase().includes(filtroPlacaDebounced.toUpperCase()));
    return statusOk && sentidoOk && sistemaOk && operacaoOk && buscaOk;
  });
  const carretasMonitoradas = useMemo(() => {
    const carretasMap = new Map<string, CarretaPainel>();

    veiculosFiltrados.forEach(veiculo => {
      const placaCarreta = (veiculo.placaCarreta || '').trim().toUpperCase();

      if (!placaCarreta || carretasMap.has(placaCarreta)) {
        return;
      }

      carretasMap.set(placaCarreta, {
        placa: placaCarreta,
        cavalo: veiculo.placa,
        sistema: veiculo.sistema,
        motorista: veiculo.motorista,
        cva: veiculo.cva,
        telefones: veiculo.telefones,
        status: veiculo.status,
        velocidade: veiculo.velocidade,
        operacao: veiculo.operacao,
        localizacao: veiculo.localizacao,
        poligono: veiculo.poligono,
        ultimaAtualizacao: veiculo.ultimaAtualizacao,
        ultimaComunicacao: veiculo.ultimaComunicacao,
        ultimaAtualizacaoOmnilink: veiculo.ultimaAtualizacaoOmnilink,
        ultimaComunicacaoOmnilink: veiculo.ultimaComunicacaoOmnilink,
        ultimaAtualizacaoTraccar: veiculo.ultimaAtualizacaoTraccar,
        ultimaComunicacaoTraccar: veiculo.ultimaComunicacaoTraccar,
        tempoParado: veiculo.tempoParado,
        tipoComunicacao: veiculo.tipoComunicacao,
        statusCarregamento: veiculo.statusCarregamento,
        ultimoPoligono: veiculo.ultimoPoligono,
        horarioSaidaUltimoPoligono: veiculo.horarioSaidaUltimoPoligono,
        sentidoRota: veiculo.sentidoRota,
        confiancaSentido: veiculo.confiancaSentido,
        veiculoVinculado: veiculo
      });
    });

    return Array.from(carretasMap.values()).sort((a, b) => a.placa.localeCompare(b.placa));
  }, [veiculosFiltrados]);
  const carretasFiltradas = useMemo(() => {
    if (!carretasNucci.length) {
      return carretasMonitoradas;
    }

    const monitoramentoPorCarreta = new Map<string, Veiculo>();
    veiculosFiltrados.forEach(veiculo => {
      const placaCarreta = (veiculo.placaCarreta || '').trim().toUpperCase();
      if (placaCarreta && !monitoramentoPorCarreta.has(placaCarreta)) {
        monitoramentoPorCarreta.set(placaCarreta, veiculo);
      }
    });

    const termoBusca = filtroPlacaDebounced.trim().toUpperCase();

    return carretasNucci
      .map(carreta => {
        const placa = (carreta.placa || '').trim().toUpperCase();
        const veiculoVinculado = monitoramentoPorCarreta.get(placa) || null;

        return {
          placa,
          marca: carreta.marca,
          modelo: carreta.modelo,
          responsavel: carreta.responsavel,
          tipoCarreta: carreta.tipoCarreta,
          classificacaoInterna: carreta.classificacaoInterna,
          cavalo: veiculoVinculado?.placa,
          sistema: veiculoVinculado?.sistema || carreta.sistema || 'Nucci',
          motorista: veiculoVinculado?.motorista,
          cva: veiculoVinculado?.cva,
          telefones: veiculoVinculado?.telefones,
          status: veiculoVinculado?.status,
          velocidade: veiculoVinculado?.velocidade,
          operacao: veiculoVinculado?.operacao,
          localizacao: veiculoVinculado?.localizacao,
          poligono: veiculoVinculado?.poligono,
          ultimaAtualizacao: veiculoVinculado?.ultimaAtualizacao,
          ultimaComunicacao: veiculoVinculado?.ultimaComunicacao,
          ultimaAtualizacaoOmnilink: veiculoVinculado?.ultimaAtualizacaoOmnilink,
          ultimaComunicacaoOmnilink: veiculoVinculado?.ultimaComunicacaoOmnilink,
          ultimaAtualizacaoTraccar: veiculoVinculado?.ultimaAtualizacaoTraccar,
          ultimaComunicacaoTraccar: veiculoVinculado?.ultimaComunicacaoTraccar,
          tempoParado: veiculoVinculado?.tempoParado,
          tipoComunicacao: veiculoVinculado?.tipoComunicacao,
          statusCarregamento: veiculoVinculado?.statusCarregamento,
          ultimoPoligono: veiculoVinculado?.ultimoPoligono,
          horarioSaidaUltimoPoligono: veiculoVinculado?.horarioSaidaUltimoPoligono,
          sentidoRota: veiculoVinculado?.sentidoRota,
          confiancaSentido: veiculoVinculado?.confiancaSentido,
          veiculoVinculado
        } as CarretaPainel;
      })
      .filter(carreta => {
        if (!matchesSistemaFilter(carreta.sistema)) {
          return false;
        }

        if (!termoBusca) {
          return true;
        }

        return [
          carreta.placa,
          carreta.marca,
          carreta.modelo,
          carreta.responsavel,
          carreta.tipoCarreta,
          carreta.cavalo,
          carreta.motorista,
          carreta.cva
        ].some(valor => (valor || '').toUpperCase().includes(termoBusca));
      })
      .sort((a, b) => a.placa.localeCompare(b.placa));
  }, [carretasNucci, carretasMonitoradas, veiculosFiltrados, filtroPlacaDebounced, filtroSistema]);
  const operacoesReferencia = isSetorScopedAccess ? operacoesDisponiveis : operacoes;


  // Função para calcular distância entre dois pontos (Haversine)
  function calcularDistanciaKm(lat1: number, lon1: number, lat2: number, lon2: number) {
    const R = 6371; // km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Math.round(R * c);
  }

  const calcularCentroPoligono = (coordenadas?: Array<{ lat: number; lng: number }>) => {
    if (!coordenadas || coordenadas.length === 0) return null;
    const somaLat = coordenadas.reduce((acc, p) => acc + p.lat, 0);
    const somaLng = coordenadas.reduce((acc, p) => acc + p.lng, 0);
    return {
      lat: somaLat / coordenadas.length,
      lng: somaLng / coordenadas.length
    };
  };

  const encontrarPontoAlvoPorSentido = (veiculo: Veiculo) => {
    const sentido = veiculo.sentidoRota;
    if (!sentido || sentido === 'desconhecido') return null;

    let poligonoOrigem: Poligono | undefined;
    let poligonoDestino: Poligono | undefined;

    if (veiculo.operacao && operacoesReferencia.length > 0) {
      const operacao = operacoesReferencia.find(op =>
        veiculo.operacao && op.nome && veiculo.operacao.toLowerCase().includes(op.nome.toLowerCase())
      );

      if (operacao) {
        poligonoOrigem = poligonos.find(p => p.id === operacao.poligonoOrigem);
        poligonoDestino = poligonos.find(p => p.id === operacao.poligonoDestino);
      }
    }

    if (sentido === 'ida') {
      const centroDestino = calcularCentroPoligono(poligonoDestino?.coordenadas);
      if (centroDestino) return centroDestino;
    }

    if (sentido === 'volta') {
      const centroOrigem = calcularCentroPoligono(poligonoOrigem?.coordenadas);
      if (centroOrigem) return centroOrigem;
    }

    if (veiculo.operacao && rotas.length > 0) {
      const rota = rotas.find(r =>
        veiculo.operacao && r.nome && veiculo.operacao.toLowerCase().includes(r.nome.toLowerCase())
      );

      if (rota && rota.pontos && rota.pontos.length > 0) {
        if (sentido === 'ida') {
          return rota.pontos[rota.pontos.length - 1];
        }
        if (sentido === 'volta') {
          return rota.pontos[0];
        }
      }
    }

    return null;
  };

  // Criar dados de localização a partir dos veículos filtrados
  const localizacaoData = useMemo(() => {
    return veiculosFiltrados
      .filter(v => v.motorista && v.motorista !== 'Sem informação' && !v.problemaRastreador)
      .map(v => {
        // Pegar coordenadas do veículo
        const veiculoLat = v.latitude ?? v.localizacao?.lat;
        const veiculoLng = v.longitude ?? v.localizacao?.lng;

        // Encontrar rota do veículo baseado na operação
        let rota = null;
        if (v.operacao && rotas.length > 0) {
          rota = rotas.find(r => v.operacao && r.nome && v.operacao.toLowerCase().includes(r.nome.toLowerCase()));
        }

        // Encontrar polígonos de origem e destino pela operação
        let poligonoOrigem = null;
        let poligonoDestino = null;
        if (v.operacao && operacoesReferencia.length > 0) {
          const operacao = operacoesReferencia.find(op => v.operacao && op.nome && v.operacao.toLowerCase().includes(op.nome.toLowerCase()));
          if (operacao) {
            poligonoOrigem = poligonos.find(p => p.id === operacao.poligonoOrigem);
            poligonoDestino = poligonos.find(p => p.id === operacao.poligonoDestino);
          }
        }

        let kmRestante = 0;
        if (veiculoLat && veiculoLng) {
          let destinoLatLng = null;
          
          // Tentar usar polígonos de origem/destino da operação
          if (v.sentidoRota === 'ida' && poligonoDestino && poligonoDestino.coordenadas.length > 0) {
            // Usar centro do polígono de destino
            const lats = poligonoDestino.coordenadas.map(c => c.lat);
            const lngs = poligonoDestino.coordenadas.map(c => c.lng);
            destinoLatLng = {
              lat: lats.reduce((a, b) => a + b, 0) / lats.length,
              lng: lngs.reduce((a, b) => a + b, 0) / lngs.length
            };
          } else if (v.sentidoRota === 'volta' && poligonoOrigem && poligonoOrigem.coordenadas.length > 0) {
            // Usar centro do polígono de origem
            const lats = poligonoOrigem.coordenadas.map(c => c.lat);
            const lngs = poligonoOrigem.coordenadas.map(c => c.lng);
            destinoLatLng = {
              lat: lats.reduce((a, b) => a + b, 0) / lats.length,
              lng: lngs.reduce((a, b) => a + b, 0) / lngs.length
            };
          }
          // Fallback: usar pontos da rota se existir
          else if (rota && rota.pontos && rota.pontos.length > 0) {
            if (v.sentidoRota === 'ida') {
              destinoLatLng = rota.pontos[rota.pontos.length - 1];
            } else if (v.sentidoRota === 'volta') {
              destinoLatLng = rota.pontos[0];
            }
          }
          
          if (destinoLatLng) {
            kmRestante = calcularDistanciaKm(veiculoLat, veiculoLng, destinoLatLng.lat, destinoLatLng.lng);
          }
        }

        // Se não conseguiu calcular, usar distanciaRestante do backend se existir
        if (kmRestante === 0 && v.distanciaRestante) {
          kmRestante = typeof v.distanciaRestante === 'number' ? v.distanciaRestante : parseInt(String(v.distanciaRestante)) || 0;
        }

        // Calcular tempo estimado
        let tempoEstimado = '-';
        if (kmRestante > 0 && v.velocidade && v.velocidade > 0) {
          const horasEstimadas = kmRestante / v.velocidade;
          const horas = Math.floor(horasEstimadas);
          const minutos = Math.round((horasEstimadas - horas) * 60);
          if (horas > 0) {
            tempoEstimado = `${horas}h ${minutos}m`;
          } else {
            tempoEstimado = `${minutos}m`;
          }
        }

        // Determinar status do veículo
        let statusVeiculo = 'Parado';
        if (v.status === 'em_movimento' || (v.velocidade && v.velocidade > 5)) {
          statusVeiculo = 'Em Movimento';
        }

        // Determinar localização atual
        let localizacaoAtual = '-';
        
        // Converter coordenadas para número se necessário
        const latNum = typeof veiculoLat === 'number' ? veiculoLat : (veiculoLat ? parseFloat(String(veiculoLat)) : null);
        const lngNum = typeof veiculoLng === 'number' ? veiculoLng : (veiculoLng ? parseFloat(String(veiculoLng)) : null);
        
        // Se está dentro de um polígono E está parado, mostrar o nome do polígono
        if (statusVeiculo === 'Parado' && v.poligono) {
          localizacaoAtual = v.poligono;
        }
        // Para todos os outros casos, mostrar coordenadas lat/long se disponível
        else if (latNum !== null && lngNum !== null && !isNaN(latNum) && !isNaN(lngNum)) {
          localizacaoAtual = `${latNum.toFixed(6)}, ${lngNum.toFixed(6)}`;
        }
        // Se não tem coordenadas mas tem polígono, mostrar o polígono
        else if (v.poligono) {
          localizacaoAtual = v.poligono;
        }
        // Fallback: usar o endereço textual salvo no banco (ds_endereco)
        else if (v.localizacao?.endereco && v.localizacao.endereco !== 'Localização não disponível') {
          localizacaoAtual = v.localizacao.endereco;
        }

        return {
          cavalo: v.placa,
          motorista: v.motorista,
          localizacao_atual: localizacaoAtual,
          sentido: v.sentidoRota || 'desconhecido',
          status: statusVeiculo,
          manutencao: v.emManutencao ? 'Sim' : 'Não',
          km: kmRestante,
          tempo: tempoEstimado,
          carregamento: v.statusCarregamento || '-',
          tempo_parado: v.tempoParado || 0
        };
      })
      .sort((a, b) => {
        // Se houver filtro de sentido específico (ida ou volta), ordenar por KM
        if (filtroSentido !== 'todos') {
          // Ordenar por KM crescente (mais próximo primeiro)
          if (a.km !== b.km) {
            return a.km - b.km;
          }
          // Se KM igual, ordenar por placa
          return a.cavalo.localeCompare(b.cavalo);
        }
        
        // Se não houver filtro, ordenar por sentido primeiro (ida, volta, desconhecido)
        const ordemSentido = { 'ida': 1, 'volta': 2, 'desconhecido': 3 };
        const ordemA = ordemSentido[a.sentido as keyof typeof ordemSentido] || 4;
        const ordemB = ordemSentido[b.sentido as keyof typeof ordemSentido] || 4;
        
        if (ordemA !== ordemB) {
          return ordemA - ordemB;
        }
        
        // Se sentido igual, ordenar por placa
        return a.cavalo.localeCompare(b.cavalo);
      });
  }, [veiculosFiltrados, rotas, operacoes, poligonos, filtroSentido]);

  // Buscar endereços via Nominatim para veículos com coordenadas na tabela
  useEffect(() => {
    const coordPattern = /^-?\d+\.\d+,\s*-?\d+\.\d+$/;
    const toFetch: Array<{ key: string; lat: number; lng: number }> = [];

    for (const row of localizacaoData) {
      const loc = row.localizacao_atual;
      if (!loc || !coordPattern.test(loc)) continue;
      const [latStr, lngStr] = loc.split(',');
      const lat = parseFloat(latStr.trim());
      const lng = parseFloat(lngStr.trim());
      const cacheKey = `${lat.toFixed(4)},${lng.toFixed(4)}`;
      if (!geocodeCacheRef.current[cacheKey] && !geocodePendingRef.current[cacheKey]) {
        toFetch.push({ key: cacheKey, lat, lng });
      }
    }

    // Remover duplicatas
    const unique = toFetch.filter((item, idx, arr) => arr.findIndex(x => x.key === item.key) === idx);
    if (unique.length === 0) return;

    // Busca serial respeitando o limite de 1 req/s do Nominatim
    const timers: ReturnType<typeof setTimeout>[] = [];
    unique.forEach((item, i) => {
      geocodePendingRef.current[item.key] = true;
      const t = setTimeout(async () => {
        try {
          const resp = await axios.get('/api/monitoramento/reverse-geocode', {
            params: { lat: item.lat, lng: item.lng }
          });
          const enderecoFormatado = resp.data?.endereco;
          geocodeCacheRef.current[item.key] = enderecoFormatado || '';
          if (enderecoFormatado) {
            setEnderecos(prev => ({ ...prev, [item.key]: enderecoFormatado }));
          }
        } catch {
          // ignorar erros de geocoding
        } finally {
          geocodePendingRef.current[item.key] = false;
        }
      }, i * 1100);
      timers.push(t);
    });

    return () => timers.forEach(t => clearTimeout(t));
  }, [localizacaoData]);

  const rotasPermitidasPorOperacao = useMemo(() => {
    const operacoesAtivas = operacoesDisponiveisAtivas;

    if (filtroOperacao) {
      const operacaoFiltrada = operacoesAtivas.find(op => op.id === filtroOperacao);
      return normalizarIdsRota(operacaoFiltrada?.rotas || []);
    }

    return normalizarIdsRota(operacoesAtivas.flatMap(op => op.rotas));
  }, [operacoesDisponiveisAtivas, filtroOperacao]);

  const rotasGerencialPermitidasSet = useMemo(() => {
    if (!isGerencialAccess) return null;
    return new Set<number>(normalizarIdsRota(operacoesDisponiveisAtivas.flatMap(op => op.rotas)));
  }, [isGerencialAccess, operacoesDisponiveisAtivas]);

  const rotasVisiveisGestao = useMemo(() => {
    if (!isGerencialAccess || !rotasGerencialPermitidasSet) return rotas;
    return rotas.filter(rota => rotasGerencialPermitidasSet.has(Number(rota.id)));
  }, [isGerencialAccess, rotas, rotasGerencialPermitidasSet]);

  // Atualiza rotas selecionadas com base na operação/filtro atual.
  React.useEffect(() => {
    if (isSetorScopedAccess) {
      setRotasSelecionadas(rotasPermitidasPorOperacao);
      return;
    }

    if (operacaoSelecionada) {
      setRotasSelecionadas(normalizarIdsRota(operacaoSelecionada.rotas));
    }
  }, [isSetorScopedAccess, operacaoSelecionada, rotasPermitidasPorOperacao, operacoesDisponiveisAtivas]);

  // const toggleResponsavel = (responsavel: string) => {
  //   setFiltroResponsavel(prev => 
  //     prev.includes(responsavel) 
  //       ? prev.filter(r => r !== responsavel)
  //       : [...prev, responsavel]
  //   );
  // };

  // Indicadores filtrados
  const isParadoEfetivo = (veiculo: Veiculo) => (
    (veiculo.status === 'parado' || (veiculo.status === 'fora_de_rota' && Number(veiculo.velocidade || 0) === 0))
    && !veiculo.problemaRastreador
    && !veiculo.emManutencao
  );

  const estatisticasFiltradas = {
    totalVeiculos: veiculosFiltrados.length,
    emMovimento: veiculosFiltrados.filter(v => v.status === 'em_movimento' && !v.problemaRastreador && !v.emManutencao).length,
    parados: veiculosFiltrados.filter(v => isParadoEfetivo(v)).length,
    foraDeRota: veiculosFiltrados.filter(v => v.status === 'fora_de_rota' && !v.problemaRastreador && !v.emManutencao).length,
    problemaRastreador: veiculosFiltrados.filter(v => v.problemaRastreador).length,
    emManutencao: veiculosFiltrados.filter(v => v.emManutencao).length,
  };

  // Abrir popup do marcador selecionado quando veiculoSelecionado mudar
  useEffect(() => {
    if (veiculoSelecionado && markerRefs.current[veiculoSelecionado.id]) {
      const marker = markerRefs.current[veiculoSelecionado.id];
      if (marker && marker.openPopup) {
        marker.openPopup();
      }
      if (mapContainerRef.current && mapContainerRef.current.setView && veiculoSelecionado.localizacao) {
        mapContainerRef.current.setView([veiculoSelecionado.localizacao.lat, veiculoSelecionado.localizacao.lng], 16);
      }
    }
  }, [veiculoSelecionado]);

  const toggleRota = (rotaId: number) => {
    setNovaOperacao(prev => ({
      ...prev,
      rotas: prev.rotas.includes(rotaId) 
        ? prev.rotas.filter(id => id !== rotaId)
        : [...prev.rotas, rotaId]
    }));
  };

  // Ícone de caminhão customizado com cabine colorida
  const createCustomIcon = (veiculo: Veiculo) => {
    // Se tem problema no rastreador, usa cor roxa
    const cabineColor = veiculo.problemaRastreador ? '#9c27b0' : getStatusColor(veiculo.status);
    const status = veiculo.problemaRastreador ? 'problema' : veiculo.status;
    const origemLat = veiculo.latitude ?? veiculo.localizacao?.lat;
    const origemLng = veiculo.longitude ?? veiculo.localizacao?.lng;
    const pontoAlvo = encontrarPontoAlvoPorSentido(veiculo);

    // Mantém o caminhão sempre "em pé" (sem virar para baixo/cima),
    // mudando apenas para direita/esquerda conforme direção do deslocamento.
    const apontaParaDireita =
      origemLat !== undefined &&
      origemLng !== undefined &&
      pontoAlvo?.lat !== undefined &&
      pontoAlvo?.lng !== undefined
        ? pontoAlvo.lng >= origemLng
        : veiculo.sentidoRota !== 'volta';

    const scaleX = apontaParaDireita ? -1 : 1;
    
    return L.divIcon({
      className: 'custom-truck-marker',
      html: `
        <div style="transform: scaleX(${scaleX}); transform-origin: center; transition: transform 0.3s ease;">
          <svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="shadow-${status}" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="1" stdDeviation="1.5" flood-opacity="0.3"/>
              </filter>
            </defs>
            <g filter="url(#shadow-${status})">
              <!-- Baú (container) -->
              <rect x="14" y="11" width="18" height="12" rx="2" fill="#4A90E2" stroke="#2C5F8D" stroke-width="1.2"/>
              <!-- Detalhe do baú -->
              <line x1="23" y1="11" x2="23" y2="23" stroke="#2C5F8D" stroke-width="0.8" opacity="0.5"/>
              
              <!-- Cabine -->
              <rect x="4" y="13" width="12" height="10" rx="2" fill="${cabineColor}" stroke="#1a1a1a" stroke-width="1.2"/>
              <!-- Janela da cabine -->
              <rect x="6.5" y="15" width="6" height="5" rx="1" fill="rgba(255,255,255,0.7)" stroke="#1a1a1a" stroke-width="0.6"/>
              
              <!-- Para-choque -->
              <rect x="4" y="23" width="12" height="2" rx="0.5" fill="#333"/>
              
              <!-- Rodas -->
              <circle cx="11" cy="28" r="3" fill="#2a2a2a" stroke="#444" stroke-width="1"/>
              <circle cx="11" cy="28" r="1.8" fill="#555"/>
              <circle cx="27" cy="28" r="3" fill="#2a2a2a" stroke="#444" stroke-width="1"/>
              <circle cx="27" cy="28" r="1.8" fill="#555"/>
            </g>
          </svg>
        </div>
      `,
      iconSize: [36, 36],
      iconAnchor: [18, 30],
      popupAnchor: [0, -30]
    });
  };

  const buscarLatLngPorEndereco = async (endereco: string) => {
    if (!endereco) return { lat: '', lng: '' };
    try {
      const res = await axios.get('/api/monitoramento/geocode-search', {
        params: { q: endereco }
      });
      const data = res.data?.results;
      if (data && data.length > 0) {
        return { lat: data[0].lat, lng: data[0].lon };
      }
    } catch (err) {}
    return { lat: '', lng: '' };
  };

  const extrairLatLngDoTexto = (valor: string) => {
    const texto = String(valor || '').trim();
    const match = texto.match(/(-?\d+(?:[.,]\d+)?)\s*[,;\s]\s*(-?\d+(?:[.,]\d+)?)/);
    if (!match) return null;

    const lat = Number(match[1].replace(',', '.'));
    const lng = Number(match[2].replace(',', '.'));

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;

    return { lat: String(lat), lng: String(lng) };
  };

  const resolverLocalParaPonto = async (valor: string) => {
    const texto = String(valor || '').trim();
    if (!texto) return null;

    const coordenadas = extrairLatLngDoTexto(texto);
    if (coordenadas) {
      return { ...coordenadas, endereco: texto };
    }

    const coords = await buscarLatLngPorEndereco(texto);
    if (coords.lat && coords.lng) {
      return { lat: String(coords.lat), lng: String(coords.lng), endereco: texto };
    }

    return null;
  };

  const sincronizarOrigemDestinoNosPontos = async (origemValor = novaRota.origem, destinoValor = novaRota.destino) => {
    const [origemPonto, destinoPonto] = await Promise.all([
      resolverLocalParaPonto(origemValor),
      resolverLocalParaPonto(destinoValor)
    ]);

    if (!origemPonto && !destinoPonto) return novaRota.pontos;

    const pontosAtuais = [...novaRota.pontos];
    const paradasSelecionadas = novaRota.poligonosParada || [];
    let pontosIntermediarios = paradasSelecionadas.length
      ? paradasSelecionadas.map(parada => ({
          lat: String(parada.lat),
          lng: String(parada.lng),
          endereco: parada.nome,
        }))
      : pontosAtuais;

    if (!paradasSelecionadas.length && pontosAtuais.length >= 2) {
      pontosIntermediarios = pontosAtuais.slice(1, -1);
    } else if (!paradasSelecionadas.length && pontosAtuais.length === 1) {
      const unico = pontosAtuais[0];
      const unicoVazio = !unico.lat && !unico.lng && !unico.endereco;
      pontosIntermediarios = unicoVazio ? [] : [unico];
    }

    const destinoAtual = pontosAtuais.length >= 2 ? pontosAtuais[pontosAtuais.length - 1] : { lat: '', lng: '', endereco: '' };
    const novosPontos = [
      origemPonto || pontosAtuais[0] || { lat: '', lng: '', endereco: '' },
      ...pontosIntermediarios,
      destinoPonto || destinoAtual
    ];

    const pontosFinais = novosPontos.length > 0 ? novosPontos : [{ lat: '', lng: '', endereco: '' }];
    setNovaRota(prev => ({ ...prev, pontos: pontosFinais, pontosDetalhados: [] }));
    return pontosFinais;
  };

  const poligonosFiltradosParada = useMemo(() => {
    const termo = normalizeText(filtroPoligonoParada);
    return poligonos
      .filter(poligono => {
        if (poligono.ativa === false) return false;
        if (!termo) return true;
        return normalizeText(poligono.nome).includes(termo) || normalizeText(poligono.tipo).includes(termo);
      })
      .slice(0, 80);
  }, [poligonos, filtroPoligonoParada]);

  const calcularCentroPoligonoParada = (poligono: Poligono) => {
    const coordenadasValidas = (poligono.coordenadas || [])
      .map(coordenada => ({
        lat: Number(coordenada.lat),
        lng: Number(coordenada.lng),
      }))
      .filter(coordenada => Number.isFinite(coordenada.lat) && Number.isFinite(coordenada.lng));

    if (!coordenadasValidas.length) return null;

    return {
      lat: coordenadasValidas.reduce((total, coordenada) => total + coordenada.lat, 0) / coordenadasValidas.length,
      lng: coordenadasValidas.reduce((total, coordenada) => total + coordenada.lng, 0) / coordenadasValidas.length,
    };
  };

  const montarPontosComParadas = (
    pontosAtuais: PontoRotaEdicao[],
    paradas: PoligonoParadaSelecionado[]
  ) => {
    const origemAtual = pontosAtuais[0];
    const destinoAtual = pontosAtuais.length >= 2 ? pontosAtuais[pontosAtuais.length - 1] : null;
    const origemTemCoordenada = Boolean(origemAtual?.lat && origemAtual?.lng);
    const destinoTemCoordenada = Boolean(destinoAtual?.lat && destinoAtual?.lng);

    const pontosParadas = paradas.map(parada => ({
      lat: String(parada.lat),
      lng: String(parada.lng),
      endereco: parada.nome,
    }));

    const pontosMontados = [
      ...(origemTemCoordenada ? [origemAtual] : []),
      ...pontosParadas,
      ...(destinoTemCoordenada ? [destinoAtual as PontoRotaEdicao] : []),
    ];

    return pontosMontados.length ? pontosMontados : [{ lat: '', lng: '', endereco: '' }];
  };

  const montarPontosIdaVolta = React.useCallback((
    pontos: PontoRotaEdicao[],
    idaVolta: boolean
  ) => {
    const pontosValidos = (pontos || []).filter(ponto => ponto && ponto.lat && ponto.lng);
    if (!idaVolta || pontosValidos.length < 2) return pontos;

    const retorno = pontosValidos.slice(0, -1).reverse();
    return [...pontosValidos, ...retorno];
  }, []);

  const formatarTempoHHMM = (valor: string | number | undefined | null) => {
    const horasDecimais = Number(String(valor || '').replace(',', '.'));
    if (!Number.isFinite(horasDecimais) || horasDecimais <= 0) return '';

    const minutosTotais = Math.round(horasDecimais * 60);
    const horas = Math.floor(minutosTotais / 60);
    const minutos = minutosTotais % 60;

    return `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}`;
  };

  const alternarPoligonoParada = (poligono: Poligono) => {
    const centro = calcularCentroPoligonoParada(poligono);
    if (!centro) {
      alert('Este poligono nao possui coordenadas validas para usar como parada.');
      return;
    }

    setNovaRota(prev => {
      const paradasAtuais = prev.poligonosParada || [];
      const jaSelecionado = paradasAtuais.some(parada => String(parada.id) === String(poligono.id));
      const proximasParadas = jaSelecionado
        ? paradasAtuais.filter(parada => String(parada.id) !== String(poligono.id))
        : [
            ...paradasAtuais,
            {
              id: poligono.id,
              nome: poligono.nome,
              tipo: poligono.tipo,
              lat: centro.lat,
              lng: centro.lng,
            },
          ];

      return {
        ...prev,
        poligonosParada: proximasParadas,
        pontos: montarPontosComParadas(prev.pontos, proximasParadas),
        pontosDetalhados: [],
      };
    });
  };

  const calcularDistanciaKmEntrePontos = (pontos: Array<{ lat: number | string; lng: number | string }>) => {
    const pontosNumericos = pontos
      .map(ponto => ({
        lat: Number(ponto.lat),
        lng: Number(ponto.lng)
      }))
      .filter(ponto => Number.isFinite(ponto.lat) && Number.isFinite(ponto.lng));

    if (pontosNumericos.length < 2) return 0;

    const toRad = (valor: number) => (valor * Math.PI) / 180;
    const raioTerraKm = 6371;

    return pontosNumericos.slice(1).reduce((total, pontoAtual, index) => {
      const pontoAnterior = pontosNumericos[index];
      const dLat = toRad(pontoAtual.lat - pontoAnterior.lat);
      const dLng = toRad(pontoAtual.lng - pontoAnterior.lng);
      const lat1 = toRad(pontoAnterior.lat);
      const lat2 = toRad(pontoAtual.lat);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      return total + raioTerraKm * c;
    }, 0);
  };

  useEffect(() => {
    const pontosDetalhadosValidos = Array.isArray(novaRota.pontosDetalhados)
      ? novaRota.pontosDetalhados.filter(ponto => ponto && Number.isFinite(Number(ponto.lat)) && Number.isFinite(Number(ponto.lng)))
      : [];

    const pontosBase = Array.isArray(novaRota.pontos)
      ? montarPontosIdaVolta(novaRota.pontos, novaRota.tipoRota === 'ida_volta')
      : [];

    const pontosBaseValidos = pontosBase.length
      ? pontosBase.filter(ponto => ponto && ponto.lat && ponto.lng)
      : [];

    const pontosParaCalculo = pontosDetalhadosValidos.length >= 2 ? pontosDetalhadosValidos : pontosBaseValidos;

    if (pontosParaCalculo.length < 2) {
      setNovaRota(prev => {
        if (!prev.distancia && !prev.tempoEstimado) return prev;
        return { ...prev, distancia: '', tempoEstimado: '' };
      });
      return;
    }

    const distanciaKm = calcularDistanciaKmEntrePontos(pontosParaCalculo);
    if (!Number.isFinite(distanciaKm) || distanciaKm <= 0) return;

    const distanciaFormatada = distanciaKm >= 10
      ? String(Math.round(distanciaKm))
      : distanciaKm.toFixed(1);
    const tempoFormatado = (distanciaKm / 50).toFixed(2);

    setNovaRota(prev => {
      if (prev.distancia === distanciaFormatada && prev.tempoEstimado === tempoFormatado) return prev;
      return {
        ...prev,
        distancia: distanciaFormatada,
        tempoEstimado: tempoFormatado
      };
    });
  }, [novaRota.pontos, novaRota.pontosDetalhados, novaRota.tipoRota, montarPontosIdaVolta]);

  const rotaAtualIdaVolta = novaRota.tipoRota === 'ida_volta';
  const pontosRotaPreview = useMemo(
    () => montarPontosIdaVolta(novaRota.pontos, rotaAtualIdaVolta),
    [novaRota.pontos, rotaAtualIdaVolta, montarPontosIdaVolta]
  );
  const tempoEstimadoFormatado = formatarTempoHHMM(novaRota.tempoEstimado);

  const importarRotaDoLink = async () => {
    const link = (novaRota.linkMaps || '').trim();

    if (!link) {
      alert('Cole um link do Google Maps para importar a rota.');
      return;
    }

    try {
      const { data } = await axios.post('/api/monitoramento/rotas/importar-link', {
        link,
        nome: novaRota.nome,
        cor: novaRota.cor
      });

      setNovaRota(prev => ({
        ...prev,
        nome: data.nome || prev.nome,
        origem: data.origem || prev.origem,
        destino: data.destino || prev.destino,
        pontosParada: data.pontosParada ?? prev.pontosParada,
        distancia: data.distancia !== undefined && data.distancia !== null ? String(data.distancia) : prev.distancia,
        tempoEstimado: data.tempoEstimado !== undefined && data.tempoEstimado !== null ? String(data.tempoEstimado) : prev.tempoEstimado,
        cor: data.cor || prev.cor,
        pontos: Array.isArray(data.pontos) && data.pontos.length > 0
          ? data.pontos.map((ponto: any) => ({
              lat: ponto.lat !== undefined && ponto.lat !== null ? String(ponto.lat) : '',
              lng: ponto.lng !== undefined && ponto.lng !== null ? String(ponto.lng) : '',
              endereco: ponto.endereco || ''
            }))
          : prev.pontos,
        pontosDetalhados: Array.isArray(data.pontosDetalhados) ? data.pontosDetalhados : [],
      }));

      alert('Rota importada com sucesso. Revise os dados e salve a rota.');
    } catch (err: any) {
      console.error('Erro ao importar rota do link:', err);
      alert(err?.response?.data?.message || err?.response?.data?.error || 'Não foi possível importar o link informado.');
    }
  };

  // Buscar trajeto detalhado para uma rota
  const buscarRotaDetalhada = async (rota: Rota) => {
    const pontosBase = (rota.pontos || [])
      .filter(p => p.lat && p.lng)
      .map(p => [Number(p.lat), Number(p.lng)] as [number, number]);

    if (pontosBase.length < 2) return;
    
    console.log(`[ROTA] Buscando rota detalhada para: ${rota.nome} (ID: ${rota.id})`);
    
    try {
      const coords = pontosBase
        .map(([lat, lng]) => `${lng},${lat}`)
        .join(';');
      if (!coords) {
        console.warn(`[ROTA] Sem coordenadas válidas para rota ${rota.nome}`);
        return;
      }
      
      console.log(`[ROTA] Chamando API OSRM com ${rota.pontos.length} pontos...`);
      const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`);
      const data = await res.json();
      
      if (data.routes && data.routes[0] && data.routes[0].geometry && data.routes[0].geometry.coordinates) {
        const polyline = data.routes[0].geometry.coordinates.map((coord: [number, number]) => [coord[1], coord[0]]);
        if (polyline.length >= 2) {
          setRotasDetalhadas(prev => ({ ...prev, [rota.id]: polyline }));
        }
        console.log(`[ROTA] ✅ Rota detalhada obtida: ${polyline.length} pontos`);

        // Persistir em background para próximos carregamentos (não bloqueia render).
        const pontosDetalhados = polyline.map(([lat, lng]: [number, number]) => ({ lat, lng }));
        axios.put(`/api/monitoramento/rotas/${rota.id}/pontos-detalhados`, { pontosDetalhados }).catch((err: any) => {
          console.error(`[ROTA] ❌ Erro ao salvar pontos detalhados:`, err.response?.data || err.message);
        });
      } else {
        console.warn(`[ROTA] Resposta da API OSRM sem rota válida:`, data);
      }
    } catch (err) {
      console.error(`[ROTA] ❌ Erro ao buscar rota detalhada:`, err);
    }
  };

  // Quando rotas são marcadas/desmarcadas, buscar detalhes
  useEffect(() => {
    console.log('[ROTA] Rotas selecionadas:', rotasSelecionadas);
    console.log('[ROTA] Rotas disponíveis:', rotas.map(r => ({ id: r.id, nome: r.nome })));
    console.log('[ROTA] Rotas detalhadas em cache:', Object.keys(rotasDetalhadas));
    
    rotasSelecionadas.forEach(rotaId => {
      const rotaIdNormalizado = Number(rotaId);
      const rota = rotas.find(r => Number(r.id) === rotaIdNormalizado);
      if (rota) {
        if (!rotasDetalhadas[rotaIdNormalizado]) {
          console.log(`[ROTA] 🔍 Buscando rota detalhada para: ${rota.nome} (não está em cache)`);
          buscarRotaDetalhada(rota);
        } else {
          console.log(`[ROTA] ✓ Rota ${rota.nome} já está em cache com ${rotasDetalhadas[rotaIdNormalizado].length} pontos`);
        }
      } else {
        console.warn(`[ROTA] ⚠️ Rota ID ${rotaIdNormalizado} selecionada mas não encontrada na lista`);
      }
    });
  }, [rotasSelecionadas, rotas]);

  // Pré-carrega no cache local os pontos detalhados vindos do backend.
  useEffect(() => {
    setRotasDetalhadas(prev => {
      let alterou = false;
      const next = { ...prev };

      rotas.forEach(rota => {
        const pontosDetalhados = (rota.pontosDetalhados || [])
          .filter(p => p && Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)))
          .map(p => [Number(p.lat), Number(p.lng)] as [number, number]);

        if (pontosDetalhados.length >= 2 && !next[rota.id]) {
          next[rota.id] = pontosDetalhados;
          alterou = true;
        }
      });

      return alterou ? next : prev;
    });
  }, [rotas]);

  const editarRota = (rota: Rota) => {
    if (isGerencialAccess && rotasGerencialPermitidasSet && !rotasGerencialPermitidasSet.has(Number(rota.id))) {
      alert('Você não possui permissão para editar esta rota.');
      return;
    }

    setRotaEmEdicao(rota.id);
    const pontosRotaExistente = rota.pontos || [];
    const primeiroPonto = pontosRotaExistente[0];
    const ultimoPonto = pontosRotaExistente[pontosRotaExistente.length - 1];
    const rotaSalvaIdaVolta = Boolean(
      primeiroPonto &&
      ultimoPonto &&
      pontosRotaExistente.length >= 3 &&
      Math.abs(Number(primeiroPonto.lat) - Number(ultimoPonto.lat)) < 0.00001 &&
      Math.abs(Number(primeiroPonto.lng) - Number(ultimoPonto.lng)) < 0.00001
    );
    const pontosParadaSalvos = Array.isArray(rota.pontosParada)
      ? rota.pontosParada
      : Array.isArray(rota.pontosParada?.poligonos)
        ? rota.pontosParada.poligonos
        : [];
    const pontosBaseEdicao = rotaSalvaIdaVolta
      ? pontosRotaExistente.slice(0, Math.ceil(pontosRotaExistente.length / 2))
      : pontosRotaExistente;

    setNovaRota({
      nome: rota.nome,
      descricao: '',
      linkMaps: '',
      origem: rota.origem,
      destino: rota.destino,
      pontosParada: typeof rota.pontosParada === 'string' ? rota.pontosParada : '',
      distancia: rota.distancia?.toString() || '',
      tempoEstimado: rota.tempoEstimado?.toString() || '',
      tipoRota: rotaSalvaIdaVolta ? 'ida_volta' : 'ida',
      cor: rota.cor || '#2196f3',
      pontos: pontosBaseEdicao && pontosBaseEdicao.length > 0 ? pontosBaseEdicao.map(p => ({ lat: p.lat.toString(), lng: p.lng.toString(), endereco: '' })) : [{ lat: '', lng: '', endereco: '' }],
      pontosDetalhados: rota.pontosDetalhados || [],
      poligonosParada: pontosParadaSalvos as unknown as PoligonoParadaSelecionado[]
    });
    setShowCadastroRota(true);
  };

  const deletarRota = async (rotaId: number) => {
    if (!canManageFullMonitoring) {
      alert('Você não possui permissão para excluir rotas.');
      return;
    }

    if (!window.confirm('Tem certeza que deseja excluir esta rota?')) return;
    try {
      await axios.delete(`/api/monitoramento/rotas/${rotaId}`);
      fetchDados(); // Atualiza lista
      if (rotaSelecionada && rotaSelecionada.id === rotaId) setRotaSelecionada(null);
    } catch (err) {
      alert('Erro ao excluir rota!');
    }
  };

  // Funções de Polígonos
  const salvarPoligono = async (poligono: Poligono) => {
    try {
      await axios.post('/api/monitoramento/poligonos', poligono);
      await fetchDados();
      alert('Polígono cadastrado com sucesso!');
    } catch (err) {
      console.error('Erro ao cadastrar polígono:', err);
      alert('Erro ao cadastrar polígono!');
    }
  };

  const atualizarPoligono = async (id: number, poligono: Poligono) => {
    try {
      await axios.put(`/api/monitoramento/poligonos/${id}`, poligono);
      await fetchDados();
      alert('Polígono atualizado com sucesso!');
    } catch (err) {
      console.error('Erro ao atualizar polígono:', err);
      alert('Erro ao atualizar polígono!');
    }
  };

  const deletarPoligono = async (id: number) => {
    try {
      await axios.delete(`/api/monitoramento/poligonos/${id}`);
      await fetchDados();
      alert('Polígono removido com sucesso!');
    } catch (err) {
      console.error('Erro ao remover polígono:', err);
      alert('Erro ao remover polígono!');
    }
  };

  // Função para gerar relatório Excel de veículos fora de rota
  const gerarRelatorioForaRota = () => {
    const veiculosForaRota = veiculosFiltrados.filter(v => v.status === 'fora_de_rota' && !v.problemaRastreador);
    if (veiculosForaRota.length === 0) {
      alert('Não há veículos fora de rota no momento.');
      return;
    }
    const dadosExcel = veiculosForaRota.map(v => ({
      'Placa': v.placa,
      'Motorista': v.motorista || 'Não informado',
      'Responsável': v.responsavel || 'Não informado',
      'Operação': v.operacao || 'Não informado',
      'Localização': v.localizacao?.endereco || 'Não disponível'
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dadosExcel);
    ws['!cols'] = [
      { wch: 12 }, { wch: 25 }, { wch: 25 }, { wch: 20 }, { wch: 40 }
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Veículos Fora de Rota');
    const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    XLSX.writeFile(wb, `Relatorio_Fora_Rota_${dataAtual}.xlsx`);
  };

  const getCoordenadaTv = (veiculo: Veiculo): [number, number] | null => {
    const lat = Number(veiculo.latitude ?? veiculo.localizacao?.lat);
    const lng = Number(veiculo.longitude ?? veiculo.localizacao?.lng);
    return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
  };

  const getOperacaoTv = (veiculo: Veiculo) => {
    if (!veiculo.operacao) return undefined;
    const termo = normalizeText(veiculo.operacao);
    return operacoesReferencia.find(op => normalizeText(op.nome).includes(termo) || termo.includes(normalizeText(op.nome)));
  };

  const getRotaTv = (veiculo: Veiculo) => {
    const operacao = getOperacaoTv(veiculo);
    const rotaId = operacao?.rotas?.[0];
    if (rotaId) {
      const rotaOperacao = rotas.find(rota => rota.id === rotaId);
      if (rotaOperacao) return rotaOperacao;
    }

    if (veiculo.operacao) {
      const termo = normalizeText(veiculo.operacao);
      return rotas.find(rota => termo.includes(normalizeText(rota.nome)) || normalizeText(rota.nome).includes(termo));
    }

    return undefined;
  };

  const getOrigemDestinoTv = (veiculo: Veiculo) => {
    const rota = getRotaTv(veiculo);
    if (rota) {
      return `${rota.origem || '-'} -> ${rota.destino || '-'}`;
    }

    const operacao = getOperacaoTv(veiculo);
    const origem = poligonos.find(p => p.id === operacao?.poligonoOrigem)?.nome;
    const destino = poligonos.find(p => p.id === operacao?.poligonoDestino)?.nome;
    if (origem || destino) {
      return `${origem || '-'} -> ${destino || '-'}`;
    }

    return veiculo.operacao || '-';
  };

  const getDestinoTv = (veiculo: Veiculo) => {
    const rota = getRotaTv(veiculo);
    if (rota?.destino) return rota.destino;

    const operacao = getOperacaoTv(veiculo);
    return poligonos.find(p => p.id === operacao?.poligonoDestino)?.nome || '-';
  };

  const getStatusTv = (veiculo: Veiculo) => {
    if (veiculo.emManutencao) return { label: 'Manutencao', className: 'maintenance' };
    if (veiculo.problemaRastreador) return { label: 'Sem sinal', className: 'late' };
    if (veiculo.status === 'fora_de_rota') return { label: 'Atrasado', className: 'late' };
    if (veiculo.status === 'parado' || isParadoEfetivo(veiculo)) return { label: 'Parado', className: 'stopped' };
    if (veiculo.status === 'em_movimento') return { label: 'Em transito', className: 'moving' };
    return { label: getStatusLabel(veiculo.status), className: 'done' };
  };

  const getKmRestanteTv = (veiculo: Veiculo) => {
    const valor = Number(veiculo.distanciaRestante);
    return Number.isFinite(valor) && valor >= 0 ? valor : null;
  };

  const formatarKmRestanteTv = (valor: number | null) => {
    if (valor === null || !Number.isFinite(valor)) return '-';
    return `${valor.toLocaleString('pt-BR', {
      minimumFractionDigits: valor < 10 ? 1 : 0,
      maximumFractionDigits: 1
    })} km`;
  };

  const formatarMinutosTv = (minutos: number | null) => {
    if (minutos === null || !Number.isFinite(minutos)) return '-';
    const total = Math.max(0, Math.round(minutos));
    const horas = Math.floor(total / 60);
    const mins = total % 60;
    return `${String(horas).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  const formatarTempoParadoAoVivoTv = (minutos: number | null) => {
    if (minutos === null || !Number.isFinite(minutos)) return '-';
    const segundosBase = Math.max(0, Math.floor(minutos * 60));
    const segundosDecorridos = Math.max(0, Math.floor((tvTempoAgoraMs - tvTempoReferenciaMs) / 1000));
    const total = segundosBase + segundosDecorridos;
    const horas = Math.floor(total / 3600);
    const mins = Math.floor((total % 3600) / 60);
    const segs = total % 60;
    return `${String(horas).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(segs).padStart(2, '0')}`;
  };

  const getTempoAteDestinoTv = (veiculo: Veiculo) => {
    const kmRestante = getKmRestanteTv(veiculo);
    const velocidade = Number(veiculo.velocidade);
    if (kmRestante === null || !Number.isFinite(velocidade) || velocidade <= 0) return '-';
    return formatarMinutosTv((kmRestante / velocidade) * 60);
  };

  const getEtaTv = (veiculo: Veiculo) => {
    const kmRestante = getKmRestanteTv(veiculo);
    const velocidade = Number(veiculo.velocidade);
    if (kmRestante === null || !Number.isFinite(velocidade) || velocidade <= 0) return '-';
    const eta = new Date(Date.now() + (kmRestante / velocidade) * 60 * 60 * 1000);
    return eta.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatarDataHoraEstimativaTv = (horas: number | null) => {
    if (horas === null || !Number.isFinite(horas) || horas < 0) return null;
    const eta = new Date(Date.now() + Math.round(horas * 60 * 60 * 1000));
    return eta.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).replace(',', '');
  };

  const getPoligonoAtualTv = (veiculo: Veiculo) => {
    const nome = veiculo.poligono && veiculo.poligono !== '-' ? veiculo.poligono : '';
    if (!nome) return null;

    const cadastro = poligonos.find(poligono => normalizeText(poligono.nome) === normalizeText(nome));
    return {
      nome,
      tipo: cadastro?.tipo || ''
    };
  };

  const isPoligonoClienteOuPatioTv = (poligonoAtual: { nome: string; tipo: string } | null) => {
    if (!poligonoAtual) return false;
    const texto = `${normalizeText(poligonoAtual.nome)} ${normalizeText(poligonoAtual.tipo)}`;
    return texto.includes('cliente') || texto.includes('patio');
  };

  const getLocalAtualTv = (veiculo: Veiculo) => {
    const poligonoAtual = getPoligonoAtualTv(veiculo);
    if (poligonoAtual) return poligonoAtual.nome;
    if (veiculo.localizacao?.endereco && veiculo.localizacao.endereco !== 'Localização não disponível') {
      return veiculo.localizacao.endereco;
    }

    const coordenada = getCoordenadaTv(veiculo);
    return coordenada ? `${coordenada[0].toFixed(6)}, ${coordenada[1].toFixed(6)}` : '-';
  };

  const getCidadeUfTv = (veiculo: Veiculo) => {
    const endereco = String(veiculo.localizacao?.endereco || '').trim();
    if (!endereco || endereco === 'Localização não disponível') {
      return getPoligonoAtualTv(veiculo)?.nome || '-';
    }

    const estados: Record<string, string> = {
      acre: 'AC', alagoas: 'AL', amapa: 'AP', amazonas: 'AM', bahia: 'BA', ceara: 'CE',
      'distrito federal': 'DF', 'espirito santo': 'ES', goias: 'GO', maranhao: 'MA',
      'mato grosso': 'MT', 'mato grosso do sul': 'MS', 'minas gerais': 'MG', para: 'PA',
      paraiba: 'PB', parana: 'PR', pernambuco: 'PE', piaui: 'PI', 'rio de janeiro': 'RJ',
      'rio grande do norte': 'RN', 'rio grande do sul': 'RS', rondonia: 'RO', roraima: 'RR',
      'santa catarina': 'SC', 'sao paulo': 'SP', sergipe: 'SE', tocantins: 'TO'
    };
    const ufs = new Set(Object.values(estados));
    const partes = endereco.split(',').map(parte => parte.trim()).filter(Boolean);
    let indiceEstado = -1;
    let uf = '';

    for (let indice = partes.length - 1; indice >= 0; indice -= 1) {
      const parte = partes[indice];
      const normalizada = normalizeText(parte);
      if (ufs.has(parte.toUpperCase())) {
        indiceEstado = indice;
        uf = parte.toUpperCase();
        break;
      }
      if (estados[normalizada]) {
        indiceEstado = indice;
        uf = estados[normalizada];
        break;
      }
      const estadoEncontrado = Object.entries(estados).find(([nomeEstado]) =>
        normalizada.endsWith(` ${nomeEstado}`) || normalizada.includes(` de ${nomeEstado}`)
      );
      if (estadoEncontrado) {
        indiceEstado = indice;
        uf = estadoEncontrado[1];
        break;
      }
    }

    if (indiceEstado < 0) {
      const indiceRegiao = partes.findIndex(parte => normalizeText(parte).includes('regiao'));
      const cidadeProvavel = indiceRegiao > 0 ? partes[indiceRegiao - 1] : partes[Math.max(0, partes.length - 1)];
      return cidadeProvavel || '-';
    }
    const ignorarComoCidade = (parte: string) => {
      const texto = normalizeText(parte);
      return texto.includes('regiao') || texto === 'rmsp' || texto === 'brasil' || /^\d{5}/.test(texto);
    };
    for (let indice = indiceEstado - 1; indice >= 0; indice -= 1) {
      if (!ignorarComoCidade(partes[indice])) return `${partes[indice]}/${uf}`;
    }
    return uf;
  };

  const formatarDataTv = (valor?: string | null) => {
    if (!valor) return '-';
    const data = new Date(valor);
    if (Number.isNaN(data.getTime())) return '-';
    return data.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).replace(',', '');
  };

  const calcularProgressoTv = (veiculo: Veiculo, rota?: Rota) => {
    const distanciaTotal = Number(rota?.distancia);
    const restante = getKmRestanteTv(veiculo);
    if (!Number.isFinite(distanciaTotal) || distanciaTotal <= 0 || restante === null) {
      if (veiculo.status === 'em_movimento') return 55;
      if (veiculo.status === 'parado') return 35;
      if (veiculo.status === 'fora_de_rota') return 45;
      return 100;
    }
    return Math.max(0, Math.min(100, Math.round(((distanciaTotal - restante) / distanciaTotal) * 100)));
  };

  const tvOperacaoSelecionada = useMemo(() => {
    if (!tvOperacaoFiltro) return null;
    return operacoesDisponiveisAtivas.find(operacao => String(operacao.id) === tvOperacaoFiltro) || null;
  }, [tvOperacaoFiltro, operacoesDisponiveisAtivas]);

  const tvPlacasOperacaoSet = useMemo(() => {
    if (!tvOperacaoSelecionada) return null;
    return new Set(
      (tvOperacaoSelecionada.veiculos || [])
        .map(placa => String(placa || '').trim().toUpperCase())
        .filter(Boolean)
    );
  }, [tvOperacaoSelecionada]);

  const tvRotasOperacao = useMemo(() => {
    if (!tvOperacaoSelecionada) return [];
    const idsRotas = normalizarIdsRota(tvOperacaoSelecionada.rotas || []);
    return idsRotas
      .map(id => rotas.find(rota => Number(rota.id) === Number(id)))
      .filter(Boolean) as Rota[];
  }, [tvOperacaoSelecionada, rotas]);

  useEffect(() => {
    if (!modoTv || tvRotasOperacao.length === 0) return;

    tvRotasOperacao.forEach(rota => {
      if (!rotasDetalhadas[rota.id]) {
        buscarRotaDetalhada(rota);
      }
    });
  }, [modoTv, tvRotasOperacao, rotasDetalhadas]);

  const tvVeiculosDisponiveis = useMemo(() => {
    const termo = normalizeText(tvBusca);
    if (!termo) return [];

    return veiculosBasePermitidos
      .filter(veiculo => getCoordenadaTv(veiculo))
      .filter(veiculo => !tvPlacasSelecionadas.includes(veiculo.placa))
      .filter(veiculo => !tvPlacasOperacaoSet || tvPlacasOperacaoSet.size === 0 || tvPlacasOperacaoSet.has(veiculo.placa.toUpperCase()))
      .filter(veiculo => {
        if (!termo) return true;
        const texto = [
          veiculo.placa,
          veiculo.placaCarreta,
          veiculo.motorista,
          veiculo.operacao,
          veiculo.numeroManifesto,
          veiculo.cva
        ].map(valor => normalizeText(valor)).join(' ');
        return texto.includes(termo);
      })
      .slice(0, 12);
  }, [tvBusca, tvPlacasSelecionadas, tvPlacasOperacaoSet, veiculosBasePermitidos]);

  const agoraTv = new Date();
  const tvDataHoje = `${agoraTv.getFullYear()}-${String(agoraTv.getMonth() + 1).padStart(2, '0')}-${String(agoraTv.getDate()).padStart(2, '0')}`;

  useEffect(() => {
    if (!modoTv) {
      setTvProgramacaoAutomatica([]);
      return;
    }
    let ativo = true;
    const inicioSemana = obterInicioSemanaProgramacao(tvDataHoje);
    const fimSemana = obterFimSemanaProgramacao(inicioSemana);
    const inicioConsultaTv = new Date(`${inicioSemana}T12:00:00`);
    inicioConsultaTv.setDate(inicioConsultaTv.getDate() - 2); // inclui sábado e domingo anteriores
    const amanhaConsultaTv = new Date(`${tvDataHoje}T12:00:00`);
    amanhaConsultaTv.setDate(amanhaConsultaTv.getDate() + 1);
    const dataAmanhaConsultaTv = amanhaConsultaTv.toISOString().slice(0, 10);
    const fimConsultaTv = dataAmanhaConsultaTv > fimSemana ? dataAmanhaConsultaTv : fimSemana;
    const carregar = async () => {
      try {
        const response = await axios.get<{ dados: ProgramacaoAutomaticaTvItem[] }>('/api/monitoramento/tv/programacao-automatica', {
          params: { dataInicial: inicioConsultaTv.toISOString().slice(0, 10), dataFinal: fimConsultaTv, _: Date.now() }
        });
        if (ativo) setTvProgramacaoAutomatica(Array.isArray(response.data?.dados) ? response.data.dados : []);
      } catch (error) {
        if (ativo) console.error('[TV] Erro ao carregar programação automática:', error);
      }
    };
    carregar();
    const intervalo = window.setInterval(carregar, 60 * 1000);
    return () => {
      ativo = false;
      window.clearInterval(intervalo);
    };
  }, [modoTv, tvDataHoje]);

  const tvProgramacaoSemanaAtual = useMemo(() => {
    const inicioSemana = obterInicioSemanaProgramacao(tvDataHoje);
    if (!inicioSemana) return [];

    return tvProgramacaoAutomatica
      .filter(item => obterInicioSemanaProgramacao(String(item.dataColeta || '').slice(0, 10)) === inicioSemana)
      .map(item => {
        const dataColeta = String(item.dataColeta || '');
        const previsaoEntrega = String(item.previsaoEntrega || '');
        const empresaColeta = String(item.empresaColeta || '').trim();
        const primeiroDestino = String(item.empresaDestino || '').split('→')[0].trim();
        const coletaNaFks = empresaColeta.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes('fks logistics');
        return {
          id: `auto-tv-${item.cotacao}-${item.coleta}`,
          dia: dataColeta.slice(0, 10),
          diaSemana: obterDiaSemana(dataColeta.slice(0, 10)),
          fornecedor: abreviarNomeEmpresaProgramacao((coletaNaFks ? primeiroDestino : empresaColeta) || '-'),
          horarioColeta: dataColeta.includes(' ') ? dataColeta.split(' ')[1].slice(0, 5) : '-',
          horarioChegada: previsaoEntrega.includes(' ') ? previsaoEntrega.split(' ')[1].slice(0, 5) : '-',
          tipo: '-',
          cavalo: String(item.placa || '-'),
          carreta: String(item.placaCarreta || '-'),
          motorista: String(item.motorista || '-'),
          observacao: '-',
          destino: String(item.empresaDestino || '-'),
          dadosAutomaticos: item
        };
      })
      .sort((a, b) => a.dia.localeCompare(b.dia) || a.horarioColeta.localeCompare(b.horarioColeta) || a.id.localeCompare(b.id));
  }, [tvProgramacaoAutomatica, tvDataHoje]);

  const tvProgramacaoOperacional = useMemo(() => {
    const linhas = [...tvProgramacaoAutomatica].sort((a, b) =>
      String(a.dataColeta || '').localeCompare(String(b.dataColeta || ''))
      || Number(a.roundtrip || 0) - Number(b.roundtrip || 0)
    );
    const grupos: Array<{ ida: ProgramacaoAutomaticaTvItem; voltas: ProgramacaoAutomaticaTvItem[] }> = [];
    const voltasSemIda: ProgramacaoAutomaticaTvItem[] = [];
    const ehIda = (item: ProgramacaoAutomaticaTvItem) => /_IDA_/i.test(String(item.codViagem || '')) || !Number(item.roundtrip);
    const numeroCodigo = (item: ProgramacaoAutomaticaTvItem) => String(item.codViagem || '').split('_')[0];
    const rotaInvertida = (ida: ProgramacaoAutomaticaTvItem, volta: ProgramacaoAutomaticaTvItem) => {
      const origemIda = String(ida.cnpj || '').replace(/\D/g, '');
      const destinoIda = String(ida.cnpjDestino || '').replace(/\D/g, '');
      const origemVolta = String(volta.cnpj || '').replace(/\D/g, '');
      const destinoVolta = String(volta.cnpjDestino || '').replace(/\D/g, '');
      return Boolean(origemIda && destinoIda && origemIda === destinoVolta && destinoIda === origemVolta);
    };

    for (const item of linhas) {
      if (ehIda(item)) {
        grupos.push({ ida: item, voltas: [] });
        continue;
      }
      const candidatas = [...grupos].reverse();
      const mesmoVinculo = candidatas.find(grupo =>
        String(grupo.ida.vinculoViagem || '') !== ''
        && String(grupo.ida.vinculoViagem) === String(item.vinculoViagem)
        && (rotaInvertida(grupo.ida, item) || numeroCodigo(grupo.ida) === numeroCodigo(item))
      );
      const mesmaRotaECodigo = candidatas.find(grupo => rotaInvertida(grupo.ida, item) && numeroCodigo(grupo.ida) === numeroCodigo(item));
      const mesmaRota = candidatas.find(grupo => rotaInvertida(grupo.ida, item));
      const grupo = mesmoVinculo || mesmaRotaECodigo || mesmaRota;
      if (grupo) grupo.voltas.push(item);
      else voltasSemIda.push(item);
    }

    const hoje = new Date(`${tvDataHoje}T12:00:00`);
    const diaSemana = hoje.getDay();
    const amanha = new Date(hoje);
    amanha.setDate(amanha.getDate() + 1);
    const dataAmanha = amanha.toISOString().slice(0, 10);
    const diasRetorno = new Set<string>();
    const adicionarDiaAnterior = (quantidade: number) => {
      const data = new Date(hoje);
      data.setDate(data.getDate() - quantidade);
      diasRetorno.add(data.toISOString().slice(0, 10));
    };
    if (diaSemana === 1) {
      adicionarDiaAnterior(1); // domingo
      adicionarDiaAnterior(2); // sábado
    } else {
      adicionarDiaAnterior(1);
    }

    const selecionadas: Array<{ item: ProgramacaoAutomaticaTvItem; ida: ProgramacaoAutomaticaTvItem; sentido: 'IDA' | 'VOLTA'; momento: 'ANTERIOR' | 'HOJE' | 'PRÓXIMA' }> = [];
    for (const grupo of grupos) {
      const diaIda = String(grupo.ida.dataColeta || '').slice(0, 10);
      const temIdaHoje = diaIda === tvDataHoje;
      const idaDaJanela = diaIda === tvDataHoje || (diaSemana === 1 && diasRetorno.has(diaIda));
      if (idaDaJanela) {
        selecionadas.push({
          item: grupo.ida,
          ida: grupo.ida,
          sentido: 'IDA',
          momento: diaIda === tvDataHoje ? 'HOJE' : 'ANTERIOR'
        });
      }
      if (diaIda === dataAmanha) {
        selecionadas.push({ item: grupo.ida, ida: grupo.ida, sentido: 'IDA', momento: 'PRÓXIMA' });
      }
      for (const volta of grupo.voltas) {
        const diaVolta = String(volta.dataColeta || '').slice(0, 10);
        // Se a viagem nao possui ida hoje, usa a volta programada para hoje
        // como representante do grupo na janela operacional.
        if (!temIdaHoje && diaVolta === tvDataHoje) {
          selecionadas.push({ item: volta, ida: grupo.ida, sentido: 'VOLTA', momento: 'HOJE' });
        }
      }
    }
    for (const volta of voltasSemIda) {
      if (String(volta.dataColeta || '').slice(0, 10) === tvDataHoje) {
        selecionadas.push({ item: volta, ida: volta, sentido: 'VOLTA', momento: 'HOJE' });
      }
    }

    return selecionadas.map(({ item, ida, sentido, momento }) => {
      const dataColeta = String(item.dataColeta || '');
      const previsaoEntrega = String(item.previsaoEntrega || '');
      const empresaColeta = String(item.empresaColeta || '').trim();
      const primeiroDestino = String(item.empresaDestino || '').split('→')[0].trim();
      const coletaNaFks = normalizeText(empresaColeta).includes('fks logistics');
      return {
        id: `auto-tv-operacional-${sentido}-${item.cotacao}-${item.coleta}`,
        dia: dataColeta.slice(0, 10),
        diaSemana: obterDiaSemana(dataColeta.slice(0, 10)),
        fornecedor: abreviarNomeEmpresaProgramacao((coletaNaFks ? primeiroDestino : empresaColeta) || '-'),
        horarioColeta: dataColeta.includes(' ') ? dataColeta.split(' ')[1].slice(0, 5) : '-',
        horarioChegada: previsaoEntrega.includes(' ') ? previsaoEntrega.split(' ')[1].slice(0, 5) : '-',
        tipo: sentido,
        sentidoViagem: sentido,
        momentoOperacional: momento,
        codViagem: item.codViagem,
        cavalo: String(item.placa || ida.placa || '-'),
        carreta: String(item.placaCarreta || ida.placaCarreta || '-'),
        motorista: String(item.motorista || ida.motorista || '-'),
        observacao: '-',
        destino: String(item.empresaDestino || '-'),
        dadosAutomaticos: item
      };
    }).sort((a, b) => a.dia.localeCompare(b.dia) || (a.sentidoViagem === b.sentidoViagem ? 0 : a.sentidoViagem === 'VOLTA' ? -1 : 1) || a.horarioColeta.localeCompare(b.horarioColeta));
  }, [tvProgramacaoAutomatica, tvDataHoje]);

  // Resolve diretamente os enderecos dos veiculos exibidos nos cards semanais.
  // Esses cards nao fazem parte de localizacaoData, portanto dependiam antes de
  // outra tabela solicitar por acaso o mesmo par de coordenadas.
  useEffect(() => {
    if (!modoTv) return;

    const placasAnteriores = new Set(
      tvProgramacaoSemanaAtual
        .filter(item => item.dia < tvDataHoje)
        .map(item => String(item.cavalo || '').trim().toUpperCase())
        .filter(Boolean)
    );
    const coordenadas = veiculosBasePermitidos
      .filter(veiculo => placasAnteriores.has(String(veiculo.placa || '').trim().toUpperCase()))
      .map(veiculo => getCoordenadaTv(veiculo))
      .filter((coordenada): coordenada is [number, number] => Boolean(coordenada))
      .map(([lat, lng]) => ({ key: `${lat.toFixed(4)},${lng.toFixed(4)}`, lat, lng }))
      .filter((item, indice, lista) => lista.findIndex(atual => atual.key === item.key) === indice);

    // Um ciclo anterior pode ter concluido a consulta depois de uma atualizacao
    // da telemetria. Nesse caso, recupera o endereco ja obtido do cache e o leva
    // para o estado reativo que efetivamente redesenha o card.
    const cacheados = coordenadas.reduce<Record<string, string>>((resultado, item) => {
      const endereco = geocodeCacheRef.current[item.key];
      if (endereco && enderecos[item.key] !== endereco) resultado[item.key] = endereco;
      return resultado;
    }, {});
    if (Object.keys(cacheados).length > 0) {
      setEnderecos(anteriores => ({ ...anteriores, ...cacheados }));
    }

    const pendentes = coordenadas
      .filter(item => !geocodeCacheRef.current[item.key] && !geocodePendingRef.current[item.key]);

    if (pendentes.length === 0) return;

    const consultar = async () => {
      for (const item of pendentes) {
        geocodePendingRef.current[item.key] = true;
        try {
          const resposta = await axios.get('/api/monitoramento/reverse-geocode', {
            params: { lat: item.lat, lng: item.lng }
          });
          const endereco = String(resposta.data?.endereco || '').trim();
          geocodeCacheRef.current[item.key] = endereco;
          if (endereco) {
            setEnderecos(anteriores => ({ ...anteriores, [item.key]: endereco }));
          }
        } catch {
          // Mantem as coordenadas disponiveis e tenta novamente no proximo ciclo.
        } finally {
          geocodePendingRef.current[item.key] = false;
        }
      }
    };

    consultar();
  }, [modoTv, tvProgramacaoSemanaAtual, tvDataHoje, veiculosBasePermitidos, enderecos]);

  const tvHistoricoVeiculosSemana = useMemo(() => {
    const ultimoUsoPorCavalo = new Map<string, any>();
    const programacoesAnteriores = tvProgramacaoSemanaAtual
      .filter(item => item.dia < tvDataHoje)
      .filter(item => item.cavalo && item.cavalo !== '-' && normalizeText(item.cavalo) !== 'a confirmar')
      .sort((a, b) => a.dia.localeCompare(b.dia) || a.horarioColeta.localeCompare(b.horarioColeta));

    for (const item of programacoesAnteriores) {
      const placa = String(item.cavalo || '').trim().toUpperCase();
      const carreta = String(item.carreta || '-').trim().toUpperCase() || '-';
      const veiculo = veiculosBasePermitidos.find(atual => String(atual.placa || '').trim().toUpperCase() === placa);
      const dadosAutomaticos = item.dadosAutomaticos as ProgramacaoAutomaticaTvItem;
      const existente = ultimoUsoPorCavalo.get(placa);
      const mesmoDia = existente?.dia === item.dia;
      const registro = mesmoDia ? existente : {
        dia: item.dia,
        diaSemana: obterDiaSemana(item.dia),
        placa,
        carreta,
        motorista: item.motorista || veiculo?.motorista || '-',
        fornecedor: abreviarNomeEmpresaProgramacao(item.fornecedor),
        ultimaProgramacao: item.horarioColeta,
        temIda: false,
        temVolta: false,
        veiculo
      };
      registro.carreta = carreta;
      registro.temVolta = registro.temVolta || Boolean(Number(dadosAutomaticos?.roundtrip));
      registro.temIda = registro.temIda || !Number(dadosAutomaticos?.roundtrip);
      if (String(item.horarioColeta || '') >= String(registro.ultimaProgramacao || '')) {
        registro.ultimaProgramacao = item.horarioColeta;
        registro.fornecedor = abreviarNomeEmpresaProgramacao(item.fornecedor);
        registro.motorista = item.motorista || registro.motorista;
      }
      ultimoUsoPorCavalo.set(placa, registro);
    }

    return [...ultimoUsoPorCavalo.values()]
      .filter(registro => Boolean(registro.veiculo))
      .map(registro => {
        const veiculo = registro.veiculo as Veiculo | undefined;
        const poligonoAtual = veiculo ? getPoligonoAtualTv(veiculo) : null;
        const nomePoligono = String(poligonoAtual?.nome || '').trim();
        const estaNoPatio = normalizeText(nomePoligono).includes('patio');
        const coordenada = veiculo ? getCoordenadaTv(veiculo) : null;
        const chaveCoordenada = coordenada ? `${coordenada[0].toFixed(4)},${coordenada[1].toFixed(4)}` : '';
        const enderecoResolvido = chaveCoordenada ? enderecos[chaveCoordenada] : '';
        const veiculoComEndereco = veiculo && enderecoResolvido
          ? { ...veiculo, localizacao: { ...veiculo.localizacao!, endereco: enderecoResolvido } }
          : veiculo;
        return {
          ...registro,
          estaNoPatio,
          nomePatio: estaNoPatio ? nomePoligono : null,
          localizacao: veiculoComEndereco ? getCidadeUfTv(veiculoComEndereco) : 'Sem localização atual',
          localizacaoCompleta: veiculoComEndereco ? getLocalAtualTv(veiculoComEndereco) : 'Veículo sem telemetria disponível',
          statusAtual: veiculo ? getStatusTv(veiculo).label : 'Sem telemetria',
          fonteLocalizacao: veiculo?.sistema || '-',
          comunicacaoLocalizacao: veiculo?.ultimaComunicacao || veiculo?.ultimaAtualizacao || '-',
          latitudeLocalizacao: coordenada?.[0] ?? null,
          longitudeLocalizacao: coordenada?.[1] ?? null
        };
      })
      .sort((a, b) => b.dia.localeCompare(a.dia) || a.placa.localeCompare(b.placa));
  }, [tvProgramacaoSemanaAtual, tvDataHoje, veiculosBasePermitidos, enderecos]);

  const tvVeiculosSemanaTotalPaginas = Math.max(
    1,
    Math.ceil(tvHistoricoVeiculosSemana.length / TV_VEICULOS_SEMANA_POR_PAGINA)
  );
  const tvVeiculosSemanaPaginaSegura = Math.min(
    tvVeiculosSemanaPagina,
    tvVeiculosSemanaTotalPaginas - 1
  );
  const tvHistoricoVeiculosSemanaPagina = useMemo(() => {
    const inicio = tvVeiculosSemanaPaginaSegura * TV_VEICULOS_SEMANA_POR_PAGINA;
    return tvHistoricoVeiculosSemana.slice(inicio, inicio + TV_VEICULOS_SEMANA_POR_PAGINA);
  }, [tvHistoricoVeiculosSemana, tvVeiculosSemanaPaginaSegura]);

  useEffect(() => {
    setTvVeiculosSemanaPagina(atual => Math.min(atual, tvVeiculosSemanaTotalPaginas - 1));
    if (!modoTv || tvVeiculosSemanaTotalPaginas <= 1) return;

    const intervalo = window.setInterval(() => {
      setTvVeiculosSemanaPagina(atual => (atual + 1) % tvVeiculosSemanaTotalPaginas);
    }, TV_TABELA_SCROLL_INTERVAL_MS);
    return () => window.clearInterval(intervalo);
  }, [modoTv, tvVeiculosSemanaTotalPaginas]);

  const tvPlacasProgramadasSemana = useMemo(() => Array.from(new Set(
    tvProgramacaoSemanaAtual
      .map(item => String(item.cavalo || '').trim().toUpperCase())
      .filter(placa => placa && placa !== '-' && normalizeText(placa) !== 'a confirmar')
  )), [tvProgramacaoSemanaAtual]);

  const tvVeiculosProgramados = useMemo(() => {
    const placasExibicao = tvPlacasProgramadasSemana.length > 0
      ? new Set(tvPlacasProgramadasSemana)
      : new Set(tvPlacasSelecionadas.map(placa => placa.trim().toUpperCase()));
    return veiculosBasePermitidos
      .filter(veiculo => placasExibicao.has(String(veiculo.placa || '').trim().toUpperCase()))
      .filter(veiculo => getCoordenadaTv(veiculo));
  }, [tvPlacasProgramadasSemana, tvPlacasSelecionadas, veiculosBasePermitidos]);

  const tvPlacasDadosViagem = useMemo(() => Array.from(new Set([
    ...tvPlacasSelecionadas,
    ...tvProgramacaoSemanaAtual.map(item => item.cavalo)
  ].map(placa => String(placa || '').trim().toUpperCase()).filter(placa => placa && placa !== '-' && normalizeText(placa) !== 'a confirmar'))), [tvPlacasSelecionadas, tvProgramacaoSemanaAtual]);

  const tvColetasDadosViagem = useMemo(() => Array.from(new Set(
    tvProgramacaoOperacional.flatMap(item => String(item.dadosAutomaticos?.coleta || '')
      .split(',')
      .map(coleta => coleta.trim())
      .filter(coleta => /^\d+$/.test(coleta)))
  )), [tvProgramacaoOperacional]);

  useEffect(() => {
    if (!modoTv || tvPlacasDadosViagem.length === 0) {
      setTvDadosViagem({});
      setTvDadosLoading(false);
      return;
    }

    const placasParam = tvPlacasDadosViagem.join(',');
    let controller: AbortController | null = null;
    let carregamentoEmAndamento = false;
    const carregarDadosViagemTv = async () => {
      if (carregamentoEmAndamento) return;
      carregamentoEmAndamento = true;
      controller = new AbortController();
      setTvDadosLoading(true);
      try {
        const coletasParam = tvColetasDadosViagem.join(',');
        const response = await axios.get(`/api/monitoramento/tv/dados-viagem?placas=${encodeURIComponent(placasParam)}${coletasParam ? `&coletas=${encodeURIComponent(coletasParam)}` : ''}`, {
          signal: controller.signal
        });
        setTvDadosViagem(response.data || {});
      } catch (error: any) {
        if (error?.code !== 'ERR_CANCELED') {
          console.error('[TV] Erro ao buscar dados de viagem:', error);
        }
      } finally {
        carregamentoEmAndamento = false;
        if (!controller?.signal.aborted) setTvDadosLoading(false);
      }
    };

    carregarDadosViagemTv();
    const intervalo = window.setInterval(carregarDadosViagemTv, TV_DADOS_REFRESH_INTERVAL_MS);
    return () => {
      window.clearInterval(intervalo);
      controller?.abort();
    };
  }, [modoTv, tvPlacasDadosViagem, tvColetasDadosViagem]);

  const tvRows = tvVeiculosProgramados.map(veiculo => {
    const rota = getRotaTv(veiculo);
    const dadosViagem = tvDadosViagem[veiculo.placa] || tvDadosViagem[veiculo.placa.toUpperCase()];
    const status = getStatusTv(veiculo);
    const progresso = calcularProgressoTv(veiculo, rota);
    const transitPrevisto = dadosViagem?.transitTimePrevisto || (rota?.tempoEstimado ? formatarTempoHHMM(rota.tempoEstimado) : '-');
    const velocidadeTela = Number(veiculo.velocidade);
    const distanciaAteDestinoKm = Number(dadosViagem?.distanciaAteDestinoKm);
    const kmRestante = dadosViagem?.distanciaAteDestinoKm !== null &&
      dadosViagem?.distanciaAteDestinoKm !== undefined &&
      Number.isFinite(distanciaAteDestinoKm) &&
      distanciaAteDestinoKm >= 0
        ? distanciaAteDestinoKm
        : getKmRestanteTv(veiculo);
    const transitRealPorVelocidadeTela = Number.isFinite(velocidadeTela) &&
      velocidadeTela > 0 &&
      Number.isFinite(distanciaAteDestinoKm) &&
      distanciaAteDestinoKm >= 0
        ? formatarDataHoraEstimativaTv(distanciaAteDestinoKm / velocidadeTela)
        : null;
    const transitRealCalculado = veiculo.tempoParado && veiculo.status === 'parado'
      ? `Parado ${formatarMinutosTv(veiculo.tempoParado)}`
      : getTempoAteDestinoTv(veiculo);
    const transitReal = dadosViagem ? (dadosViagem.transitTimeReal || transitRealPorVelocidadeTela || '-') : transitRealCalculado;
    const tempoDestino = dadosViagem ? (dadosViagem.tempoAteDestino || '-') : getTempoAteDestinoTv(veiculo);

    return {
      veiculo,
      rota,
      dadosViagem,
      status,
      progresso,
      motorista: dadosViagem?.motorista || veiculo.motorista || '-',
      telefone: dadosViagem?.telefone || veiculo.telefones?.[0] || '-',
      placaCarreta: dadosViagem?.placaCarreta || veiculo.placaCarreta || veiculo.cva || '-',
      origemDestino: dadosViagem?.empresaColeta || dadosViagem?.empresaDestino
        ? `${dadosViagem?.empresaColeta || '-'} -> ${dadosViagem?.empresaDestino || '-'}`
        : getOrigemDestinoTv(veiculo),
      destino: dadosViagem?.empresaDestino || getDestinoTv(veiculo),
      ordemColeta: dadosViagem?.ordemColeta || '-',
      janelaColeta: formatarDataTv(dadosViagem?.dataColeta),
      horarioChegadaColeta: dadosViagem?.horarioChegadaColeta || '-',
      tipoHorarioChegadaColeta: dadosViagem?.tipoHorarioChegadaColeta || null,
      motivoHorarioChegadaColeta: dadosViagem?.motivoHorarioChegadaColeta || '',
      janelaEntrega: dadosViagem?.transitTimePrevisto || formatarDataTv(dadosViagem?.previsaoEntrega),
      horarioChegadaEntrega: dadosViagem?.transitTimeReal || '-',
      tipoHorarioChegadaEntrega: dadosViagem?.tipoHorarioChegadaDestino || null,
      transitPrevisto,
      transitReal,
      tempoDestino,
      kmRestante,
      motivoTransitTimePrevisto: dadosViagem?.motivoTransitTimePrevisto || '',
      motivoTransitTimeReal: (dadosViagem?.transitTimeReal || !transitRealPorVelocidadeTela)
        ? (dadosViagem?.motivoTransitTimeReal || '')
        : '',
      motivoTempoAteDestino: dadosViagem?.motivoTempoAteDestino || '',
      etaPrevisto: rota?.tempoEstimado ? getEtaTv(veiculo) : '-',
      etaReal: getEtaTv(veiculo),
      manifesto: dadosViagem?.numeroManifesto || veiculo.numeroManifesto || veiculo.cva || '-',
      dataManifesto: formatarDataTv(dadosViagem?.dataManifesto || veiculo.dataManifesto),
      contrato: dadosViagem?.numeroContrato || veiculo.numeroContrato || '-',
      dataContrato: formatarDataTv(dadosViagem?.dataContrato || veiculo.dataCVA),
      coordenada: getCoordenadaTv(veiculo)
    };
  });

  const tvProgramacaoRows = tvProgramacaoOperacional.map(item => {
    const placa = String(item.cavalo || '').trim().toUpperCase();
    const dadosAutomaticos = item.dadosAutomaticos;
    const coletasDaViagem = String(dadosAutomaticos.coleta || '').split(',').map(valor => valor.trim()).filter(Boolean);
    const dadosPorColeta = coletasDaViagem.map(coleta => tvDadosViagem[`coleta:${coleta}`]).find(Boolean);
    const dadosPlaca = tvDadosViagem[placa] || tvDadosViagem[placa.toUpperCase()];
    const dadosVinculados = dadosPorColeta || (dadosPlaca?.ordemColeta && coletasDaViagem.includes(String(dadosPlaca.ordemColeta).trim())
      ? dadosPlaca
      : undefined);
    const empresaColetaCompleta = String(dadosAutomaticos.empresaColeta || '-');
    const empresaDestinoCompleta = String(dadosAutomaticos.empresaDestino || '-');
    const destinosReduzidos = empresaDestinoCompleta
      .split(/\s*(?:→|->)\s*/)
      .map(nome => abreviarNomeEmpresaProgramacao(nome))
      .filter(Boolean)
      .join(' → ');
    const origemDestino = `${abreviarNomeEmpresaProgramacao(empresaColetaCompleta)} → ${destinosReduzidos || '-'}`;

    return {
      ...item,
      placa,
      apenasProgramado: false,
      documentacaoDivergente: false,
      placaCarreta: dadosAutomaticos.placaCarreta || item.carreta || '-',
      motorista: dadosAutomaticos.motorista || dadosVinculados?.motorista || '-',
      ordemColeta: dadosAutomaticos.coleta || '-',
      origemDestino,
      origemDestinoCompleto: `${empresaColetaCompleta} → ${empresaDestinoCompleta}`,
      janelaColeta: formatarDataTv(dadosAutomaticos.dataColeta) !== '-'
        ? formatarDataTv(dadosAutomaticos.dataColeta)
        : `${formatarDataProgramacao(item.dia)} ${item.horarioColeta || ''}`.trim(),
      horarioChegadaColeta: dadosVinculados?.horarioChegadaColeta || '-',
      tipoHorarioChegadaColeta: dadosVinculados?.tipoHorarioChegadaColeta || null,
      motivoHorarioChegadaColeta: dadosVinculados?.motivoHorarioChegadaColeta || '',
      janelaEntrega: formatarDataTv(dadosAutomaticos.previsaoEntrega),
      horarioChegadaEntrega: dadosVinculados?.transitTimeReal || '-',
      tipoHorarioChegadaEntrega: dadosVinculados?.tipoHorarioChegadaDestino || null,
      motivoHorarioChegadaEntrega: dadosVinculados?.motivoTransitTimeReal || ''
    };
  });

  const tvPontosMapa = tvRows.map(row => row.coordenada).filter(Boolean) as Array<[number, number]>;
  const tvPontosRotasOperacao = tvRotasOperacao.flatMap(rota => {
    const pontos = rotasDetalhadas[rota.id] || rota.pontos.map(ponto => [ponto.lat, ponto.lng] as [number, number]);
    return pontos.filter(([lat, lng]) => Number.isFinite(Number(lat)) && Number.isFinite(Number(lng)));
  });
  const tvPontosMapaBase = tvPontosMapa.length > 0 ? tvPontosMapa : tvPontosRotasOperacao;
  const tvRowAtiva = tvRows[tvVeiculoAtivoIndex] || tvRows[0];
  const tvPontoFoco = tvRowAtiva?.coordenada || null;

  useEffect(() => {
    const container = tvTabelaWrapRef.current;
    if (!modoTv || !container || tvProgramacaoRows.length === 0) return;

    container.scrollTo({ top: 0, behavior: 'auto' });

    const avancarTabela = () => {
      const limite = container.scrollHeight - container.clientHeight;
      if (limite <= 2) return;

      if (container.scrollTop >= limite - 2) {
        container.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      const cabecalho = container.querySelector('thead') as HTMLElement | null;
      const passo = Math.max(80, container.clientHeight - (cabecalho?.offsetHeight || 0) - 16);
      container.scrollTo({
        top: Math.min(limite, container.scrollTop + passo),
        behavior: 'smooth'
      });
    };

    const intervalo = window.setInterval(avancarTabela, TV_TABELA_SCROLL_INTERVAL_MS);
    return () => window.clearInterval(intervalo);
  }, [modoTv, tvProgramacaoRows.length]);

  useEffect(() => {
    const placaAtiva = tvRowAtiva?.veiculo.placa;
    const container = tvKmResumoRef.current;
    const item = placaAtiva ? tvKmItemRefs.current[placaAtiva] : null;
    if (!container || !item) return;

    const esquerdaItem = item.offsetLeft;
    const direitaItem = esquerdaItem + item.offsetWidth;
    const esquerdaVisivel = container.scrollLeft;
    const direitaVisivel = esquerdaVisivel + container.clientWidth;

    if (esquerdaItem < esquerdaVisivel || direitaItem > direitaVisivel) {
      container.scrollTo({
        left: Math.max(0, esquerdaItem - (container.clientWidth - item.offsetWidth) / 2),
        behavior: 'smooth'
      });
    }
  }, [tvRowAtiva?.veiculo.placa]);

  useEffect(() => {
    if (!modoTv || tvRows.length === 0) {
      setTvVeiculoAtivoIndex(0);
      setTvSegundosTroca(TV_TROCA_SEGUNDOS);
      setTvProximaTrocaMs(Date.now() + TV_TROCA_SEGUNDOS * 1000);
      return;
    }

    if (tvVeiculoAtivoIndex >= tvRows.length) {
      setTvVeiculoAtivoIndex(0);
      setTvSegundosTroca(TV_TROCA_SEGUNDOS);
      setTvProximaTrocaMs(Date.now() + TV_TROCA_SEGUNDOS * 1000);
    }
  }, [modoTv, tvRows.length, tvVeiculoAtivoIndex]);

  useEffect(() => {
    if (!modoTv || tvRows.length <= 1) {
      setTvSegundosTroca(TV_TROCA_SEGUNDOS);
      setTvProximaTrocaMs(Date.now() + TV_TROCA_SEGUNDOS * 1000);
      return;
    }

    if (tvTempoAgoraMs >= tvProximaTrocaMs) {
      setTvVeiculoAtivoIndex(index => (index + 1) % tvRows.length);
      setTvSegundosTroca(TV_TROCA_SEGUNDOS);
      setTvProximaTrocaMs(tvTempoAgoraMs + TV_TROCA_SEGUNDOS * 1000);
      return;
    }

    const segundosRestantes = Math.max(1, Math.ceil((tvProximaTrocaMs - tvTempoAgoraMs) / 1000));
    setTvSegundosTroca(segundosRestantes);
  }, [modoTv, tvRows.length, tvTempoAgoraMs, tvProximaTrocaMs]);

  useEffect(() => {
    if (!modoTv) return;

    const atualizarAgora = () => setTvTempoAgoraMs(Date.now());
    atualizarAgora();
    const interval = window.setInterval(atualizarAgora, 1000);

    return () => window.clearInterval(interval);
  }, [modoTv]);

  useEffect(() => {
    const agora = Date.now();
    setTvTempoReferenciaMs(agora);
    setTvTempoAgoraMs(agora);
  }, [modoTv, veiculos, dadosParadosPoligono]);

  const createTvIcon = (veiculo: Veiculo, ativo = false) => {
    const tvStatus = getStatusTv(veiculo);
    const cabineColor = veiculo.problemaRastreador ? '#9c27b0' : getStatusColor(veiculo.status);
    const status = veiculo.problemaRastreador ? 'problema' : veiculo.status;
    const origemLat = veiculo.latitude ?? veiculo.localizacao?.lat;
    const origemLng = veiculo.longitude ?? veiculo.localizacao?.lng;
    const pontoAlvo = encontrarPontoAlvoPorSentido(veiculo);
    const apontaParaDireita =
      origemLat !== undefined &&
      origemLng !== undefined &&
      pontoAlvo?.lat !== undefined &&
      pontoAlvo?.lng !== undefined
        ? pontoAlvo.lng >= origemLng
        : veiculo.sentidoRota !== 'volta';
    const scaleX = apontaParaDireita ? -1 : 1;

    return L.divIcon({
      className: 'tv-vehicle-marker',
      html: `
        <div class="tv-marker-shell ${tvStatus.className} ${ativo ? 'active' : ''}">
          <div class="tv-marker-icon" style="transform: scaleX(${scaleX});">
            <svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <filter id="tv-shadow-${status}" x="-50%" y="-50%" width="200%" height="200%">
                  <feDropShadow dx="0" dy="1" stdDeviation="1.5" flood-opacity="0.3"/>
                </filter>
              </defs>
              <g filter="url(#tv-shadow-${status})">
                <rect x="14" y="11" width="18" height="12" rx="2" fill="#4A90E2" stroke="#2C5F8D" stroke-width="1.2"/>
                <line x1="23" y1="11" x2="23" y2="23" stroke="#2C5F8D" stroke-width="0.8" opacity="0.5"/>
                <rect x="4" y="13" width="12" height="10" rx="2" fill="${cabineColor}" stroke="#1a1a1a" stroke-width="1.2"/>
                <rect x="6.5" y="15" width="6" height="5" rx="1" fill="rgba(255,255,255,0.7)" stroke="#1a1a1a" stroke-width="0.6"/>
                <rect x="4" y="23" width="12" height="2" rx="0.5" fill="#333"/>
                <circle cx="11" cy="28" r="3" fill="#2a2a2a" stroke="#444" stroke-width="1"/>
                <circle cx="11" cy="28" r="1.8" fill="#555"/>
                <circle cx="27" cy="28" r="3" fill="#2a2a2a" stroke="#444" stroke-width="1"/>
                <circle cx="27" cy="28" r="1.8" fill="#555"/>
              </g>
            </svg>
          </div>
        </div>
      `,
      iconSize: [42, 42],
      iconAnchor: [21, 34],
      popupAnchor: [0, -34]
    });
  };

  const adicionarVeiculoTv = (placa: string) => {
    setTvPlacasSelecionadas(prev => prev.includes(placa) ? prev : [...prev, placa]);
    setTvBusca('');
    setTvVeiculoAtivoIndex(0);
    setTvSegundosTroca(TV_TROCA_SEGUNDOS);
    setTvProximaTrocaMs(Date.now() + TV_TROCA_SEGUNDOS * 1000);
  };

  const removerVeiculoTv = (placa: string) => {
    setTvPlacasSelecionadas(prev => prev.filter(item => item !== placa));
    setTvVeiculoAtivoIndex(0);
    setTvSegundosTroca(TV_TROCA_SEGUNDOS);
    setTvProximaTrocaMs(Date.now() + TV_TROCA_SEGUNDOS * 1000);
  };

  const exportarTv = () => {
    if (!tvRows.length) {
      alert('Nenhum veiculo selecionado para exportar.');
      return;
    }

    const ws = XLSX.utils.json_to_sheet(tvRows.map(row => ({
      Veiculo: row.veiculo.placa,
      Motorista: row.motorista,
      Telefone: row.telefone,
      'Origem -> Destino': row.origemDestino,
      Manifesto: row.manifesto,
      'Data Manifesto': row.dataManifesto,
      Contrato: row.contrato,
      'Data Contrato': row.dataContrato,
      'Transit Time Previsto': row.transitPrevisto,
      'Transit Time Real': row.transitReal,
      'Tempo ate o Destino': row.tempoDestino,
      Status: row.status.label,
      Progresso: `${row.progresso}%`
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'TV Viagens');
    XLSX.writeFile(wb, `TV_Viagens_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.xlsx`);
  };

  const salvarProgramacaoTv = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!programacaoTvForm.fornecedor.trim()) {
      alert('Informe o fornecedor.');
      return;
    }

    setProgramacaoTvSalvando(true);
    try {
      if (programacaoTvEditandoId) {
        const response = await axios.put<ProgramacaoTvItem>(`/api/monitoramento/tv/programacao/${programacaoTvEditandoId}`, programacaoTvForm);
        setProgramacaoTv(programacaoTv.map(item => item.id === programacaoTvEditandoId ? response.data : item));
      } else {
        const response = await axios.post<ProgramacaoTvItem>('/api/monitoramento/tv/programacao', programacaoTvForm);
        setProgramacaoTv([...programacaoTv, response.data]);
      }
      setProgramacaoTvForm(PROGRAMACAO_TV_VAZIA);
      setProgramacaoTvEditandoId(null);
    } catch (err) {
      console.error('[TV PROGRAMACAO] Erro ao salvar:', err);
      alert('Não foi possível salvar a programação no banco de dados.');
    } finally {
      setProgramacaoTvSalvando(false);
    }
  };

  const editarProgramacaoTv = (item: ProgramacaoTvItem) => {
    const { id, ...campos } = item;
    setProgramacaoTvForm(campos);
    setProgramacaoTvEditandoId(id);
  };

  const exportarProgramacaoTv = async () => {
    if (!programacaoTvFiltrada.length) {
      alert('Nenhuma programação cadastrada para exportar.');
      return;
    }

    try {
      const arquivo = new ExcelJS.Workbook();
      arquivo.creator = 'Sistema FKS';
      arquivo.created = new Date();
      const planilha = arquivo.addWorksheet('Programação', {
        views: [{ state: 'frozen', ySplit: 3 }],
        pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 }
      });

      planilha.mergeCells('A1:J1');
      const titulo = planilha.getCell('A1');
      titulo.value = 'PROGRAMAÇÃO - MONITORAMENTO TV';
      titulo.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
      titulo.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } };
      titulo.alignment = { horizontal: 'center', vertical: 'middle' };
      planilha.getRow(1).height = 30;
      planilha.getRow(2).height = 8;

      const converterDataExcel = (dia: string) => {
        if (!dia) return null;
        const [ano, mes, numeroDia] = dia.split('-').map(Number);
        return new Date(ano, mes - 1, numeroDia);
      };
      const textoExcel = (valor: string) => String(valor || '').toLocaleUpperCase('pt-BR');

      planilha.addTable({
        name: 'TabelaProgramacaoMonitoramentoTv',
        ref: 'A3',
        headerRow: true,
        totalsRow: false,
        style: { theme: 'TableStyleMedium4', showRowStripes: true },
        columns: [
          { name: 'Dia', filterButton: true },
          { name: 'Dia semana', filterButton: true },
          { name: 'Fornecedor', filterButton: true },
          { name: 'Horário Coleta', filterButton: true },
          { name: 'Destino', filterButton: true },
          { name: 'Horário Chegada', filterButton: true },
          { name: 'Tipo', filterButton: true },
          { name: 'Cavalo', filterButton: true },
          { name: 'Carreta', filterButton: true },
          { name: 'Motorista', filterButton: true }
        ],
        rows: programacaoTvFiltrada.map(item => [
          converterDataExcel(item.dia), textoExcel(item.diaSemana), textoExcel(item.fornecedor),
          textoExcel(item.horarioColeta), textoExcel(item.destino || '-'), textoExcel(item.horarioChegada), textoExcel(item.tipo),
          textoExcel(item.cavalo), textoExcel(item.carreta), textoExcel(item.motorista)
        ])
      });

      const larguras = [13, 15, 32, 18, 38, 18, 17, 16, 16, 32];
      planilha.columns.forEach((coluna, index) => { coluna.width = larguras[index]; });
      planilha.getColumn(1).numFmt = 'dd/mm/yyyy';
      planilha.getColumn(1).alignment = { horizontal: 'center', vertical: 'middle' };
      planilha.getColumn(2).alignment = { horizontal: 'center', vertical: 'middle' };
      planilha.getColumn(7).alignment = { horizontal: 'center', vertical: 'middle' };
      planilha.eachRow((linha, numeroLinha) => {
        if (numeroLinha >= 3) {
          linha.height = numeroLinha === 3 ? 24 : 22;
          linha.alignment = { ...linha.alignment, vertical: 'middle', wrapText: true };
        }
      });

      const buffer = await arquivo.xlsx.writeBuffer();
      const data = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
      saveAs(new Blob([buffer as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Programacao_Monitoramento_TV_${data}.xlsx`);
    } catch (err) {
      console.error('[TV PROGRAMACAO] Erro ao exportar Excel:', err);
      alert('Não foi possível exportar a programação.');
    }
  };

  const excluirProgramacaoTv = async (id: string) => {
    if (!window.confirm('Deseja excluir esta programação?')) return;
    try {
      await axios.delete(`/api/monitoramento/tv/programacao/${id}`);
      setProgramacaoTv(programacaoTv.filter(item => item.id !== id));
      if (programacaoTvEditandoId === id) {
        setProgramacaoTvForm(PROGRAMACAO_TV_VAZIA);
        setProgramacaoTvEditandoId(null);
      }
    } catch (err) {
      console.error('[TV PROGRAMACAO] Erro ao excluir:', err);
      alert('Não foi possível excluir a programação do banco de dados.');
    }
  };

  const abrirEdicaoAgendaFixa = () => {
    setTvAgendaRascunho(tvAgendaFixa.map(item => ({ ...item })));
    setTvAgendaErro('');
    setTvAgendaEditando(true);
  };

  const atualizarItemAgendaFixa = (index: number, campo: keyof AgendaFixaTvItem, valor: string) => {
    setTvAgendaRascunho(atual => atual.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [campo]: valor } : item
    ));
  };

  const adicionarItemAgendaFixa = () => {
    setTvAgendaRascunho(atual => [...atual, { dia: 'Segunda', fornecedor: '', horario: '' }]);
  };

  const removerItemAgendaFixa = (index: number) => {
    setTvAgendaRascunho(atual => atual.filter((_, itemIndex) => itemIndex !== index));
  };

  const salvarAgendaFixa = async () => {
    const agenda = tvAgendaRascunho
      .map(item => ({
        dia: String(item.dia || '').trim(),
        fornecedor: String(item.fornecedor || '').trim(),
        horario: String(item.horario || '').trim()
      }))
      .filter(item => item.dia || item.fornecedor || item.horario);

    if (agenda.some(item => !item.dia || !item.fornecedor || !item.horario)) {
      setTvAgendaErro('Preencha dia, fornecedor e horário em todas as linhas.');
      return;
    }

    setTvAgendaSalvando(true);
    setTvAgendaErro('');
    try {
      await axios.put('/api/monitoramento/tv/configuracao', {
        placas: tvPlacasSelecionadas,
        operacao: tvOperacaoFiltro,
        agenda
      });
      setTvAgendaFixa(agenda);
      setTvAgendaEditando(false);
    } catch (err) {
      console.error('[TV] Erro ao salvar agenda fixa:', err);
      setTvAgendaErro('Não foi possível salvar a agenda. Tente novamente.');
    } finally {
      setTvAgendaSalvando(false);
    }
  };

  if (modoTv) {
    return (
      <div className={`monitoramento-module tv-monitoring-mode ${tvExpandido ? 'tv-fullscreen' : ''}`}>
        <div className="tv-topbar">
          <div>
            <span className="tv-eyebrow">Monitoramento TV</span>
            <h2>Acompanhamento de viagens</h2>
          </div>
          <div className="tv-topbar-actions">
            {!publicTvMode && (
              <button className="monitoramento-btn secondary" onClick={() => {
                setTvExpandido(false);
                setModoTv(false);
              }}>
                <span className="btn-icon">←</span>
                <span className="btn-label">Voltar</span>
              </button>
            )}
            <button className="monitoramento-btn" onClick={exportarTv}>
              <FaFileAlt />
              <span className="btn-label">Exportar</span>
            </button>
            <button className="monitoramento-btn tv-programacao-button" onClick={() => setProgramacaoTvAberta(true)}>
              <FaCalendarAlt />
              <span className="btn-label">Programação</span>
            </button>
          </div>
        </div>

        {tvAgendaEditando && (
          <div className="tv-agenda-edit-overlay" role="dialog" aria-modal="true" aria-labelledby="tv-agenda-edit-title">
            <div className="tv-agenda-edit-modal">
              <div className="tv-agenda-edit-header">
                <div>
                  <span>Agenda da programação</span>
                  <h2 id="tv-agenda-edit-title">Editar janelas fixas</h2>
                </div>
                <button type="button" onClick={() => setTvAgendaEditando(false)} aria-label="Fechar edição">
                  <FaTimes />
                </button>
              </div>
              <div className="tv-agenda-edit-columns" aria-hidden="true">
                <span>Dia</span>
                <span>Fornecedor</span>
                <span>Horário / observação</span>
                <span></span>
              </div>
              <div className="tv-agenda-edit-list">
                {tvAgendaRascunho.map((item, index) => (
                  <div className="tv-agenda-edit-row" key={`agenda-edit-${index}`}>
                    <select value={item.dia} onChange={event => atualizarItemAgendaFixa(index, 'dia', event.target.value)}>
                      {TV_DIAS_AGENDA.slice(1).concat(TV_DIAS_AGENDA[0]).map(dia => <option value={dia} key={dia}>{dia}</option>)}
                    </select>
                    <input
                      value={item.fornecedor}
                      onChange={event => atualizarItemAgendaFixa(index, 'fornecedor', event.target.value)}
                      placeholder="Nome do fornecedor"
                      maxLength={150}
                    />
                    <input
                      value={item.horario}
                      onChange={event => atualizarItemAgendaFixa(index, 'horario', event.target.value)}
                      placeholder="Ex.: 14h na Estampo"
                      maxLength={250}
                    />
                    <button type="button" className="remove" onClick={() => removerItemAgendaFixa(index)} aria-label={`Remover ${item.fornecedor || 'linha'}`}>
                      <FaTrash />
                    </button>
                  </div>
                ))}
                {tvAgendaRascunho.length === 0 && <div className="tv-agenda-edit-empty">Nenhuma janela cadastrada.</div>}
              </div>
              {tvAgendaErro && <div className="tv-agenda-edit-error">{tvAgendaErro}</div>}
              <div className="tv-agenda-edit-footer">
                <button type="button" className="add" onClick={adicionarItemAgendaFixa}><FaPlus /> Adicionar linha</button>
                <div>
                  <button type="button" className="cancel" onClick={() => setTvAgendaEditando(false)}>Cancelar</button>
                  <button type="button" className="save" onClick={salvarAgendaFixa} disabled={tvAgendaSalvando}>
                    <FaSave /> {tvAgendaSalvando ? 'Salvando...' : 'Salvar agenda'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {programacaoTvAberta && (
          <div className="tv-programacao-overlay" role="dialog" aria-modal="true" aria-labelledby="tv-programacao-titulo">
            <div className="tv-programacao-modal">
              <div className="tv-programacao-header">
                <div>
                  <span className="tv-eyebrow">Agenda de coletas</span>
                  <h2 id="tv-programacao-titulo">{programacaoTvVisaoAutomatica ? 'Visão automática' : 'Programação'}</h2>
                </div>
                <div className="tv-programacao-header-actions">
                  <button type="button" className="tv-programacao-export" onClick={exportarProgramacaoTv} disabled={programacaoTvVisaoAutomatica || !programacaoTvFiltrada.length} title={programacaoTvVisaoAutomatica ? 'Volte ao cadastro para exportar a programação manual' : 'Exportar programação'}>
                    <FaFileAlt /> Exportar
                  </button>
                  <button
                    type="button"
                    className={`tv-programacao-auto-view ${programacaoTvVisaoAutomatica ? 'active' : ''}`}
                    onClick={() => {
                      setProgramacaoTvVisaoAutomatica(valor => !valor);
                      setProgramacaoTvPagina(1);
                    }}
                  >
                    <FaTable /> {programacaoTvVisaoAutomatica ? 'Voltar ao cadastro' : 'Visão automática'}
                  </button>
                  <button type="button" className="tv-programacao-close" onClick={() => {
                    setProgramacaoTvAberta(false);
                    setProgramacaoTvVisaoAutomatica(false);
                  }} aria-label="Fechar programação">
                    <FaTimes />
                  </button>
                </div>
              </div>

              {!programacaoTvVisaoAutomatica && <form className="tv-programacao-form" onSubmit={salvarProgramacaoTv}>
                <label>Dia
                  <input required type="date" value={programacaoTvForm.dia} onChange={event => {
                    const dia = event.target.value;
                    setProgramacaoTvForm({ ...programacaoTvForm, dia, diaSemana: obterDiaSemana(dia) });
                  }} />
                </label>
                {programacaoTvForm.dia && (
                  <label>Dia semana
                    <input readOnly value={programacaoTvForm.diaSemana} aria-readonly="true" />
                  </label>
                )}
                <label>Fornecedor
                  <input required value={programacaoTvForm.fornecedor} onChange={event => setProgramacaoTvForm({ ...programacaoTvForm, fornecedor: event.target.value })} placeholder="Nome do fornecedor" />
                </label>
                <label>Horário Coleta
                  <input value={programacaoTvForm.horarioColeta} onChange={event => setProgramacaoTvForm({ ...programacaoTvForm, horarioColeta: event.target.value })} placeholder="Ex.: 07h na Brose" />
                </label>
                <label>Horário Chegada
                  <input value={programacaoTvForm.horarioChegada} onChange={event => setProgramacaoTvForm({ ...programacaoTvForm, horarioChegada: event.target.value })} placeholder="Ex.: 18h (FKS)" />
                </label>
                <label>Tipo
                  <input value={programacaoTvForm.tipo} onChange={event => setProgramacaoTvForm({ ...programacaoTvForm, tipo: event.target.value })} placeholder="Carreta, Truck..." />
                </label>
                <label>Cavalo
                  <input value={programacaoTvForm.cavalo} onChange={event => setProgramacaoTvForm({ ...programacaoTvForm, cavalo: event.target.value })} placeholder="Placa do cavalo" />
                </label>
                <label>Carreta
                  <input value={programacaoTvForm.carreta} onChange={event => setProgramacaoTvForm({ ...programacaoTvForm, carreta: event.target.value })} placeholder="Placa da carreta" />
                </label>
                <label>Motorista
                  <input value={programacaoTvForm.motorista} onChange={event => setProgramacaoTvForm({ ...programacaoTvForm, motorista: event.target.value })} placeholder="Nome do motorista" />
                </label>
                <div className="tv-programacao-form-actions">
                  {programacaoTvEditandoId && (
                    <button type="button" className="secondary" onClick={() => { setProgramacaoTvForm(PROGRAMACAO_TV_VAZIA); setProgramacaoTvEditandoId(null); }}>Cancelar</button>
                  )}
                  <button type="submit" disabled={programacaoTvSalvando}><FaSave /> {programacaoTvSalvando ? 'Salvando...' : programacaoTvEditandoId ? 'Atualizar' : 'Adicionar'}</button>
                </div>
              </form>}

              {programacaoTvVisaoAutomatica && (
                <div className="tv-programacao-auto-banner">
                  <div>
                    <FaTable />
                    <span>
                      <strong>Programação consolidada</strong>
                      <small>Dados da Nucci salvos no espelho local e organizados pela data de coleta.</small>
                    </span>
                  </div>
                  <em>{programacaoAutomaticaLoading ? 'Atualizando...' : programacaoAutomaticaSincronizadaEm ? `Atualizado em ${formatarDataHoraProgramacao(programacaoAutomaticaSincronizadaEm)}` : 'Atualização automática ativa'}</em>
                </div>
              )}

              {programacaoTvVisaoAutomatica && programacaoAutomaticaAviso && <div className="tv-programacao-auto-message warning">{programacaoAutomaticaAviso}</div>}
              {programacaoTvVisaoAutomatica && programacaoAutomaticaErro && <div className="tv-programacao-auto-message error">{programacaoAutomaticaErro}</div>}

              <div className="tv-programacao-filterbar">
                {programacaoTvVisaoAutomatica ? <form className="tv-programacao-period-filter" onSubmit={event => {
                  event.preventDefault();
                  if (!programacaoAutomaticaPeriodo.dataInicial || !programacaoAutomaticaPeriodo.dataFinal || programacaoAutomaticaPeriodo.dataInicial > programacaoAutomaticaPeriodo.dataFinal) {
                    setProgramacaoAutomaticaErro('Informe um período válido.');
                    return;
                  }
                  setProgramacaoAutomaticaErro('');
                  setProgramacaoAutomaticaPagina(1);
                  setProgramacaoAutomaticaPeriodoAplicado({ ...programacaoAutomaticaPeriodo });
                }}>
                  <FaCalendarAlt />
                  <strong>Período</strong>
                  <label>De<input type="date" required value={programacaoAutomaticaPeriodo.dataInicial} onChange={event => setProgramacaoAutomaticaPeriodo(periodo => ({ ...periodo, dataInicial: event.target.value }))} /></label>
                  <label>Até<input type="date" required value={programacaoAutomaticaPeriodo.dataFinal} onChange={event => setProgramacaoAutomaticaPeriodo(periodo => ({ ...periodo, dataFinal: event.target.value }))} /></label>
                  <button type="submit" disabled={programacaoAutomaticaLoading}><FaSearch /> {programacaoAutomaticaLoading ? 'Buscando...' : 'Buscar'}</button>
                </form> : <>
                <label>
                  <FaCalendarAlt />
                  <span>Filtrar por semana</span>
                  <select value={programacaoTvSemana} onChange={event => {
                    const semana = event.target.value;
                    setProgramacaoTvSemana(semana);
                    if (semana) {
                      const periodo = { dataInicial: semana, dataFinal: obterFimSemanaProgramacao(semana) };
                      setProgramacaoAutomaticaPeriodo(periodo);
                      setProgramacaoAutomaticaPeriodoAplicado(periodo);
                    }
                  }}>
                    <option value="">Todas as semanas</option>
                    {programacaoTvSemanas.map(semana => (
                      <option key={semana.inicio} value={semana.inicio}>
                        Semana {semana.numero} — {formatarDataProgramacao(semana.inicio)} até {formatarDataProgramacao(semana.fim)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <FaCalendarAlt />
                  <span>Dia da semana</span>
                  <select value={programacaoTvDiaSemana} onChange={event => setProgramacaoTvDiaSemana(event.target.value)}>
                    <option value="">Todos os dias</option>
                    {['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'].map(dia => (
                      <option key={dia} value={dia}>{dia}</option>
                    ))}
                  </select>
                </label>
                </>}
                <span className="tv-programacao-result-count">
                  {programacaoTvVisaoAutomatica ? programacaoAutomaticaFiltrada.length : programacaoTvQuantidadeViagens} {programacaoTvVisaoAutomatica
                    ? (programacaoAutomaticaFiltrada.length === 1 ? 'registro encontrado' : 'registros encontrados')
                    : (programacaoTvQuantidadeViagens === 1 ? 'viagem encontrada' : 'viagens encontradas')}
                </span>
              </div>

              <div className={`tv-programacao-table-wrap ${programacaoTvVisaoAutomatica ? 'automatic-view' : ''}`}>
                {programacaoTvVisaoAutomatica ? <table className="tv-programacao-table tv-programacao-auto-table">
                  <thead><tr><th>Cod. viagem</th><th>Cotação</th><th>Coleta</th><th>Data coleta</th><th>Previsão entrega</th><th>Empresa coleta</th><th>Destino</th><th>Contrato</th><th>Status</th><th>Placa</th><th>Carreta</th><th>Manifesto</th><th>Motorista</th><th>Telefone</th></tr></thead>
                  <tbody>
                    {programacaoAutomaticaLoading && programacaoAutomaticaGruposPaginados.length === 0 ? (
                      <tr><td colSpan={14} className="tv-programacao-empty">Carregando dados da Nucci...</td></tr>
                    ) : programacaoAutomaticaGruposPaginados.length === 0 ? (
                      <tr><td colSpan={14} className="tv-programacao-empty">Nenhum registro encontrado para o filtro selecionado.</td></tr>
                    ) : programacaoAutomaticaGruposPaginados.map(grupo => {
                      const item = grupo.ida;
                      const expandida = programacaoAutomaticaViagensExpandidas.has(grupo.chave);
                      const alternarExpansao = () => {
                        if (!grupo.voltas.length) return;
                        setProgramacaoAutomaticaViagensExpandidas(atuais => {
                          const proximas = new Set(atuais);
                          if (proximas.has(grupo.chave)) proximas.delete(grupo.chave);
                          else proximas.add(grupo.chave);
                          return proximas;
                        });
                      };
                      return <React.Fragment key={grupo.chave}>
                        <tr
                          className={`${grupo.voltas.length ? 'tv-programacao-trip-expandable' : ''} ${expandida ? 'expanded' : ''}`}
                          onClick={alternarExpansao}
                          onKeyDown={evento => {
                            if (grupo.voltas.length && (evento.key === 'Enter' || evento.key === ' ')) {
                              evento.preventDefault();
                              alternarExpansao();
                            }
                          }}
                          tabIndex={grupo.voltas.length ? 0 : undefined}
                          aria-expanded={grupo.voltas.length ? expandida : undefined}
                        >
                          <td><span className="tv-programacao-expand-indicator">{grupo.voltas.length ? (expandida ? '▾' : '▸') : ''}</span><strong className={`tv-programacao-trip-code ${/_VOLTA_/i.test(String(item.codViagem || '')) ? 'return' : 'outbound'}`}>{item.codViagem || '-'}</strong></td>
                          <td><strong>{item.cotacao}</strong></td>
                          <td><strong>{item.coleta || '-'}</strong>{item.viagemIdaVolta ? <small className="tv-programacao-merged-badge round-trip">Ida e volta • {item.quantidadeColetas} coletas</small> : Number(item.quantidadeColetas) > 1 && <small className="tv-programacao-merged-badge">{item.quantidadeColetas} coletas na mesma viagem</small>}</td>
                          <td>{formatarDataHoraProgramacao(item.dataColeta)}</td><td>{formatarDataHoraProgramacao(item.previsaoEntrega)}</td>
                          <td><strong>{item.empresaColeta || '-'}</strong><small>{item.cnpj || ''}</small></td><td><strong>{item.empresaDestino || '-'}</strong><small>{item.cnpjDestino || ''}</small></td>
                          <td>{item.contrato || '-'}</td><td><span className={`tv-programacao-contract-status ${item.contrato ? 'linked' : 'pending'}`}>{item.contrato ? `Status ${item.statusContrato ?? '-'}` : 'Sem contrato'}</span></td>
                          <td><strong>{item.placa || '-'}</strong></td><td>{item.placaCarreta || '-'}</td><td>{item.numeroManifesto || '-'}</td><td><strong>{item.motorista || '-'}</strong><small>{item.cpfMotorista || ''}</small></td><td>{item.telefoneMotorista || '-'}</td>
                        </tr>
                        {expandida && grupo.voltas.map(volta => <tr key={`${volta.cotacao}-${volta.coleta}`} className="tv-programacao-trip-return-detail">
                          <td><span className="tv-programacao-return-connector">↳</span><strong className="tv-programacao-trip-code return">{volta.codViagem || '-'}</strong></td>
                          <td><strong>{volta.cotacao}</strong></td>
                          <td><strong>{volta.coleta || '-'}</strong>{Number(volta.quantidadeColetas) > 1 && <small className="tv-programacao-merged-badge">{volta.quantidadeColetas} coletas na mesma viagem</small>}</td>
                          <td>{formatarDataHoraProgramacao(volta.dataColeta)}</td><td>{formatarDataHoraProgramacao(volta.previsaoEntrega)}</td>
                          <td><strong>{volta.empresaColeta || '-'}</strong><small>{volta.cnpj || ''}</small></td><td><strong>{volta.empresaDestino || '-'}</strong><small>{volta.cnpjDestino || ''}</small></td>
                          <td>{volta.contrato || '-'}</td><td><span className={`tv-programacao-contract-status ${volta.contrato ? 'linked' : 'pending'}`}>{volta.contrato ? `Status ${volta.statusContrato ?? '-'}` : 'Sem contrato'}</span></td>
                          <td><strong>{volta.placa || '-'}</strong></td><td>{volta.placaCarreta || '-'}</td><td>{volta.numeroManifesto || '-'}</td><td><strong>{volta.motorista || '-'}</strong><small>{volta.cpfMotorista || ''}</small></td><td>{volta.telefoneMotorista || '-'}</td>
                        </tr>)}
                      </React.Fragment>;
                    })}
                  </tbody>
                </table> : <table className="tv-programacao-table">
                  <thead><tr><th>Cod. viagem</th><th>Dia</th><th>OC</th><th>Cotação</th><th>Dia semana</th><th>Fornecedor</th><th>Origem</th><th>Horário Coleta</th><th>Destino</th><th>Horário Chegada</th><th>Tipo</th><th>Cavalo</th><th>Carreta</th><th>Motorista</th>{!programacaoTvVisaoAutomatica && <th>Ações</th>}</tr></thead>
                  <tbody>
                    {programacaoTvPaginada.length === 0 ? (
                      <tr><td colSpan={15} className="tv-programacao-empty">Nenhuma programação encontrada para o filtro selecionado.</td></tr>
                    ) : programacaoTvGruposPaginados.map(grupo => {
                      const expandida = programacaoTvViagensExpandidas.has(grupo.chave);
                      const alternarExpansao = () => {
                        if (!grupo.voltas.length) return;
                        setProgramacaoTvViagensExpandidas(atuais => {
                          const proximas = new Set(atuais);
                          if (proximas.has(grupo.chave)) proximas.delete(grupo.chave);
                          else proximas.add(grupo.chave);
                          return proximas;
                        });
                      };
                      const ida = grupo.ida;
                      return <React.Fragment key={grupo.chave}>
                        <tr
                          className={`${ida.origemAutomatica ? 'tv-programacao-row-automatic' : ''} ${grupo.voltas.length ? 'tv-programacao-trip-expandable' : ''} ${expandida ? 'expanded' : ''}`}
                          onClick={alternarExpansao}
                          onKeyDown={event => {
                            if (grupo.voltas.length && (event.key === 'Enter' || event.key === ' ')) {
                              event.preventDefault();
                              alternarExpansao();
                            }
                          }}
                          tabIndex={grupo.voltas.length ? 0 : undefined}
                          aria-expanded={grupo.voltas.length ? expandida : undefined}
                        >
                          <td><span className="tv-programacao-expand-indicator">{grupo.voltas.length ? (expandida ? '▾' : '▸') : ''}</span><strong className={`tv-programacao-trip-code ${/_VOLTA_/i.test(String(ida.codViagem || '')) ? 'return' : 'outbound'}`}>{ida.codViagem || '-'}</strong></td><td><strong>{ida.dia ? ida.dia.split('-').reverse().join('/') : '-'}</strong></td><td><strong>{ida.oc || '-'}</strong></td><td>{ida.cotacao || '-'}</td><td><strong>{ida.diaSemana}</strong></td><td>{ida.fornecedor}</td><td>{ida.origem || '-'}</td><td>{ida.horarioColeta || '-'}</td><td>{ida.destino || '-'}</td><td>{ida.horarioChegada || '-'}</td><td>{ida.tipo || '-'}</td><td>{ida.cavalo || '-'}</td><td>{ida.carreta || '-'}</td><td>{ida.motorista || '-'}</td>
                          <td className="tv-programacao-actions">{grupo.voltas.length ? <span className="tv-programacao-auto-source">{expandida ? 'Ocultar detalhes' : 'Ver detalhes'}</span> : <span className={`tv-programacao-auto-source ${/_VOLTA_/i.test(String(ida.codViagem || '')) ? 'return' : ''}`}>{/_VOLTA_/i.test(String(ida.codViagem || '')) ? 'Volta' : 'Automático'}</span>}</td>
                        </tr>
                        {expandida && grupo.voltas.map(volta => <tr key={volta.id} className="tv-programacao-row-automatic tv-programacao-trip-return-detail">
                          <td><span className="tv-programacao-return-connector">↳</span><strong className="tv-programacao-trip-code return">{volta.codViagem || '-'}</strong></td><td><strong>{volta.dia ? volta.dia.split('-').reverse().join('/') : '-'}</strong></td><td><strong>{volta.oc || '-'}</strong></td><td>{volta.cotacao || '-'}</td><td><strong>{volta.diaSemana}</strong></td><td>{volta.fornecedor}</td><td>{volta.origem || '-'}</td><td>{volta.horarioColeta || '-'}</td><td>{volta.destino || '-'}</td><td>{volta.horarioChegada || '-'}</td><td>{volta.tipo || '-'}</td><td>{volta.cavalo || '-'}</td><td>{volta.carreta || '-'}</td><td>{volta.motorista || '-'}</td><td className="tv-programacao-actions"><span className="tv-programacao-auto-source return">Volta</span></td>
                        </tr>)}
                      </React.Fragment>;
                    })}
                  </tbody>
                </table>}
              </div>
              <div className="tv-programacao-pagination">
                <span>
                  Página {programacaoTvVisaoAutomatica ? programacaoAutomaticaPagina : programacaoTvPagina} de {programacaoTvVisaoAutomatica ? programacaoAutomaticaTotalPaginas : programacaoTvTotalPaginas}
                </span>
                {programacaoTvVisaoAutomatica ? <div>
                  <button type="button" onClick={() => setProgramacaoAutomaticaPagina(1)} disabled={programacaoAutomaticaPagina === 1} aria-label="Primeira página">«</button>
                  <button type="button" onClick={() => setProgramacaoAutomaticaPagina(pagina => Math.max(1, pagina - 1))} disabled={programacaoAutomaticaPagina === 1}>Anterior</button>
                  <strong>{programacaoAutomaticaPagina}</strong>
                  <button type="button" onClick={() => setProgramacaoAutomaticaPagina(pagina => Math.min(programacaoAutomaticaTotalPaginas, pagina + 1))} disabled={programacaoAutomaticaPagina === programacaoAutomaticaTotalPaginas}>Próxima</button>
                  <button type="button" onClick={() => setProgramacaoAutomaticaPagina(programacaoAutomaticaTotalPaginas)} disabled={programacaoAutomaticaPagina === programacaoAutomaticaTotalPaginas} aria-label="Última página">»</button>
                </div> : <div>
                  <button type="button" onClick={() => setProgramacaoTvPagina(1)} disabled={programacaoTvPagina === 1} aria-label="Primeira página">«</button>
                  <button type="button" onClick={() => setProgramacaoTvPagina(pagina => Math.max(1, pagina - 1))} disabled={programacaoTvPagina === 1}>Anterior</button>
                  <strong>{programacaoTvPagina}</strong>
                  <button type="button" onClick={() => setProgramacaoTvPagina(pagina => Math.min(programacaoTvTotalPaginas, pagina + 1))} disabled={programacaoTvPagina === programacaoTvTotalPaginas}>Próxima</button>
                  <button type="button" onClick={() => setProgramacaoTvPagina(programacaoTvTotalPaginas)} disabled={programacaoTvPagina === programacaoTvTotalPaginas} aria-label="Última página">»</button>
                </div>}
              </div>
            </div>
          </div>
        )}

        <div className="tv-map-layout">
          <aside className="tv-focus-panel">
          {tvRowAtiva && (() => {
            const poligonoAtual = getPoligonoAtualTv(tvRowAtiva.veiculo);
            const poligonosParados = poligonosTempoPorPlaca?.get(tvRowAtiva.veiculo.placa) || [];
            const tempoParadoPoligono = poligonoAtual
              ? poligonosParados.find((p: any) => normalizeText(p.nomePoligono) === normalizeText(poligonoAtual.nome))?.tempoParado
              : null;
            const tempoParado = Number(tempoParadoPoligono ?? tvRowAtiva.veiculo.tempoParado ?? 0);
            const mostrarTempoParado = isParadoEfetivo(tvRowAtiva.veiculo) &&
              tempoParado > 0;
            const tempoParadoCritico = Boolean(
              mostrarTempoParado &&
              tempoParado > 10 &&
              !isPoligonoClienteOuPatioTv(poligonoAtual)
            );
            const programacaoEmFoco = tvProgramacaoRows.find(item => item.placa === tvRowAtiva.veiculo.placa);
            const documentacaoDivergente = Boolean(programacaoEmFoco?.documentacaoDivergente);

            return (
              <div className={`tv-focus-card ${documentacaoDivergente ? 'tv-focus-card-document-warning' : ''}`}>
                <span className={`tv-status-pill tv-focus-status ${tvRowAtiva.status.className}`}>{tvRowAtiva.status.label}</span>
                <span>Em foco</span>
                <strong>{tvRowAtiva.veiculo.placa}</strong>
                <small>{tvRowAtiva.motorista || tvRowAtiva.origemDestino}</small>
                <small className="tv-focus-phone">{tvRowAtiva.telefone !== '-' ? tvRowAtiva.telefone : 'Telefone nao informado'}</small>
                <small className="tv-focus-speed">
                  Velocidade atual: {Number.isFinite(Number(tvRowAtiva.veiculo.velocidade))
                    ? `${Math.round(Number(tvRowAtiva.veiculo.velocidade))} km/h`
                    : '-'}
                </small>
                <div className="tv-focus-details">
                  <div className="tv-focus-location">
                    <b>Local atual</b>
                    <p title={getLocalAtualTv(tvRowAtiva.veiculo)}>{getCidadeUfTv(tvRowAtiva.veiculo)}</p>
                  </div>
                  <div className="tv-focus-destination">
                    <b>Destino</b>
                    <p title={tvRowAtiva.destino}>{tvRowAtiva.destino || '-'}</p>
                  </div>
                  {poligonoAtual && (
                    <div>
                      <b>Poligono</b>
                      <p>{poligonoAtual.nome}</p>
                    </div>
                  )}
                  {documentacaoDivergente ? (
                    <div className="tv-focus-document-warning">
                      <b>Alerta de documentação</b>
                      <p>Os documentos encontrados são antigos e não correspondem à data programada.</p>
                    </div>
                  ) : (
                    <div className="tv-focus-km">
                      <b>Km faltante até o destino</b>
                      <p>{formatarKmRestanteTv(tvRowAtiva.kmRestante)}</p>
                    </div>
                  )}
                  {mostrarTempoParado && (
                    <div>
                      <b>Tempo parado</b>
                      <p className={tempoParadoCritico ? 'tv-focus-danger' : ''}>
                        {formatarTempoParadoAoVivoTv(tempoParado)}
                      </p>
                    </div>
                  )}
                </div>
                <div className="tv-timer-bar">
                  <i style={{ width: `${(tvSegundosTroca / 15) * 100}%` }}></i>
                </div>
                <em>Proxima troca em {tvSegundosTroca}s</em>
              </div>
            );
          })()}
          </aside>
          <div className="tv-week-operations-layout">
          <div className="tv-week-used-panel">
            <div className="tv-week-used-header">
              <div>
                <span>Veículos utilizados na semana</span>
                <strong>Cavalos e carretas dos dias anteriores</strong>
              </div>
              <b>
                {tvHistoricoVeiculosSemana.length} {tvHistoricoVeiculosSemana.length === 1 ? 'veículo' : 'veículos'}
                {tvVeiculosSemanaTotalPaginas > 1 ? ` • ${tvVeiculosSemanaPaginaSegura + 1}/${tvVeiculosSemanaTotalPaginas}` : ''}
              </b>
            </div>
            <div className="tv-week-used-days">
              {tvHistoricoVeiculosSemana.length === 0 ? (
                <div className="tv-week-used-empty">Nenhum veículo utilizado em dias anteriores desta semana.</div>
              ) : <div className={`tv-week-used-cards ${tvHistoricoVeiculosSemanaPagina.length > 4 ? 'is-dense' : ''}`}>
                {tvHistoricoVeiculosSemanaPagina.map((registro: any) => (
                  <article className={`tv-week-used-card ${registro.estaNoPatio ? 'in-yard' : ''}`} key={registro.placa}>
                    {registro.estaNoPatio ? <>
                      <div className="tv-week-used-yard-plate">
                        <small>Cavalo</small>
                        <strong>{registro.placa}</strong>
                      </div>
                      <div className="tv-week-used-yard-location">
                        <small>Dentro do pátio</small>
                        <strong>{registro.nomePatio}</strong>
                      </div>
                    </> : <>
                    <div className="tv-week-used-card-top">
                      <span>{registro.diaSemana} • {formatarDataProgramacao(registro.dia)}</span>
                      <b className={normalizeText(registro.statusAtual).includes('atras') ? 'late' : ''}>{registro.statusAtual}</b>
                    </div>
                    <div className="tv-week-used-plates">
                      <div><small>Cavalo</small><strong>{registro.placa}</strong></div>
                      <span>+</span>
                      <div><small>Carreta</small><strong>{registro.carreta}</strong></div>
                    </div>
                    <div className="tv-week-used-location" title={registro.localizacaoCompleta}>
                      <small>Localização atual</small>
                      <LocalizacaoSemanalTv
                        lat={registro.latitudeLocalizacao}
                        lng={registro.longitudeLocalizacao}
                        localizacaoInicial={registro.localizacao}
                        fonte={registro.fonteLocalizacao}
                        comunicacao={registro.comunicacaoLocalizacao}
                      />
                    </div>
                    <div className="tv-week-used-meta">
                      <span title={registro.motorista}>Motorista: <b>{registro.motorista || '-'}</b></span>
                      <span>Último uso: <b>{registro.ultimaProgramacao || '-'}</b></span>
                      <span><b>{registro.temVolta ? 'Ida e volta' : 'Somente ida'}</b></span>
                    </div>
                    </>}
                  </article>
                ))}
              </div>}
            </div>
            {false && <>
            <button className="tv-expand-button" type="button" onClick={() => setTvExpandido(prev => !prev)}>
              <FaExpand />
              <span>{tvExpandido ? 'Sair da tela cheia' : 'Tela cheia'}</span>
            </button>
          <MapContainer
            center={(tvPontosMapaBase[0] || [-19.8157, -43.9542]) as [number, number]}
            zoom={tvPontosMapaBase.length > 1 ? 7 : 11}
            className="tv-map"
          >
            <TileLayer
              attribution="Tiles &copy; Esri"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            />
            <MapResizer trigger={tvExpandido} />
            {tvPontoFoco ? (
              <MapFocusPoint ponto={tvPontoFoco} zoom={13} />
            ) : (
              <MapAutoFit pontos={tvPontosMapaBase} />
            )}
            {poligonos.map(poligono => {
              const positions: [number, number][] = (poligono.coordenadas || [])
                .map(ponto => [Number(ponto.lat), Number(ponto.lng)] as [number, number])
                .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));

              if (positions.length < 3) return null;

              return (
                <LeafletPolygon
                  key={`tv-poligono-${poligono.id || poligono.nome}`}
                  positions={positions}
                  pathOptions={{
                    color: poligono.cor,
                    fillColor: poligono.cor,
                    fillOpacity: 0.18,
                    weight: 3
                  }}
                >
                  <Popup>
                    <div className="marker-popup">
                      <h4>{poligono.nome}</h4>
                      <p><strong>Tipo:</strong> {poligono.tipo}</p>
                      {poligono.descricao && <p><strong>Descricao:</strong> {poligono.descricao}</p>}
                    </div>
                  </Popup>
                </LeafletPolygon>
              );
            })}
            {tvRotasOperacao.map(rota => {
              const pontosRota = rotasDetalhadas[rota.id] || rota.pontos.map(ponto => [ponto.lat, ponto.lng] as [number, number]);
              if (pontosRota.length < 2) return null;

              return (
                <Polyline
                  key={`tv-operacao-rota-${rota.id}`}
                  positions={pontosRota}
                  color={rota.cor || '#1d74d8'}
                  weight={7}
                  opacity={0.82}
                />
              );
            })}
            {tvRows.map(row => {
              const ativo = row.veiculo.placa === tvRowAtiva?.veiculo.placa;
              const rotaPontos = row.rota
                ? (rotasDetalhadas[row.rota.id] || row.rota.pontos.map(ponto => [ponto.lat, ponto.lng] as [number, number]))
                : [];

              return (
                <React.Fragment key={row.veiculo.placa}>
                  {!tvOperacaoSelecionada && rotaPontos.length > 1 && (
                    <Polyline
                      positions={rotaPontos}
                      color={row.status.className === 'late' ? '#ff7a00' : row.status.className === 'stopped' ? '#2f80ed' : '#4caf50'}
                      weight={ativo ? 7 : 4}
                      opacity={ativo ? 0.9 : 0.45}
                    />
                  )}
                  {row.coordenada && (
                    <Marker position={row.coordenada} icon={createTvIcon(row.veiculo, ativo)}>
                      <Popup>
                        <strong>{row.veiculo.placa}</strong><br />
                        {row.veiculo.motorista || 'Motorista nao informado'}<br />
                        {row.status.label} - {row.progresso}%
                      </Popup>
                    </Marker>
                  )}
                </React.Fragment>
              );
            })}
          </MapContainer>
          </>}
          </div>
          <aside className="tv-fixed-agenda-panel">
            <div className="tv-fixed-agenda-header">
              <div>
                <span>Agenda da programação</span>
                <strong>Janelas fixas da semana</strong>
              </div>
              {!publicTvMode ? (
                <button type="button" className="tv-fixed-agenda-edit" onClick={abrirEdicaoAgendaFixa} title="Editar agenda fixa">
                  <FaEdit />
                  <span>Editar</span>
                </button>
              ) : <FaCalendarAlt aria-hidden="true" />}
            </div>
            <div className="tv-fixed-agenda-table-wrap">
              <table className="tv-fixed-agenda-table">
                <thead>
                  <tr>
                    <th>Dia</th>
                    <th>Fornecedor</th>
                    <th>Horário</th>
                  </tr>
                </thead>
                <tbody>
                  {tvAgendaFixa.map((item, index) => (
                    <tr
                      className={TV_DIAS_AGENDA[new Date().getDay()] === item.dia ? 'is-today' : ''}
                      key={`${item.dia}-${item.fornecedor}-${index}`}
                    >
                      <td>{item.dia}</td>
                      <td>{item.fornecedor}</td>
                      <td>{item.horario}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </aside>
          </div>
        </div>

        <div className="tv-table-panel">
          <div className="tv-table-header">
            <div className="tv-km-resumo" ref={tvKmResumoRef}>
              {tvDadosLoading ? (
                <span className="tv-km-loading">Atualizando quilômetros...</span>
              ) : tvRows.length === 0 ? (
                <span className="tv-km-loading">Nenhum veículo selecionado</span>
              ) : (
                tvRows.map(row => (
                  <div
                    className={`tv-km-item ${row.veiculo.placa === tvRowAtiva?.veiculo.placa ? 'active' : ''}`}
                    key={`km-${row.veiculo.placa}`}
                    ref={ref => { tvKmItemRefs.current[row.veiculo.placa] = ref; }}
                  >
                    <strong title="Placa">{row.veiculo.placa}</strong>
                    <span className="tv-km-valor" title="KM faltante">{formatarKmRestanteTv(row.kmRestante)}</span>
                    <span className="tv-km-destino" title={row.destino}>{row.destino}</span>
                  </div>
                ))
              )}
            </div>
            <span className="tv-table-count">{`${tvProgramacaoOperacional.length} etapas na janela operacional`}</span>
          </div>
          <div className="tv-table-wrap" ref={tvTabelaWrapRef}>
            <table className="tv-trip-table">
              <thead>
                <tr>
                  <th>Dia Semana</th>
                  <th>Viagem</th>
                  <th>Veiculo</th>
                  <th>Fornecedor</th>
                  <th>Motorista</th>
                  <th>OC</th>
                  <th>Origem {'->'} Destino</th>
                  <th>Janela Coleta</th>
                  <th>Horario Chegada</th>
                  <th>Janela Entrega</th>
                  <th>Horario Chegada</th>
                </tr>
              </thead>
              <tbody>
                {tvProgramacaoRows.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="tv-empty-state">Nenhuma viagem para a janela operacional.</td>
                  </tr>
                ) : (
                  tvProgramacaoRows.map(item => (
                    <tr
                      key={item.id}
                      className={`${item.documentacaoDivergente ? 'tv-schedule-document-warning' : ''} tv-schedule-moment-${normalizeText(item.momentoOperacional)} ${!item.cavalo || item.cavalo === '-' || normalizeText(item.cavalo) === 'a confirmar' ? 'tv-schedule-unconfirmed' : ''}`}
                    >
                      <td><strong>{item.diaSemana || obterDiaSemana(item.dia) || '-'}</strong></td>
                      <td><span className={`tv-trip-direction ${String(item.sentidoViagem).toLowerCase()}`}>{item.sentidoViagem}</span></td>
                      <td>
                        <strong>{item.placa || '-'}</strong>
                        {(!item.cavalo || item.cavalo === '-' || normalizeText(item.cavalo) === 'a confirmar') && <small className={`tv-vehicle-pending ${item.momentoOperacional === 'HOJE' ? 'critical' : ''}`}>{item.momentoOperacional === 'HOJE' ? 'Sem documentação' : 'A confirmar'}</small>}
                        {item.placaCarreta !== '-' && <small className="tv-schedule-trailer">{item.placaCarreta}</small>}
                        {item.documentacaoDivergente && (
                          <span className="tv-schedule-document-badge" title="Os dados exibidos pertencem a uma documentação antiga e não correspondem à data programada.">
                            Documentação divergente
                          </span>
                        )}
                      </td>
                      <td title={String(item.fornecedor || '-')}>{item.fornecedor || '-'}</td>
                      {item.apenasProgramado ? (
                        <td colSpan={7} className="tv-schedule-programmed-only">Programado</td>
                      ) : (
                        <>
                          <td>{item.motorista}</td>
                          <td>{item.ordemColeta}</td>
                          <td title={item.origemDestinoCompleto}>{item.origemDestino}</td>
                          <td>{item.janelaColeta}</td>
                          <td>
                            {item.tipoHorarioChegadaColeta && item.horarioChegadaColeta !== '-' && (
                              <span className={`tv-arrival-icon ${item.tipoHorarioChegadaColeta === 'realizado' ? 'actual' : 'estimated'}`}>
                                {item.tipoHorarioChegadaColeta === 'realizado' ? '✓' : '⏱'}
                              </span>
                            )}
                            <span title={item.motivoHorarioChegadaColeta || undefined}>
                              {item.horarioChegadaColeta === '-' && item.motivoHorarioChegadaColeta ? 'NÃ£o identificado' : item.horarioChegadaColeta}
                            </span>
                          </td>
                          <td>{item.janelaEntrega}</td>
                          <td>
                            {item.tipoHorarioChegadaEntrega && item.horarioChegadaEntrega !== '-' && (
                              <span className={`tv-arrival-icon ${item.tipoHorarioChegadaEntrega === 'realizado' ? 'actual' : 'estimated'}`}>
                                {item.tipoHorarioChegadaEntrega === 'realizado' ? '✓' : '⏱'}
                              </span>
                            )}
                            <span title={item.motivoHorarioChegadaEntrega || undefined}>
                              {item.horarioChegadaEntrega === '-' && item.motivoHorarioChegadaEntrega ? 'NÃ£o identificado' : item.horarioChegadaEntrega}
                            </span>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="monitoramento-module">
      {/* Componente de Alarme para Veículos Parados */}
      <AlarmeVeiculoParado 
        enabled={alarmeAtivo} 
        onAlertaCountChange={setVeiculosEmAlerta}
        forcarExibicao={mostrarModalAlertas}
        onFecharModal={() => setMostrarModalAlertas(false)}
        placasPermitidas={placasPermitidasArray}
        exibirModalAutomatico={isOperacionalAccess}
      />
      
      <div className="monitoramento-header">
        <h2>🛰️ Monitoramento de Frota</h2>
        <div className="header-actions">
          <button className="monitoramento-btn" onClick={() => setShowTracking(true)}>
            <span className="btn-icon">⌖</span>
            <span className="btn-label">Tracking</span>
          </button>
          <button className="monitoramento-btn tv-open-btn" onClick={() => setModoTv(true)}>
            <span className="btn-icon">TV</span>
            <span className="btn-label">TV</span>
          </button>
          {canManageFullMonitoring && (
            <button className="monitoramento-btn" onClick={() => setShowCadastroRota(true)}>
              <span className="btn-icon">+</span>
              <span className="btn-label">Cadastrar Rota</span>
            </button>
          )}
          {canManageFullMonitoring && (
            <button className="monitoramento-btn" onClick={() => {
              setShowCadastroOperacao(true);
              setFiltroRotasModal('');
              setFiltroVeiculosModal('');
            }}>
              <span className="btn-icon">+</span>
              <span className="btn-label">Cadastrar Operação</span>
            </button>
          )}
          {canManageOperations && (
            <button className="monitoramento-btn" onClick={() => setShowListaOperacoes(true)}>
              <span className="btn-icon">🏭</span>
              <span className="btn-label">Gerenciar Operações</span>
            </button>
          )}
          {canViewEditRoutes && (
            <button className="monitoramento-btn" onClick={() => {
              setShowRotas(!showRotas);
              if (!showRotas && rotasSelecionadas.length === 0 && rotasVisiveisGestao.length > 0) {
                setRotasSelecionadas(rotasVisiveisGestao.map(r => r.id));
              }
            }}>
              <span className="btn-icon">{showRotas ? '📍' : '🗺️'}</span>
              <span className="btn-label">{showRotas ? 'Ver Veículos' : 'Ver Rotas'}</span>
            </button>
          )}
          {canManageFullMonitoring && (
            <button className="monitoramento-btn" onClick={() => {
                const editor = document.querySelector('[data-poligono-button]') as HTMLButtonElement;
                if (editor) editor.click();
              }}
            >
              <span className="btn-icon">📍</span>
              <span className="btn-label">Polígonos ({poligonos.length})</span>
            </button>
          )}
          {(canManageFullMonitoring || isOperacionalAccess || isGerencialAccess) && (
            <button 
              className={`monitoramento-btn ${alarmeAtivo ? 'btn-alarme-ativo' : 'btn-alarme-inativo'}`}
              onClick={() => setAlarmeAtivo(!alarmeAtivo)}
              title={alarmeAtivo ? 'Desativar som do alarme (alertas continuarão aparecendo)' : 'Ativar som do alarme'}
            >
              <span className="btn-icon">{alarmeAtivo ? '🔔' : '🔕'}</span>
              <span className="btn-label">{alarmeAtivo ? 'Som Ativo' : 'Som Desativado'}</span>
            </button>
          )}
          {(canManageFullMonitoring || isOperacionalAccess || isGerencialAccess) && (
            <button 
              className="monitoramento-btn"
              onClick={() => setShowModalProximidade(true)}
              title="Buscar veículo mais próximo com sentido volta"
            >
              <span className="btn-icon">📍</span>
              <span className="btn-label">Proximidade</span>
            </button>
          )}
          {(canManageFullMonitoring || isOperacionalAccess || isGerencialAccess) && veiculosEmAlerta > 0 && (
            <button 
              className="monitoramento-btn btn-alertas-ativos"
              onClick={() => setMostrarModalAlertas(true)}
              title="Visualizar veículos em alerta"
              style={{
                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                animation: 'pulse-red 2s ease-in-out infinite',
                position: 'relative'
              }}
            >
              <span className="btn-icon">🚨</span>
              <span className="btn-label">Alertas ({veiculosEmAlerta})</span>
              <span style={{
                position: 'absolute',
                top: -5,
                right: -5,
                background: '#ff1744',
                color: 'white',
                borderRadius: '50%',
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 'bold',
                border: '2px solid white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}>
                {veiculosEmAlerta}
              </span>
            </button>
          )}
          {canManageFullMonitoring && (
            <button className="monitoramento-btn" onClick={() => setShowModalManutencao(true)}>
              <span className="btn-icon">🔧</span>
              <span className="btn-label">Ver Manutenção</span>
            </button>
          )}
          {canManageFullMonitoring && (
            <button className="monitoramento-btn" onClick={() => setShowModalDeparaPlacas(true)}>
              <span className="btn-icon">🔁</span>
              <span className="btn-label">Depara Placas</span>
            </button>
          )}
        </div>
      </div>

      {/* Modal de veículos em manutenção */}
      {showTracking && (
        <TrackingModal
          onClose={() => setShowTracking(false)}
          vehicles={veiculosBasePermitidos.map(veiculo => {
            const rota = getRotaTv(veiculo);
            const operacao = getOperacaoTv(veiculo);
            const poligonoAtual = poligonos.find(item => normalizeText(item.nome) === normalizeText(veiculo.poligono));
            const origemOperacao = poligonos.find(item => item.id === operacao?.poligonoOrigem)?.nome;
            const destinoOperacao = poligonos.find(item => item.id === operacao?.poligonoDestino)?.nome;
            return {
              placa: veiculo.placa,
              placaCarreta: veiculo.placaCarreta || veiculo.cva,
              motorista: veiculo.motorista,
              sistema: veiculo.sistema,
              status: getStatusTv(veiculo).label,
              estaParado: isParadoEfetivo(veiculo),
              poligonoAtual: veiculo.poligono,
              tipoPoligonoAtual: poligonoAtual?.tipo,
              localizacao: veiculo.localizacao?.endereco || getLocalAtualTv(veiculo),
              latitude: veiculo.latitude ?? veiculo.localizacao?.lat,
              longitude: veiculo.longitude ?? veiculo.localizacao?.lng,
              problemaRastreador: veiculo.problemaRastreador,
              distanciaRestante: getKmRestanteTv(veiculo) ?? undefined,
              origem: rota?.origem || origemOperacao,
              destino: rota?.destino || destinoOperacao,
              operacao: veiculo.operacao,
              numeroContrato: veiculo.numeroContrato,
              numeroManifesto: veiculo.numeroManifesto,
              dataManifesto: veiculo.dataManifesto,
            };
          })}
        />
      )}

      {showModalManutencao && (
        <div className="modal-manutencao-backdrop" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-manutencao" style={{ background: 'white', borderRadius: 10, padding: 32, minWidth: 400, maxWidth: 600, boxShadow: '0 4px 24px rgba(0,0,0,0.18)', position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: '#222', margin: 0 }}>Veículos em Manutenção</h2>
              <button onClick={() => setShowModalManutencao(false)} style={{ background: 'none', border: 'none', fontSize: 28, cursor: 'pointer', color: '#888', lineHeight: 1, padding: 0, marginLeft: 16 }} aria-label="Fechar modal">✕</button>
            </div>

            {/* Tabela Carretas Específicas - AGORA LOGO APÓS LOCALIZAÇÃO DE VEÍCULOS */}

                        {/* Tabela Localização de Veículos + Carretas Específicas */}
                        <div style={{ marginTop: 32 }}>
                          <div className="localizacao-tabela-container">
                            ...existing code...
                          </div>                          
                        </div>
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              {veiculosEmManutencaoPermitidos.length === 0 ? (
                <div style={{ color: '#888', fontSize: 16, textAlign: 'center', margin: '32px 0' }}>Nenhum veículo em manutenção no momento.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: '8px 6px', fontWeight: 600, fontSize: 15, borderBottom: '1px solid #eee' }}>Placa</th>
                      <th style={{ padding: '8px 6px', fontWeight: 600, fontSize: 15, borderBottom: '1px solid #eee' }}>Tempo em Manutenção</th>
                      <th style={{ padding: '8px 6px', fontWeight: 600, fontSize: 15, borderBottom: '1px solid #eee' }}>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {veiculosEmManutencaoPermitidos.map(v => {
                      let tempo = '-';
                      if (v.dtEntradaManutencao) {
                        const entrada = new Date(v.dtEntradaManutencao);
                        const agora = new Date();
                        const diffMs = agora.getTime() - entrada.getTime();
                        const diffH = Math.floor(diffMs / (1000 * 60 * 60));
                        const diffM = Math.floor((diffMs / (1000 * 60)) % 60);
                        tempo = `${diffH}h ${diffM}min`;
                      }
                      return (
                        <tr key={v.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '8px 6px', fontWeight: 500, fontSize: 15 }}>{v.placa}</td>
                          <td style={{ padding: '8px 6px', fontSize: 15 }}>{tempo}</td>
                          <td style={{ padding: '8px 6px' }}>
                            <button onClick={() => removerDeManutencao(v.placa)} style={{ background: '#4CAF50', color: 'white', border: 'none', borderRadius: 4, padding: '6px 14px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>Finalizar</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Filtros de operação, status e responsável */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap' }}>
        {/* Só mostra o filtro de operação se houver mais de uma operação disponível */}
        {operacoesDisponiveisAtivas.length > 1 && (
          <div>
            <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>🏭 Operação:</label>
            <select 
              value={filtroOperacao || ''} 
              onChange={e => setFiltroOperacao(e.target.value ? parseInt(e.target.value) : null)}
              style={{ 
                padding: '8px 12px', 
                border: '2px solid #e0e0e0', 
                borderRadius: 6, 
                fontSize: 14,
                minWidth: 200,
                cursor: 'pointer',
                background: filtroOperacao ? '#e3f2fd' : 'white'
              }}
            >
              <option value="">Todas as Operações</option>
              {operacoesDisponiveisAtivas.map(op => (
                <option key={op.id} value={op.id}>{op.nome}</option>
              ))}
            </select>
            {filtroOperacao && (
              <button 
                onClick={() => setFiltroOperacao(null)}
                style={{ 
                  marginLeft: 8, 
                  padding: '6px 10px', 
                  border: 'none', 
                  background: '#f44336',
                  color: 'white',
                  borderRadius: 4,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >Limpar</button>
            )}
          </div>
        )}
        <div>
          <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>Status:</label>
          <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)} style={{ padding: '8px 12px', border: '2px solid #e0e0e0', borderRadius: 6, fontSize: 14 }}>
            <option value="todos">Todos</option>
            <option value="em_movimento">Em Movimento</option>
            <option value="parado">Parados</option>
            <option value="fora_de_rota">Fora de Rota</option>
          </select>
        </div>
        <div>
          <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>Sistema:</label>
          <select value={filtroSistema} onChange={e => setFiltroSistema(e.target.value)} style={{ padding: '8px 12px', border: '2px solid #e0e0e0', borderRadius: 6, fontSize: 14 }}>
            <option value="todos">Omnilink + Traccar</option>
            <option value="omnilink">Omnilink</option>
            <option value="traccar">Traccar</option>
          </select>
        </div>
        <div>
          <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>Sentido:</label>
          <select value={filtroSentido} onChange={e => setFiltroSentido(e.target.value)} style={{ padding: '8px 12px', border: '2px solid #e0e0e0', borderRadius: 6, fontSize: 14 }}>
            <option value="todos">Todos</option>
            <option value="ida">➡️ Ida</option>
            <option value="volta">⬅️ Volta</option>
            <option value="desconhecido">❓ Desconhecido</option>
          </select>
        </div>
        <div>
          <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>🔍 Buscar Placa / CVA / Motorista:</label>
          <input 
            type="text"
            placeholder="Placa, CVA, Motorista"
            value={filtroPlaca}
            onChange={e => setFiltroPlaca(e.target.value)}
            style={{ 
              padding: '8px 12px', 
              border: '2px solid #e0e0e0', 
              borderRadius: 6, 
              fontSize: 14,
              minWidth: 200,
              textTransform: 'uppercase'
            }}
          />
          {filtroPlaca && (
            <button 
              onClick={() => setFiltroPlaca('')}
              style={{ 
                marginLeft: 8, 
                padding: '6px 10px', 
                border: 'none', 
                background: '#f44336', 
                color: 'white', 
                borderRadius: 4, 
                cursor: 'pointer',
                fontSize: 12
              }}
            >
              ✕ Limpar
            </button>
          )}
        </div>

      </div>

      {/* Backdrop escuro quando modal de polígonos estiver aberto */}

      {/* Cards de Estatísticas */}
      <div className={`estatisticas-grid${showListaPoligonos ? ' modal-aberto' : ''}`}> 
        <div className="stat-card total">
          <div className="stat-icon">🚛</div>
          <div className="stat-info">
            <h3>{estatisticasFiltradas.totalVeiculos}</h3>
            <p>Total de Veículos</p>
          </div>
        </div>
        <div 
          className="stat-card em-movimento clickable"
          onMouseEnter={() => setShowTooltipEmMovimento(true)}
          onMouseLeave={() => setShowTooltipEmMovimento(false)}
          onClick={gerarRelatorioEmMovimento}
        >
          <div className="stat-icon">▶️</div>
          <div className="stat-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <div>
              <h3>{estatisticasFiltradas.emMovimento}</h3>
              <p>Em Movimento</p>
            </div>
            <div style={{ marginLeft: 16, textAlign: 'right', fontSize: 18, fontWeight: 500, color: '#222' }}>
              <div>Ida <span style={{ fontWeight: 700 }}>{veiculosFiltrados.filter(v => v.status === 'em_movimento' && v.sentidoRota === 'ida' && !v.problemaRastreador).length}</span></div>
              <div>Volta <span style={{ fontWeight: 700 }}>{veiculosFiltrados.filter(v => v.status === 'em_movimento' && v.sentidoRota === 'volta' && !v.problemaRastreador).length}</span></div>
            </div>
          </div>
          {showTooltipEmMovimento && estatisticasFiltradas.emMovimento > 0 && (
            <div className="tooltip-indicador">
              <div className="tooltip-header">
                <strong>Veículos em Movimento:</strong>
                <small>Clique para baixar relatório</small>
              </div>
              <div className="tooltip-lista">
                {veiculosFiltrados
                  .filter(v => v.status === 'em_movimento' && !v.problemaRastreador)
                  .map(v => (
                    <div key={v.id} className="tooltip-item">
                      <span className="placa">{v.placa}</span>
                      <span className="info">{v.motorista}</span>
                      <span className="velocidade">{v.velocidade} km/h</span>
                    </div>
                  ))}
              </div>
            </div>            
          )}
        </div>
        <div 
          className="stat-card parados clickable"
          onMouseEnter={() => setShowTooltipParados(true)}
          onMouseLeave={() => setShowTooltipParados(false)}
          onClick={gerarRelatorioParados}
        >
          <div className="stat-icon">⏸️</div>
          <div className="stat-info">
            <h3>{estatisticasFiltradas.parados}</h3>
            <p>Parados</p>
          </div>
          {showTooltipParados && estatisticasFiltradas.parados > 0 && (
            <div className="tooltip-indicador">
              <div className="tooltip-header">
                <strong>Veículos Parados:</strong>
                <small>Clique para baixar relatório</small>
              </div>
              <div className="tooltip-lista">
                {veiculosFiltrados
                  .filter(v => isParadoEfetivo(v))
                  .map(v => (
                    <div key={v.id} className="tooltip-item-parado">
                      <div className="parado-header">
                        <span className="placa">{v.placa}</span>
                        <span className="tempo">{v.tempoParado} min</span>
                      </div>
                      <div className="parado-info">
                        <span className="motorista">{v.motorista || 'Sem informação'}</span>
                      </div>
                      <div className="parado-local">
                        <span className="poligono">📍 {v.poligono && v.poligono !== '-' ? v.poligono : (v.localizacao?.endereco || 'Sem informação')}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
        <div 
          className="stat-card fora-rota clickable"
          onMouseEnter={() => setShowTooltipForaRota(true)}
          onMouseLeave={() => setShowTooltipForaRota(false)}
          onClick={gerarRelatorioForaRota}
        >
          <div className="stat-icon">⚠️</div>
          <div className="stat-info">
            <h3>{estatisticasFiltradas.foraDeRota}</h3>
            <p>Fora de Rota</p>
          </div>
          {showTooltipForaRota && estatisticasFiltradas.foraDeRota > 0 && (
            <div className="tooltip-indicador">
              <div className="tooltip-header">
                <strong>Veículos Fora de Rota:</strong>
                <small>Clique para baixar relatório</small>
              </div>
              <div className="tooltip-lista">
                {veiculosFiltrados
                  .filter(v => v.status === 'fora_de_rota' && !v.problemaRastreador)
                  .map(v => (
                    <div key={v.id} className="tooltip-item">
                      <span className="placa">{v.placa}</span>
                      <span className="info">{v.motorista}</span>
                      <span className="operacao">{v.operacao}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
        <div 
          className="stat-card problema-rastreador clickable"
          onMouseEnter={() => setShowTooltipProblemaRastreador(true)}
          onMouseLeave={() => setShowTooltipProblemaRastreador(false)}
          onClick={gerarRelatorioProblemaRastreador}
        >
          <div className="stat-icon">📡</div>
          <div className="stat-info">
            <h3>{estatisticasFiltradas.problemaRastreador}</h3>
            <p>Problema Rastreador</p>
          </div>
          {showTooltipProblemaRastreador && estatisticasFiltradas.problemaRastreador > 0 && (
            <div className="tooltip-indicador">
              <div className="tooltip-header">
                <strong>Veículos com Problema:</strong>
                <small>Clique para baixar relatório</small>
              </div>
              <div className="tooltip-lista">
                {veiculosFiltrados
                  .filter(v => v.problemaRastreador)
                  .map(v => (
                    <div key={v.id} className="tooltip-item">
                      <span className="placa">{v.placa}</span>
                      <span className="responsavel">{v.responsavel || 'Não informado'}</span>
                      <span className="data">{v.ultimaComunicacao}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
        <div 
          className="stat-card manutencao clickable"
          onMouseEnter={() => setShowTooltipEmManutencao(true)}
          onMouseLeave={() => setShowTooltipEmManutencao(false)}
          onClick={gerarRelatorioEmManutencao}
        >
          <div className="stat-icon">🔧</div>
          <div className="stat-info">
            <h3>{estatisticasFiltradas.emManutencao}</h3>
            <p>Manutenção</p>
          </div>
          {showTooltipEmManutencao && estatisticasFiltradas.emManutencao > 0 && (
            <div className="tooltip-indicador">
              <div className="tooltip-header">
                <strong>Veículos em Manutenção:</strong>
                <small>Clique para baixar relatório</small>
              </div>
              <div className="tooltip-lista">
                {veiculosFiltrados
                  .filter(v => v.emManutencao)
                  .map(v => (
                    <div key={v.id} className="tooltip-item">
                      <span className="placa">{v.placa}</span>
                      <span className="motorista">{v.motorista || 'Sem informação'}</span>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          removerDeManutencao(v.placa);
                        }}
                        style={{
                          padding: '4px 8px',
                          fontSize: '11px',
                          backgroundColor: '#4CAF50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Remover
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="monitoramento-content">
        {/* Mapa */}
        <div className={`mapa-container ${mapaExpandido ? 'mapa-expandido' : ''}`} style={{ position: 'relative' }}>
          <div className="mapa-header">
            <h3>{showRotas ? 'Rotas Cadastradas' : 'Mapa de Localização'}</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <button
                className="btn-expandir-mapa"
                onClick={() => setMapaExpandido(!mapaExpandido)}
                title={mapaExpandido ? 'Recolher mapa' : 'Expandir mapa'}
              >
                {mapaExpandido ? '🗙' : '⛶'}
              </button>
              <span className="ultima-atualizacao" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                Última atualização: {estatisticas.ultimaAtualizacao}
                {isUpdating && (
                  <span style={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: '4px',
                    color: '#2196f3',
                    fontSize: '12px',
                    fontWeight: 500
                  }}>
                    <span className="spinner" style={{
                      width: '12px',
                      height: '12px',
                      border: '2px solid #e3f2fd',
                      borderTopColor: '#2196f3',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }}></span>
                    Atualizando...
                  </span>
                )}
              </span>
            </div>
          </div>
          <MapContainer
            ref={mapContainerRef}
            center={veiculoSelecionado && veiculoSelecionado.localizacao && veiculoSelecionado.localizacao.lat && veiculoSelecionado.localizacao.lng
              ? [veiculoSelecionado.localizacao.lat, veiculoSelecionado.localizacao.lng]
              : [-23.550520, -46.633308]}
            zoom={veiculoSelecionado ? 16 : 12}
            className="leaflet-container"
            style={{ height: '500px', position: 'relative' }}
          >
            <MapResizer trigger={mapaExpandido} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {/* Exibir veículos no mapa */}
            {veiculosFiltrados.map(veiculo => {
              // Fallback: se não tem coordenadas, mas tem polígono, usar centro do polígono
              let lat = veiculo.localizacao?.lat;
              let lng = veiculo.localizacao?.lng;
              
              if ((!lat || !lng) && veiculo.poligono) {
                const poligonoObj = poligonos.find(p => 
                  p.nome.toLowerCase().trim() === (veiculo.poligono || '').toLowerCase().trim()
                );
                if (poligonoObj && poligonoObj.coordenadas.length > 0) {
                  lat = poligonoObj.coordenadas.reduce((sum, c) => sum + c.lat, 0) / poligonoObj.coordenadas.length;
                  lng = poligonoObj.coordenadas.reduce((sum, c) => sum + c.lng, 0) / poligonoObj.coordenadas.length;
                }
              }
              if (!lat || !lng) return null;
              // Se selecionado, abrir popup e destacar
              return (
                <Marker
                  key={veiculo.id}
                  position={[lat, lng]}
                  icon={createCustomIcon(veiculo)}
                  interactive={true}
                  bubblingMouseEvents={false}
                  riseOnHover={true}
                  zIndexOffset={veiculoSelecionado?.id === veiculo.id ? 1000 : 0}
                  eventHandlers={{
                    mousedown: () => setVeiculoSelecionado(veiculo),
                    click: () => setVeiculoSelecionado(veiculo)
                  }}
                  ref={ref => { if (ref) markerRefs.current[veiculo.id] = ref; }}
                >
                  {false && (
                  <Popup autoPan={true} autoClose={false} closeOnClick={false}>
                      <div className="marker-popup">
                        <h4>
                          {veiculo.placa}
                          {renderIconeTipoComunicacao(veiculo.tipoComunicacao)}
                        </h4>
                        <p><strong>Motorista:</strong> {veiculo.motorista}</p>
                        <p><strong>Status:</strong> {getStatusLabel(veiculo.status)}</p>
                        <p><strong>Velocidade:</strong> {veiculo.velocidade} km/h</p>
                        {/* Tempo Parado total (antigo) */}
                        {/* Exibe todos os tempos por polígono no card principal */}
                        {(() => {
                          const poligonos = poligonosTempoPorPlaca?.get(veiculo.placa) || [];
                          if (poligonos.length === 0) return null;
                          return (
                            <div style={{margin: '8px 0'}}>
                              <strong>⏱️ Tempo parado por polígono:</strong>
                              {poligonos.map((p: any, idx: number) => (
                                <div key={p.nomePoligono + idx} style={{marginLeft: 8, fontSize: 14}}>
                                  <span style={{fontWeight:600}}>{p.nomePoligono}</span> <span style={{color:'#888'}}>({p.tipoPoligono || '-'})</span>: <span style={{color:'#ff9800',fontWeight:600}}>{p.tempoParado >= 60 ? `${Math.floor(p.tempoParado/60)}h${p.tempoParado%60>0?` ${p.tempoParado%60}min`:''}` : `${p.tempoParado} min`}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                        {(isParadoEfetivo(veiculo) && veiculo.tempoParado !== undefined && veiculo.tempoParado > 0) && (
                          <p style={{ color: '#ff9800', fontWeight: 600 }}>
                            <strong>⏱️ Tempo Parado:</strong> {veiculo.tempoParado} min
                            {veiculo.tempoParado >= 60 && ` (${Math.floor(veiculo.tempoParado / 60)}h${veiculo.tempoParado % 60 > 0 ? ` ${veiculo.tempoParado % 60}min` : ''})`}
                          </p>
                        )}
                        {veiculo.status === 'em_movimento' && veiculo.ultimoPoligono && (
                          <>
                            <p><strong>Último Polígono:</strong> {veiculo.ultimoPoligono}</p>
                            {veiculo.horarioSaidaUltimoPoligono && (
                              <p><strong>Saiu às:</strong> {formatarDataSemTimezone(veiculo.horarioSaidaUltimoPoligono)}</p>
                            )}
                          </>
                        )}
                        <p><strong>Operação:</strong> {veiculo.operacao}</p>
                        <p><strong>Latitude:</strong> {veiculo.latitude ?? '-'}</p>
                        <p><strong>Longitude:</strong> {veiculo.longitude ?? '-'}</p>
                        <p><strong>Localização:</strong> {veiculo.poligono}</p>
                        {veiculo.sentidoRota && veiculo.sentidoRota !== 'desconhecido' && (
                          <p><strong>Sentido:</strong> {veiculo.sentidoRota === 'ida' ? '➡️ Ida' : '⬅️ Volta'} ({veiculo.confiancaSentido}%)</p>
                        )}
                      </div>
                    </Popup>
                  )}
                  </Marker>
                );
            })}
            {/* Exibir rotas selecionadas com trajeto detalhado */}
            {rotasSelecionadas.map(rotaId => {
              const rota = rotas.find(r => r.id === rotaId);
              if (!rota) return null;
              const polyline = rotasDetalhadas[rotaId];
              if (!polyline || polyline.length < 2) return null;
              return (
                <Polyline
                  key={rota.id}
                  positions={polyline}
                  color={rota.cor || 'blue'}
                  weight={5}
                  opacity={0.8}
                >
                  <Popup>
                    <div className="marker-popup">
                      <h4>{rota.nome}</h4>
                      <p><strong>Origem:</strong> {rota.origem}</p>
                      <p><strong>Destino:</strong> {rota.destino}</p>
                      <p><strong>Distância:</strong> {rota.distancia} km</p>
                      <p><strong>Tempo:</strong> {rota.tempoEstimado}h</p>
                    </div>
                  </Popup>
                </Polyline>
              );
            })}
            
            {/* Exibir polígonos cadastrados */}
            {poligonos.map(poligono => {
              const positions: [number, number][] = poligono.coordenadas.map(p => [p.lat, p.lng]);
              return (
                <LeafletPolygon
                  key={poligono.id}
                  positions={positions}
                  pathOptions={{
                    color: poligono.cor,
                    fillColor: poligono.cor,
                    fillOpacity: 0.2,
                    weight: 3
                  }}
                >
                  <Popup>
                    <div className="marker-popup">
                      <h4>{poligono.nome}</h4>
                      <p><strong>Tipo:</strong> {poligono.tipo}</p>
                      {poligono.descricao && <p><strong>Descrição:</strong> {poligono.descricao}</p>}
                      <p><strong>Pontos:</strong> {poligono.coordenadas.length}</p>
                    </div>
                  </Popup>
                </LeafletPolygon>
              );
            })}
            
            {/* Componente de gerenciamento de polígonos */}
            <PoligonoEditor
              poligonos={poligonos}
              onSalvar={salvarPoligono}
              onAtualizar={atualizarPoligono}
              onDeletar={deletarPoligono}
              showListaPoligonos={showListaPoligonos}
              setShowListaPoligonos={setShowListaPoligonos}
            />
          </MapContainer>

          {veiculoSelecionado && (
            <div className="veiculo-detalhes-card" role="dialog" aria-label={`Detalhes do veiculo ${veiculoSelecionado.placa}`}>
              <button type="button" className="veiculo-detalhes-fechar" onClick={() => {
                setVeiculoSelecionado(null);
                setCoordenadasCopiadas(false);
              }} aria-label="Fechar detalhes do veiculo">×</button>
              <div className="veiculo-detalhes-header">
                <div>
                  <span>Veículo selecionado</span>
                  <strong>{veiculoSelecionado.placa}{renderIconeTipoComunicacao(veiculoSelecionado.tipoComunicacao)}</strong>
                </div>
                <span className="veiculo-detalhes-status" style={{ backgroundColor: getStatusColor(veiculoSelecionado.status) }}>
                  {getStatusLabel(veiculoSelecionado.status)}
                </span>
              </div>
              <div className="veiculo-detalhes-grid">
                <div><b>Motorista</b><p>{veiculoSelecionado.motorista || '-'}</p></div>
                <div><b>Velocidade</b><p>{Number(veiculoSelecionado.velocidade || 0)} km/h</p></div>
                <div><b>Operação</b><p>{veiculoSelecionado.operacao || '-'}</p></div>
                <div><b>Sistema</b><p>{veiculoSelecionado.sistema || 'Omnilink'}</p></div>
                <div className="veiculo-detalhes-localizacao"><b>Localização</b><p>{veiculoSelecionado.poligono || veiculoSelecionado.localizacao?.endereco || '-'}</p></div>
                <div><b>Latitude</b><p>{veiculoSelecionado.latitude ?? veiculoSelecionado.localizacao?.lat ?? '-'}</p></div>
                <div><b>Longitude</b><p>{veiculoSelecionado.longitude ?? veiculoSelecionado.localizacao?.lng ?? '-'}</p></div>
                <button
                  type="button"
                  className={`veiculo-detalhes-copiar ${coordenadasCopiadas ? 'copiado' : ''}`}
                  onClick={async () => {
                    const latitude = Number(veiculoSelecionado.latitude ?? veiculoSelecionado.localizacao?.lat);
                    const longitude = Number(veiculoSelecionado.longitude ?? veiculoSelecionado.localizacao?.lng);
                    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

                    const coordenadas = `${latitude}, ${longitude}`;
                    try {
                      if (navigator.clipboard && window.isSecureContext) {
                        await navigator.clipboard.writeText(coordenadas);
                      } else {
                        const campoTemporario = document.createElement('textarea');
                        campoTemporario.value = coordenadas;
                        campoTemporario.style.position = 'fixed';
                        campoTemporario.style.opacity = '0';
                        document.body.appendChild(campoTemporario);
                        campoTemporario.select();
                        document.execCommand('copy');
                        campoTemporario.remove();
                      }
                      setCoordenadasCopiadas(true);
                      window.setTimeout(() => setCoordenadasCopiadas(false), 2000);
                    } catch (error) {
                      console.error('Erro ao copiar latitude e longitude:', error);
                    }
                  }}
                  disabled={!Number.isFinite(Number(veiculoSelecionado.latitude ?? veiculoSelecionado.localizacao?.lat)) || !Number.isFinite(Number(veiculoSelecionado.longitude ?? veiculoSelecionado.localizacao?.lng))}
                  title="Copiar latitude e longitude para colar no Google Maps"
                >
                  {coordenadasCopiadas ? '✓ Coordenadas copiadas!' : '📍 Copiar para o Google Maps'}
                </button>
              </div>
            </div>
          )}

          {/* Barra cinza de parados em polígono - dentro do mapa */}
          {false && mostrarOverlayParados && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '300px',
                height: '100%',
                background: '#f5f5f5',
                borderLeft: '1px solid #ddd',
                zIndex: 1200,
                boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Botão para fechar */}
              <button
                onClick={() => setMostrarOverlayParados(false)}
                style={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  fontSize: 18,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1201
                }}
                title="Fechar"
              >
                ×
              </button>

              {/* Tabela de fornecedores */}
              <div style={{ padding: '50px 12px 12px 12px', overflowY: 'auto', flex: 1 }}>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #999' }}>
                      <th style={{ textAlign: 'left', padding: '8px 4px', fontWeight: 600 }}>Dia</th>
                      <th style={{ textAlign: 'left', padding: '8px 4px', fontWeight: 600 }}>Fornecedor</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '6px 4px' }}>Segunda</td>
                      <td style={{ padding: '6px 4px' }}>Estampo Tec</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '6px 4px' }}>Segunda</td>
                      <td style={{ padding: '6px 4px' }}>Brose Pinhais</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '6px 4px' }}>Terça</td>
                      <td style={{ padding: '6px 4px' }}>Autoplas</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '6px 4px' }}>Terça</td>
                      <td style={{ padding: '6px 4px' }}>Brôse Jarinu</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '6px 4px' }}>Quarta</td>
                      <td style={{ padding: '6px 4px' }}>Estampo Tec</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '6px 4px' }}>Quarta</td>
                      <td style={{ padding: '6px 4px' }}>Pro Metal Ind</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '6px 4px' }}>Quarta</td>
                      <td style={{ padding: '6px 4px' }}>Brose Pinhais</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '6px 4px' }}>Quinta</td>
                      <td style={{ padding: '6px 4px' }}>Brose Pinhais</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '6px 4px' }}>Quinta</td>
                      <td style={{ padding: '6px 4px' }}>Autoplas</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '6px 4px' }}>Sexta</td>
                      <td style={{ padding: '6px 4px' }}>Brose Goiana</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '6px 4px' }}>Sábado</td>
                      <td style={{ padding: '6px 4px' }}>Autoplas</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Botão para mostrar barra - dentro do mapa */}
          {false && !mostrarOverlayParados && (
            <button
              onClick={() => setMostrarOverlayParados(true)}
              style={{
                position: 'absolute',
                bottom: 24,
                right: 12,
                zIndex: 1201,
                padding: '8px 16px',
                fontSize: 13,
                borderRadius: 6,
                border: 'none',
                background: 'linear-gradient(90deg,#2196f3 60%,#1976d2 100%)',
                color: '#fff',
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                transition: 'all 0.2s',
                outline: 'none',
              }}
              title="Mostrar painel"
              onMouseOver={e => {
                e.currentTarget.style.background = 'linear-gradient(90deg,#1976d2 60%,#1565c0 100%)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.25)';
              }}
              onMouseOut={e => {
                e.currentTarget.style.background = 'linear-gradient(90deg,#2196f3 60%,#1976d2 100%)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
              }}
            >
              ▶ Parados
            </button>
          )}
        </div>
            
        {/* Lista de Veículos ou Rotas */}
        {!showRotas ? (
          <>
            <div className="veiculos-container">
              <div className="veiculos-header">
                <h3>Frota ({visualizacaoFrota === 'cavalos' ? veiculosFiltrados.length : carretasFiltradas.length})</h3>
                <div className="veiculos-view-toggle">
                  <button
                    type="button"
                    className={visualizacaoFrota === 'cavalos' ? 'active' : ''}
                    onClick={() => setVisualizacaoFrota('cavalos')}
                  >
                    Cavalos
                  </button>
                  <button
                    type="button"
                    className={visualizacaoFrota === 'carretas' ? 'active' : ''}
                    onClick={() => setVisualizacaoFrota('carretas')}
                  >
                    Carretas
                  </button>
                </div>
              </div>
              <div className="veiculos-lista">
                {visualizacaoFrota === 'cavalos' ? (
                  veiculosFiltrados.map(veiculo => (
                    <div
                      key={veiculo.id}
                      className={`veiculo-card ${veiculoSelecionado?.id === veiculo.id ? 'selected' : ''}`}
                      onClick={() => setVeiculoSelecionado(veiculo)}
                    >
                      <div className="veiculo-header">
                        <span className="veiculo-placa">
                          {veiculo.placa}
                          {renderIconeTipoComunicacao(veiculo.tipoComunicacao)}
                        </span>
                        <span
                          className="veiculo-status"
                          style={{ backgroundColor: getStatusColor(veiculo.status) }}
                        >
                          {getStatusLabel(veiculo.status)}
                        </span>
                      </div>
                      <div className="veiculo-info">
                        <p><strong>Motorista:</strong> {veiculo.motorista}</p>
                        <p><strong>Sistema:</strong> {veiculo.sistema || 'Omnilink'}</p>
                        <p><strong>CVA:</strong> {veiculo.cva}</p>
                        <p><strong>Telefones:</strong> {veiculo.telefones && veiculo.telefones.length > 0 ? veiculo.telefones.join(', ') : '-'}</p>
                        <p><strong>Latitude:</strong> {veiculo.latitude ?? '-'}</p>
                        <p><strong>Longitude:</strong> {veiculo.longitude ?? '-'}</p>
                        <p><strong>Velocidade:</strong> {veiculo.velocidade} km/h</p>
                        {veiculo.statusCarregamento && (
                          <p>
                            <strong>Carregamento:</strong> {veiculo.statusCarregamento}
                          </p>
                        )}
                        {(() => {
                          const poligonos = poligonosTempoPorPlaca?.get(veiculo.placa) || [];
                          if (poligonos.length > 0) {
                            return (
                              <div style={{ margin: '8px 0' }}>
                                <strong>⏱️ Tempo parado por polígono:</strong>
                                {poligonos.map((p: any, idx: number) => (
                                  <div key={p.nomePoligono + idx} style={{ marginLeft: 8, fontSize: 14 }}>
                                    <span style={{ fontWeight: 600 }}>{p.nomePoligono}</span> <span style={{ color: '#888' }}>({p.tipoPoligono || '-'})</span>: <span style={{ color: '#ff9800', fontWeight: 600 }}>{p.tempoParado >= 60 ? `${Math.floor(p.tempoParado / 60)}h${p.tempoParado % 60 > 0 ? ` ${p.tempoParado % 60}min` : ''}` : `${p.tempoParado} min`}</span>
                                  </div>
                                ))}
                              </div>
                            );
                          } else if ((veiculo.status === 'parado' || (veiculo.status === 'fora_de_rota' && Number(veiculo.velocidade || 0) === 0)) && veiculo.tempoParado !== undefined && veiculo.tempoParado > 0) {
                            return (
                              <p style={{ color: '#ff9800', fontWeight: 600 }}>
                                <strong>⏱️ Tempo Parado:</strong> {veiculo.tempoParado} min
                                {veiculo.tempoParado >= 60 && ` (${Math.floor(veiculo.tempoParado / 60)}h${veiculo.tempoParado % 60 > 0 ? ` ${veiculo.tempoParado % 60}min` : ''})`}
                              </p>
                            );
                          }
                          return null;
                        })()}
                        {veiculo.status === 'em_movimento' && veiculo.ultimoPoligono && (
                          <>
                            <p><strong>Último Polígono:</strong> {veiculo.ultimoPoligono}</p>
                            {veiculo.horarioSaidaUltimoPoligono && (
                              <p><strong>Saiu às:</strong> {formatarDataSemTimezone(veiculo.horarioSaidaUltimoPoligono)}</p>
                            )}
                          </>
                        )}
                        <p><strong>Operação:</strong> {veiculo.operacao}</p>
                        <p><strong>Localização:</strong> {veiculo.poligono}</p>
                        {veiculo.sentidoRota && veiculo.sentidoRota !== 'desconhecido' && (
                          <p>
                            <strong>Sentido:</strong>
                            <span style={{
                              marginLeft: 6,
                              padding: '2px 8px',
                              borderRadius: 4,
                              fontSize: '0.85em',
                              fontWeight: 600,
                              backgroundColor: veiculo.sentidoRota === 'ida' ? '#4caf50' : '#2196f3',
                              color: 'white'
                            }}>
                              {veiculo.sentidoRota === 'ida' ? '➡️ Ida' : '⬅️ Volta'} ({veiculo.confiancaSentido}%)
                            </span>
                          </p>
                        )}
                        <p><strong>Última atualização:</strong> {veiculo.ultimaAtualizacao}</p>
                        {veiculo.ultimaComunicacaoOmnilink && (
                          <p><strong>Última comunicação Omnilink:</strong> {veiculo.ultimaComunicacaoOmnilink}</p>
                        )}
                        {veiculo.ultimaComunicacaoTraccar && (
                          <p><strong>Última comunicação Traccar:</strong> {veiculo.ultimaComunicacaoTraccar}</p>
                        )}
                      </div>
                      <div className="veiculo-localizacao">
                        📍 {veiculo.localizacao?.endereco || 'Localização não disponível'}
                      </div>
                    </div>
                  ))
                ) : carretasFiltradas.length > 0 ? (
                  carretasFiltradas.map(carreta => (
                    <div
                      key={carreta.placa}
                      className={`veiculo-card ${veiculoSelecionado?.placaCarreta === carreta.placa ? 'selected' : ''}`}
                      onClick={() => setVeiculoSelecionado(carreta.veiculoVinculado || null)}
                    >
                      <div className="veiculo-header">
                        <span className="veiculo-placa">
                          {carreta.placa}
                          {renderIconeTipoComunicacao(carreta.tipoComunicacao)}
                        </span>
                        <span
                          className="veiculo-status"
                          style={{ backgroundColor: getStatusColor(carreta.status || 'desconhecido') }}
                        >
                          {carreta.status ? getStatusLabel(carreta.status) : 'Sem Monitoramento'}
                        </span>
                      </div>
                      <div className="veiculo-info">
                        <p><strong>Tipo:</strong> {carreta.tipoCarreta || '-'}</p>
                        <p><strong>Marca/Modelo:</strong> {[carreta.marca, carreta.modelo].filter(Boolean).join(' / ') || '-'}</p>
                        <p><strong>Responsável:</strong> {carreta.responsavel || '-'}</p>
                        <p><strong>Cavalo:</strong> {carreta.cavalo || '-'}</p>
                        <p><strong>Sistema:</strong> {carreta.sistema || 'Nucci'}</p>
                        <p><strong>Motorista:</strong> {carreta.motorista || '-'}</p>
                        <p><strong>CVA:</strong> {carreta.cva || '-'}</p>
                        <p><strong>Telefones:</strong> {carreta.telefones && carreta.telefones.length > 0 ? carreta.telefones.join(', ') : '-'}</p>
                        <p><strong>Velocidade:</strong> {carreta.velocidade ?? 0} km/h</p>
                        {carreta.statusCarregamento && (
                          <p>
                            <strong>Carregamento:</strong> {carreta.statusCarregamento}
                          </p>
                        )}
                        {(() => {
                          const poligonos = carreta.cavalo ? (poligonosTempoPorPlaca?.get(carreta.cavalo) || []) : [];
                          if (poligonos.length > 0) {
                            return (
                              <div style={{ margin: '8px 0' }}>
                                <strong>⏱️ Tempo parado por polígono:</strong>
                                {poligonos.map((p: any, idx: number) => (
                                  <div key={p.nomePoligono + idx} style={{ marginLeft: 8, fontSize: 14 }}>
                                    <span style={{ fontWeight: 600 }}>{p.nomePoligono}</span> <span style={{ color: '#888' }}>({p.tipoPoligono || '-'})</span>: <span style={{ color: '#ff9800', fontWeight: 600 }}>{p.tempoParado >= 60 ? `${Math.floor(p.tempoParado / 60)}h${p.tempoParado % 60 > 0 ? ` ${p.tempoParado % 60}min` : ''}` : `${p.tempoParado} min`}</span>
                                  </div>
                                ))}
                              </div>
                            );
                          } else if ((carreta.status === 'parado' || (carreta.status === 'fora_de_rota' && Number(carreta.velocidade || 0) === 0)) && carreta.tempoParado !== undefined && carreta.tempoParado > 0) {
                            return (
                              <p style={{ color: '#ff9800', fontWeight: 600 }}>
                                <strong>⏱️ Tempo Parado:</strong> {carreta.tempoParado} min
                                {carreta.tempoParado >= 60 && ` (${Math.floor(carreta.tempoParado / 60)}h${carreta.tempoParado % 60 > 0 ? ` ${carreta.tempoParado % 60}min` : ''})`}
                              </p>
                            );
                          }
                          return null;
                        })()}
                        {carreta.status === 'em_movimento' && carreta.ultimoPoligono && (
                          <>
                            <p><strong>Último Polígono:</strong> {carreta.ultimoPoligono}</p>
                            {carreta.horarioSaidaUltimoPoligono && (
                              <p><strong>Saiu às:</strong> {formatarDataSemTimezone(carreta.horarioSaidaUltimoPoligono)}</p>
                            )}
                          </>
                        )}
                        <p><strong>Operação:</strong> {carreta.operacao || '-'}</p>
                        <p><strong>Localização:</strong> {carreta.poligono || '-'}</p>
                        {carreta.sentidoRota && carreta.sentidoRota !== 'desconhecido' && (
                          <p>
                            <strong>Sentido:</strong>
                            <span style={{
                              marginLeft: 6,
                              padding: '2px 8px',
                              borderRadius: 4,
                              fontSize: '0.85em',
                              fontWeight: 600,
                              backgroundColor: carreta.sentidoRota === 'ida' ? '#4caf50' : '#2196f3',
                              color: 'white'
                            }}>
                              {carreta.sentidoRota === 'ida' ? '➡️ Ida' : '⬅️ Volta'} ({carreta.confiancaSentido}%)
                            </span>
                          </p>
                        )}
                        <p><strong>Última atualização:</strong> {carreta.ultimaAtualizacao || '-'}</p>
                        {carreta.ultimaComunicacaoOmnilink && (
                          <p><strong>Última comunicação Omnilink:</strong> {carreta.ultimaComunicacaoOmnilink}</p>
                        )}
                        {carreta.ultimaComunicacaoTraccar && (
                          <p><strong>Última comunicação Traccar:</strong> {carreta.ultimaComunicacaoTraccar}</p>
                        )}
                      </div>
                      <div className="veiculo-localizacao">
                        📍 {carreta.localizacao?.endereco || carreta.poligono || 'Localização não disponível'}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="veiculos-empty-state">
                    Nenhuma carreta encontrada com os filtros atuais.
                  </div>
                )}
              </div>
            </div>

            {/* Tabela de Análise */}
            <div className="analise-tabela-container" style={{ marginTop: 32 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700, color: '#333', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: '1.25rem' }}>📊</span>
                  <span>Tabela de Análise</span>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: '#999', background: '#f5f5f5', padding: '4px 10px', borderRadius: 12 }}>{veiculosFiltrados.length} registros</span>
                </h3>
                {isUpdating && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#2196f3' }}>
                    <span className="spinner" style={{ width: 14, height: 14, border: '2px solid #e3f2fd', borderTopColor: '#2196f3', borderRadius: '50%' }}></span>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Atualizando...</span>
                  </div>
                )}
              </div>
              <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                <div style={{ overflowY: 'auto', maxHeight: '500px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                      <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                        <th style={{ padding: '10px 8px', color: '#495057', fontWeight: 700, fontSize: '0.75rem', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>CVA</th>
                        <th style={{ padding: '10px 8px', color: '#495057', fontWeight: 700, fontSize: '0.75rem', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Data</th>
                        <th style={{ padding: '10px 8px', color: '#495057', fontWeight: 700, fontSize: '0.75rem', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Cavalo</th>
                        <th style={{ padding: '10px 8px', color: '#495057', fontWeight: 700, fontSize: '0.75rem', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Carreta</th>
                        <th style={{ padding: '10px 8px', color: '#495057', fontWeight: 700, fontSize: '0.75rem', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Motorista</th>
                        <th style={{ padding: '10px 8px', color: '#495057', fontWeight: 700, fontSize: '0.75rem', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Telefone</th>
                        {/* <th style={{ padding: '10px 8px', color: '#495057', fontWeight: 700, fontSize: '0.75rem', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Manutenção</th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {veiculosFiltrados.map((veiculo, idx) => (
                        <tr key={veiculo.id || idx} style={{ borderBottom: '1px solid #f0f0f0', transition: 'background 0.15s' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'white'}>
                          <td style={{ padding: '10px 8px', fontWeight: 600, color: '#1976d2' }}>{(veiculo.cva && veiculo.cva !== '-') ? veiculo.cva : (veiculo.numeroManifesto ? `Manifesto ${veiculo.numeroManifesto}` : '-')}</td>
                          <td style={{ padding: '10px 8px', color: '#6c757d', fontSize: '0.75rem' }}>{(veiculo.dataCVA && veiculo.dataCVA !== '-') ? formatarDataSemTimezone(veiculo.dataCVA).split(',')[0] : (veiculo.dataManifesto ? formatarDataSemTimezone(veiculo.dataManifesto).split(',')[0] : '-')}</td>
                          <td style={{ padding: '10px 8px', fontWeight: 600 }}>{veiculo.placa || '-'}</td>
                          <td style={{ padding: '10px 8px' }}>{veiculo.placaCarreta || '-'}</td> 
                          <td style={{ padding: '10px 8px' }}>{veiculo.motorista || '-'}</td>
                          <td style={{ padding: '10px 8px', color: '#6c757d', fontSize: '0.75rem' }}>{veiculo.telefones && veiculo.telefones.length > 0 ? veiculo.telefones[0] : '-'}</td>
                          {/* <td style={{ padding: '10px 8px', textAlign: 'center', color: veiculo.emManutencao ? '#d32f2f' : '#388e3c', fontWeight: 700 }}>
                            {veiculo.emManutencao ? 'Sim' : 'Não'}
                          </td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Tabela Performance */}
            <div className="performance-tabela-container" style={{ marginTop: 32 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700, color: '#333', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: '1.25rem' }}>🏆</span>
                  <span>Performance Motorista</span>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: '#999', background: '#f5f5f5', padding: '4px 10px', borderRadius: 12 }}>{performanceData?.length || 0} motoristas</span>
                </h3>
                {(isUpdating || loadingPerformance) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#2196f3' }}>
                    <span className="spinner" style={{ width: 14, height: 14, border: '2px solid #e3f2fd', borderTopColor: '#2196f3', borderRadius: '50%' }}></span>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Atualizando...</span>
                  </div>
                )}
              </div>
              <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                <div style={{ overflowY: 'auto', maxHeight: '500px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                      <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>                    
                        <th style={{ padding: '10px 12px', color: '#495057', fontWeight: 700, fontSize: '0.75rem', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Motorista</th>
                        <th style={{ padding: '10px 12px', color: '#495057', fontWeight: 700, fontSize: '0.75rem', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Viagens</th>
                        <th style={{ padding: '10px 12px', color: '#495057', fontWeight: 700, fontSize: '0.75rem', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>✓ Dentro 80km</th>
                        <th style={{ padding: '10px 12px', color: '#495057', fontWeight: 700, fontSize: '0.75rem', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>⚠ Fora 80km</th>                    
                      </tr>
                    </thead>
                    <tbody>
                      {performanceData && performanceData.length > 0 ? (
                        performanceData.map((row, idx) => {
                          const pctDentro = Number(row.pct_dentro_80 || 0);
                          const pctFora = Number(row.pct_fora_80 || 0);
                          return (
                            <tr key={(row.nm_motorista||'') + idx} style={{ borderBottom: '1px solid #f0f0f0', transition: 'background 0.15s' }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}>
                              <td style={{ padding: '10px 12px', fontWeight: 600 }}>
                                <span
                                  style={{ cursor: 'pointer', textDecoration: 'underline dotted', color: '#1976d2' }}
                                  title={row.periodos && row.periodos.length > 0 ?
                                    row.periodos.map((p: any, i: number) =>
                                      `Viagem ${i+1} (CVA: ${p.cva || '-'} | Placa: ${p.placa || '-'})`
                                    ).join('\n') : 'Sem detalhes'}
                                >
                                  {row.nm_motorista || '-'}
                                </span>
                              </td>
                              <td style={{ padding: '10px 12px', fontSize: '0.875rem', fontWeight: 600, textAlign: 'center', color: '#1976d2' }}>{row.total_events || 0}</td>
                              <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                <span style={{ 
                                  padding: '4px 10px', 
                                  borderRadius: 10, 
                                  fontSize: '0.75rem', 
                                  fontWeight: 600,
                                  background: pctDentro >= 80 ? '#d4edda' : pctDentro >= 60 ? '#fff3cd' : '#f8d7da',
                                  color: pctDentro >= 80 ? '#155724' : pctDentro >= 60 ? '#856404' : '#721c24',
                                  display: 'inline-block'
                                }}>
                                  {pctDentro.toFixed(1)}%
                                </span>
                              </td>
                              <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                <span style={{ 
                                  padding: '4px 10px', 
                                  borderRadius: 10, 
                                  fontSize: '0.75rem', 
                                  fontWeight: 600,
                                  background: pctFora < 20 ? '#d4edda' : pctFora < 40 ? '#fff3cd' : '#f8d7da',
                                  color: pctFora < 20 ? '#155724' : pctFora < 40 ? '#856404' : '#721c24',
                                  display: 'inline-block'
                                }}>
                                  {pctFora.toFixed(1)}%
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={4} style={{ padding: '40px 20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>📊</div>
                            <div style={{ color: '#999', fontSize: '0.9375rem', fontWeight: 500 }}>Nenhum dado de performance disponível</div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Tabela Localização de Veículos */}
            <div className="localizacao-tabela-container" style={{ marginTop: 32 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700, color: '#333', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: '1.25rem' }}>🚛</span>
                  <span>Localização de Veículos</span>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: '#999', background: '#f5f5f5', padding: '4px 10px', borderRadius: 12 }}>{localizacaoData?.length || 0} veículos</span>
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {isUpdating && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#2196f3' }}>
                      <span className="spinner" style={{ width: 14, height: 14, border: '2px solid #e3f2fd', borderTopColor: '#2196f3', borderRadius: '50%' }}></span>
                      <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Atualizando...</span>
                    </div>
                  )}
                  <button
                  onClick={() => {
                    if (!localizacaoData || localizacaoData.length === 0) {
                      alert('Nenhum dado para exportar.');
                      return;
                    }
                    const dadosExcel = localizacaoData.map(row => ({
                      'Cavalo': row.cavalo,
                      'Motorista': row.motorista,
                      'Localização Atual': row.localizacao_atual,
                      'Sentido': row.sentido === 'ida' ? 'Ida' : row.sentido === 'volta' ? 'Volta' : 'Desconhecido',
                      'Status': row.status,
                      'Manutenção': row.manutencao || 'N/A',
                      'KM': row.km,
                      'Tempo': (row.status === 'Parado' && row.tempo_parado)
                        ? (row.tempo_parado >= 60
                            ? `${Math.floor(row.tempo_parado / 60)}h ${row.tempo_parado % 60}min`
                            : `${row.tempo_parado} min`)
                        : (row.tempo || '-'),
                      'Carregamento': row.carregamento
                    }));
                    const wb = XLSX.utils.book_new();
                    const ws = XLSX.utils.json_to_sheet(dadosExcel);
                    ws['!cols'] = [
                      { wch: 12 }, // Cavalo
                      { wch: 25 }, // Motorista
                      { wch: 30 }, // Localização Atual
                      { wch: 12 }, // Sentido
                      { wch: 15 }, // Status
                      { wch: 15 }, // Manutenção
                      { wch: 8 },  // KM
                      { wch: 12 }, // Tempo
                      { wch: 20 }  // Carregamento
                    ];
                    XLSX.utils.book_append_sheet(wb, ws, 'LocalizacaoVeiculos');
                    const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
                    XLSX.writeFile(wb, `Localizacao_Veiculos_${dataAtual}.xlsx`);
                  }}
                  style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 16px', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer' }}
                  title="Exportar tabela para Excel"
                >
                  Exportar Excel
                </button>
                </div>
              </div>
              <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                <div style={{ overflowY: 'auto', maxHeight: '500px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                      <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>                    
                        <th style={{ padding: '10px 12px', color: '#495057', fontWeight: 700, fontSize: '0.75rem', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Cavalo</th>
                        <th style={{ padding: '10px 12px', color: '#495057', fontWeight: 700, fontSize: '0.75rem', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Motorista</th>
                        <th style={{ padding: '10px 12px', color: '#495057', fontWeight: 700, fontSize: '0.75rem', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Localização Atual</th>
                        <th style={{ padding: '10px 12px', color: '#495057', fontWeight: 700, fontSize: '0.75rem', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sentido</th>
                        <th style={{ padding: '10px 12px', color: '#495057', fontWeight: 700, fontSize: '0.75rem', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</th>
                        <th style={{ padding: '10px 12px', color: '#495057', fontWeight: 700, fontSize: '0.75rem', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>KM</th>
                        <th style={{ padding: '10px 12px', color: '#495057', fontWeight: 700, fontSize: '0.75rem', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tempo</th>
                        <th style={{ padding: '10px 12px', color: '#495057', fontWeight: 700, fontSize: '0.75rem', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Carregamento</th>
                        <th style={{ padding: '10px 12px', color: '#495057', fontWeight: 700, fontSize: '0.75rem', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Manutenção</th>
                      </tr>
                    </thead>
                    <tbody>
                      {localizacaoData && localizacaoData.length > 0 ? (
                        localizacaoData.map((row, idx) => {
                          let sentidoLabel = 'Desconhecido';
                          if (row.sentido === 'ida') sentidoLabel = 'Ida';
                          else if (row.sentido === 'volta') sentidoLabel = 'Volta';
                          // Buscar velocidade do veículo original se não existir em row
                          let velocidade = 0;
                          if ('velocidade' in row) {
                            velocidade = (row as any).velocidade;
                          } else if (row.cavalo) {
                            const veic = veiculos.find(v => v.placa === row.cavalo);
                            if (veic) velocidade = veic.velocidade;
                          }

                          const rowBackground =
                            row.sentido === 'volta'
                              ? '#fff8e1'
                              : row.sentido === 'ida'
                                ? '#e8f5e9'
                                : 'white';
                          const rowHoverBackground =
                            row.sentido === 'volta'
                              ? '#ffefc2'
                              : row.sentido === 'ida'
                                ? '#dff0e2'
                                : '#f8f9fa';

                          return (
                            <tr
                              key={idx}
                              style={{ borderBottom: '1px solid #f0f0f0', transition: 'background 0.15s', background: rowBackground }}
                              onMouseEnter={(e) => e.currentTarget.style.background = rowHoverBackground}
                              onMouseLeave={(e) => e.currentTarget.style.background = rowBackground}
                            >
                              <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1976d2' }}>{row.cavalo || '-'}</td>
                              <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{row.motorista || '-'}</td>
                              <td style={{ padding: '10px 12px', color: '#6c757d', fontSize: '0.75rem' }}>{(() => {
                                const loc = row.localizacao_atual;
                                if (loc && /^-?\d+\.\d+,\s*-?\d+\.\d+$/.test(loc)) {
                                  const [latStr, lngStr] = loc.split(',');
                                  const cacheKey = `${parseFloat(latStr.trim()).toFixed(4)},${parseFloat(lngStr.trim()).toFixed(4)}`;
                                  return enderecos[cacheKey] || loc;
                                }
                                return loc || '-';
                              })()}</td>
                              <td style={{ padding: '10px 12px', textAlign: 'center' }}>{sentidoLabel}</td>
                              <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                <span style={{ 
                                  padding: '4px 8px', 
                                  borderRadius: '4px', 
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  background: row.status === 'Em Movimento' ? '#e3f2fd' : '#fff3e0',
                                  color: row.status === 'Em Movimento' ? '#1976d2' : '#f57c00'
                                }}>
                                  {row.status || '-'}
                                </span>
                              </td>
                              <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 500 }}>{row.km || 0}</td>
                              <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: (row.status === 'Parado' || (row.status === 'Fora de Rota' && velocidade === 0)) ? '#f57c00' : '#666', whiteSpace: 'nowrap' }}>
                                {(
                                  (row.status === 'Parado') ||
                                  (row.status === 'Fora de Rota' && velocidade === 0)
                                ) && row.tempo_parado
                                  ? (row.tempo_parado >= 60
                                      ? `${Math.floor(row.tempo_parado / 60)}h ${row.tempo_parado % 60}min`
                                      : `${row.tempo_parado} min`)
                                  : (row.tempo || '-')
                                }
                              </td>
                              <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 500, whiteSpace: 'nowrap' }}>{row.carregamento || '-'}</td>
                              <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: row.manutencao === 'Sim' ? '#d32f2f' : '#388e3c' }}>
                                {row.manutencao === 'Sim' ? 'Sim' : 'Não'}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={8} style={{ padding: '40px 20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🚛</div>
                            <div style={{ color: '#999', fontSize: '0.9375rem', fontWeight: 500 }}>Nenhum dado de localização disponível</div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="veiculos-container">
            <div className="veiculos-header">
              <h3>Rotas ({rotasVisiveisGestao.length})</h3>
              <button 
                className="btn-small"
                onClick={() => {
                  if (rotasSelecionadas.length === rotasVisiveisGestao.length) {
                    setRotasSelecionadas([]);
                  } else {
                    setRotasSelecionadas(rotasVisiveisGestao.map(r => r.id));
                  }
                }}
              >
                {rotasSelecionadas.length === rotasVisiveisGestao.length ? 'Limpar' : 'Todas'}
              </button>
            </div>
            <div className="veiculos-lista">
              {rotasVisiveisGestao.map(rota => (
                <div
                  key={rota.id}
                  className={`rota-card ${rotasSelecionadas.includes(rota.id) ? 'selected' : ''}`}
                  onClick={() => toggleRota(rota.id)}
                >
                  <div className="rota-header">
                    <div className="rota-nome-container">
                      <div 
                        className="rota-cor-indicator" 
                        style={{ backgroundColor: rota.cor }}
                      ></div>
                      <span className="rota-nome">{rota.nome}</span>
                    </div>
                    <input 
                      type="checkbox" 
                      checked={rotasSelecionadas.includes(rota.id)}
                      onChange={() => toggleRota(rota.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="rota-info">
                    <p><strong>Origem:</strong> {rota.origem}</p>
                    <p><strong>Destino:</strong> {rota.destino}</p>
                    <p><strong>Distância:</strong> {rota.distancia} km</p>
                    <p><strong>Tempo estimado:</strong> {rota.tempoEstimado}h</p>
                  </div>
                  <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 4 }}>
                    <button
                      type="button"
                      style={{ background: '#4caf50', color: '#fff', border: 'none', borderRadius: 4, padding: '0 8px', cursor: 'pointer' }}
                      onClick={() => editarRota(rota)}
                    >
                      Editar
                    </button>
                    {canManageFullMonitoring && (
                      <button
                        type="button"
                        style={{ background: '#e57373', color: '#fff', border: 'none', borderRadius: 4, padding: '0 8px', cursor: 'pointer' }}
                        onClick={() => deletarRota(rota.id)}
                      >
                        Excluir
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal Cadastro de Rota */}
      {showCadastroRota && (
        <div className="modal-overlay" onClick={() => setShowCadastroRota(false)}>
          <div
            className="modal-content route-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <span className="route-modal-main-icon"><FaRoute aria-hidden="true" /></span>
              <div>
                <h3>{rotaEmEdicao ? 'Editar Rota' : 'Cadastrar Nova Rota'}</h3>
                <p className="route-modal-subtitle">Defina origem, destino e pontos para o acompanhamento no mapa.</p>
              </div>
              <button className="modal-close" onClick={() => {
                setShowCadastroRota(false);
                setRotaEmEdicao(null);
                setNovaRota(criarNovaRotaVazia());
              }}>×</button>
            </div>
            <div className="modal-body route-modal-body">
              <div className="route-designer-shell">
                <div className="route-designer-left">
                  <section className="route-designer-card">
                    <h4><FaFileAlt aria-hidden="true" /> Informações Gerais</h4>
                    <div className="route-designer-grid">
                      <div className="form-group">
                        <label>Nome da Rota <span>*</span></label>
                        <div className="route-input-box route-counted-input">
                          <input
                            type="text"
                            value={novaRota.nome}
                            onChange={e => setNovaRota({...novaRota, nome: e.target.value})}
                            placeholder="Ex.: Rota São Paulo - Campinas"
                            maxLength={100}
                          />
                          <small>{novaRota.nome.length}/100</small>
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Descrição (opcional)</label>
                        <div className="route-input-box route-counted-input">
                          <input
                            type="text"
                            value={novaRota.descricao || ''}
                            onChange={e => setNovaRota({ ...novaRota, descricao: e.target.value })}
                            placeholder="Descrição ou observações sobre a rota"
                            maxLength={255}
                          />
                          <small>{(novaRota.descricao || '').length}/255</small>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="route-designer-card">
                    <h4><FaRoute aria-hidden="true" /> Origem e Destino</h4>
                    <div className="route-designer-grid">
                      <div className="form-group">
                        <label>Origem <span>*</span></label>
                        <div className="route-input-box route-icon-input">
                          <FaMapMarkerAlt aria-hidden="true" />
                          <input
                            type="text"
                            value={novaRota.origem}
                            onChange={e => setNovaRota({...novaRota, origem: e.target.value})}
                            onBlur={e => sincronizarOrigemDestinoNosPontos(e.target.value, novaRota.destino)}
                            placeholder="Endereço ou latitude, longitude"
                          />
                          <FaSearch aria-hidden="true" />
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Destino <span>*</span></label>
                        <div className="route-input-box route-icon-input">
                          <FaMapMarkerAlt aria-hidden="true" />
                          <input
                            type="text"
                            value={novaRota.destino}
                            onChange={e => setNovaRota({...novaRota, destino: e.target.value})}
                            onBlur={e => sincronizarOrigemDestinoNosPontos(novaRota.origem, e.target.value)}
                            placeholder="Endereço ou latitude, longitude"
                          />
                          <FaSearch aria-hidden="true" />
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="route-designer-card">
                    <div className="route-section-row">
                      <div>
                        <h4><FaFlag aria-hidden="true" /> Pontos de Parada (opcional)</h4>
                        <p>Adicione paradas intermediárias na rota na ordem desejada.</p>
                      </div>
                      <span className="route-selected-count">
                        {(novaRota.poligonosParada || []).length} selecionado(s)
                      </span>
                    </div>
                    <div className="route-poligono-selector">
                      <div className="route-poligono-search">
                        <FaSearch aria-hidden="true" />
                        <input
                          type="text"
                          value={filtroPoligonoParada}
                          onChange={e => setFiltroPoligonoParada(e.target.value)}
                          placeholder="Buscar poligono por nome ou tipo"
                        />
                      </div>

                      <div className="route-poligono-list">
                        {poligonosFiltradosParada.length === 0 ? (
                          <div className="route-empty-stops">
                            <FaRoad aria-hidden="true" />
                            <span>Nenhum poligono encontrado.</span>
                            <small>Cadastre ou ajuste o filtro para selecionar uma parada.</small>
                          </div>
                        ) : (
                          poligonosFiltradosParada.map(poligono => {
                            const selecionado = (novaRota.poligonosParada || [])
                              .some(parada => String(parada.id) === String(poligono.id));
                            return (
                              <button
                                key={poligono.id || poligono.nome}
                                type="button"
                                className={`route-poligono-option ${selecionado ? 'selected' : ''}`}
                                onClick={() => alternarPoligonoParada(poligono)}
                              >
                                <span className="route-poligono-check">{selecionado ? '✓' : ''}</span>
                                <span>
                                  <strong>{poligono.nome}</strong>
                                  <small>{poligono.tipo || 'outro'}</small>
                                </span>
                              </button>
                            );
                          })
                        )}
                      </div>

                      {(novaRota.poligonosParada || []).length > 0 && (
                        <div className="route-selected-stops">
                          {(novaRota.poligonosParada || []).map(parada => (
                            <span key={parada.id || parada.nome} className="route-selected-stop">
                              {parada.nome}
                              <button
                                type="button"
                                aria-label={`Remover parada ${parada.nome}`}
                                onClick={() => {
                                  setNovaRota(prev =>{
                                    const proximasParadas = (prev.poligonosParada || [])
                                      .filter(item => String(item.id) !== String(parada.id));
                                    return {
                                      ...prev,
                                      poligonosParada: proximasParadas,
                                      pontos: montarPontosComParadas(prev.pontos, proximasParadas),
                                    };
                                  });
                                }}
                              >
                                <FaTimes aria-hidden="true" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      <small>Clique em “Adicionar Parada” para incluir.</small>
                    </div>
                  </section>

                  <section className="route-designer-card">
                    <h4><FaClock aria-hidden="true" /> Resumo da Rota (estimativa)</h4>
                    <div className="route-mode-toggle" role="group" aria-label="Tipo da rota">
                      <button
                        type="button"
                        className={novaRota.tipoRota === 'ida' ? 'active' : ''}
                        onClick={() => setNovaRota(prev => ({ ...prev, tipoRota: 'ida', pontosDetalhados: [] }))}
                      >
                        Somente ida
                      </button>
                      <button
                        type="button"
                        className={novaRota.tipoRota === 'ida_volta' ? 'active' : ''}
                        onClick={() => setNovaRota(prev => ({ ...prev, tipoRota: 'ida_volta', pontosDetalhados: [] }))}
                      >
                        Ida e volta
                      </button>
                    </div>
                    <div className="route-estimate-grid">
                      <div className="route-estimate-card route-estimate-distance">
                        <FaRoad aria-hidden="true" />
                        <span>Distância Estimada</span>
                        <strong>{novaRota.distancia || '—'}</strong>
                        <em>km</em>
                      </div>
                      <div className="route-estimate-card route-estimate-time">
                        <FaClock aria-hidden="true" />
                        <span>Tempo Estimado</span>
                        <strong className="route-time-hhmm">{tempoEstimadoFormatado || '—'}</strong>
                        <em className="route-time-unit">hh:mm</em>
                        <strong>{novaRota.tempoEstimado || '—'}</strong>
                        <em>h</em>
                      </div>
                      <div className="route-estimate-card route-estimate-stops">
                        <FaFlag aria-hidden="true" />
                        <span>N° de Paradas</span>
                        <strong>{(novaRota.poligonosParada || []).length}</strong>
                      </div>
                    </div>
                    <div className="route-manual-metrics">
                      <div className="form-group">
                        <label>Distância Estimada (km)</label>
                        <input type="number" value={novaRota.distancia} onChange={e => setNovaRota({...novaRota, distancia: e.target.value})} placeholder="0" />
                      </div>
                      <div className="form-group">
                        <label>Tempo Estimado (hh:mm)</label>
                        <input type="text" value={tempoEstimadoFormatado} readOnly placeholder="00:00" />
                      </div>
                    </div>
                    <div className="route-color-row">
                      <label>Cor da Rota (no mapa)</label>
                      <div className="route-color-options">
                        {['#2196f3', '#22c55e', '#7c3aed', '#f97316', '#ef4444', '#06b6d4', '#64748b'].map((cor) => (
                          <button
                            key={cor}
                            type="button"
                            className={novaRota.cor === cor ? 'selected' : ''}
                            style={{ background: cor }}
                            onClick={() => setNovaRota({ ...novaRota, cor })}
                            aria-label={`Selecionar cor ${cor}`}
                          >
                            {novaRota.cor === cor && '✓'}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="route-info-note">
                      <FaInfoCircle aria-hidden="true" />
                      A distância e o tempo são estimativas e podem variar de acordo com o trajeto real.
                    </div>
                  </section>
                </div>

                <div className="route-designer-right">
                  <section className="route-designer-card route-map-card">
                    <h4><FaMapMarkerAlt aria-hidden="true" /> Mapa da Rota</h4>
                    <div className="route-map-shell">
                      <EditableRouteMap
                        pontos={pontosRotaPreview as PontoRotaEdicao[]}
                        cor={novaRota.cor || '#2196f3'}
                        onChange={() => {}}
                        onDetailedGeometryChange={(pontosDetalhados) =>
                          setNovaRota(prev => ({ ...prev, pontosDetalhados }))
                        }
                      />
                      <div className="route-map-controls">
                        <button type="button"><FaPlus aria-hidden="true" /></button>
                        <button type="button"><FaMinus aria-hidden="true" /></button>
                        <button type="button"><FaExpand aria-hidden="true" /></button>
                      </div>
                    </div>
                    <div className="route-map-summary">
                      <strong>Resumo do Trajeto</strong>
                      <div className="route-map-summary-grid">
                        <span><i className="origin" /> Origem: {novaRota.origem || '—'}</span>
                        <span><FaRoad aria-hidden="true" /> Distância: {novaRota.distancia || '—'} km</span>
                        <span><i className="stop" /> Paradas: {(novaRota.poligonosParada || []).length} ponto(s)</span>
                        <span className="route-map-time-hhmm"><FaClock aria-hidden="true" /> Tempo: {tempoEstimadoFormatado || '—'}</span>
                        <span><FaClock aria-hidden="true" /> Tempo: {novaRota.tempoEstimado || '—'} h</span>
                        <span><i className="destination" /> Destino: {novaRota.destino || '—'}</span>
                        <span><FaRoute aria-hidden="true" /> Trajeto: —</span>
                      </div>
                    </div>
                    <div className="route-warning-note">
                      <FaInfoCircle aria-hidden="true" />
                      Preencha origem e destino para gerar a rota no mapa.
                    </div>
                  </section>
                </div>
              </div>
              <div className="form-group">
                <label>Nome da Rota:</label>
                <input type="text" value={novaRota.nome} onChange={e => setNovaRota({...novaRota, nome: e.target.value})} placeholder="Ex: SP - RJ" />
              </div>
              <div className="form-group">
                <label>Link do Google Maps (opcional):</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="text"
                    value={novaRota.linkMaps || ''}
                    onChange={e => setNovaRota({ ...novaRota, linkMaps: e.target.value })}
                    placeholder="Cole o link da rota do Google Maps"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={importarRotaDoLink}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    <FaLink aria-hidden="true" />
                    Importar link
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label><FaMapMarkerAlt aria-hidden="true" /> Origem:</label>
                <input type="text" value={novaRota.origem} onChange={e => setNovaRota({...novaRota, origem: e.target.value})} placeholder="Endereço de origem" />
              </div>
              <div className="form-group">
                <label><FaMapMarkerAlt aria-hidden="true" /> Destino:</label>
                <input type="text" value={novaRota.destino} onChange={e => setNovaRota({...novaRota, destino: e.target.value})} placeholder="Endereço de destino" />
              </div>
              <div className="form-group">
                <label>Pontos de Parada (opcional):</label>
                <textarea value={novaRota.pontosParada} onChange={e => setNovaRota({...novaRota, pontosParada: e.target.value})} placeholder="Lista de pontos de parada" rows={3}></textarea>
              </div>
              <div className="form-group">
                <label><FaMapMarkedAlt aria-hidden="true" /> Pontos da Rota (Latitude/Longitude):</label>
                {novaRota.pontos.map((ponto, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 4, alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="Endereço ou referência"
                      value={ponto.endereco || ''}
                      onChange={e => {
                        const novosPontos = [...novaRota.pontos];
                        novosPontos[idx].endereco = e.target.value;
                        setNovaRota({ ...novaRota, pontos: novosPontos });
                      }}
                      onBlur={async e => {
                        const endereco = e.target.value;
                        if (endereco && (!ponto.lat || !ponto.lng)) {
                          const coords = await buscarLatLngPorEndereco(endereco);
                          const novosPontos = [...novaRota.pontos];
                          novosPontos[idx].lat = coords.lat;
                          novosPontos[idx].lng = coords.lng;
                          setNovaRota({ ...novaRota, pontos: novosPontos });
                        }
                      }}
                      style={{ width: 220 }}
                    />
                    <input
                      type="text"
                      placeholder="Latitude"
                      value={ponto.lat}
                      onChange={e => {
                        const novosPontos = [...novaRota.pontos];
                        novosPontos[idx].lat = e.target.value;
                        setNovaRota({ ...novaRota, pontos: novosPontos });
                      }}
                      style={{ width: 100 }}
                    />
                    <input
                      type="text"
                      placeholder="Longitude"
                      value={ponto.lng}
                      onChange={e => {
                        const novosPontos = [...novaRota.pontos];
                        novosPontos[idx].lng = e.target.value;
                        setNovaRota({ ...novaRota, pontos: novosPontos });
                      }}
                      style={{ width: 100 }}
                    />
                    <button
                      type="button"
                      style={{ background: '#e57373', color: '#fff', border: 'none', borderRadius: 4, padding: '0 8px', cursor: 'pointer' }}
                      onClick={() => {
                        const novosPontos = novaRota.pontos.filter((_, i) => i !== idx);
                        setNovaRota({ ...novaRota, pontos: novosPontos.length ? novosPontos : [{ lat: '', lng: '', endereco: '' }] });
                      }}
                      disabled={novaRota.pontos.length === 1}
                    >
                      <FaTrash aria-hidden="true" />
                      <span>Remover</span>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  style={{ background: '#2196f3', color: '#fff', border: 'none', borderRadius: 4, padding: '0 12px', marginTop: 4, cursor: 'pointer' }}
                  onClick={() => setNovaRota({ ...novaRota, pontos: [...novaRota.pontos, { lat: '', lng: '', endereco: '' }] })}
                >
                  <FaPlus aria-hidden="true" />
                  Adicionar Ponto
                </button>
              </div>
              <EditableRouteMap
                pontos={novaRota.pontos as PontoRotaEdicao[]}
                cor={novaRota.cor || '#2196f3'}
                onChange={(pontos) => setNovaRota(prev => ({ ...prev, pontos }))}
                onDetailedGeometryChange={(pontosDetalhados) =>
                  setNovaRota(prev => ({ ...prev, pontosDetalhados }))
                }
              />
              <div className="form-group">
                <label>Distância Estimada (km):</label>
                <input type="number" value={novaRota.distancia} onChange={e => setNovaRota({...novaRota, distancia: e.target.value})} placeholder="0" />
              </div>
              <div className="form-group">
                <label>Tempo Estimado (horas):</label>
                <input type="number" value={novaRota.tempoEstimado} onChange={e => setNovaRota({...novaRota, tempoEstimado: e.target.value})} placeholder="0" />
              </div>
              <div className="form-group">
                <label>Cor da Rota:</label>
                <input
                  type="color"
                  value={novaRota.cor || '#2196f3'}
                  onChange={e => setNovaRota({ ...novaRota, cor: e.target.value })}
                  style={{ width: 48, height: 32, border: 'none', background: 'none', cursor: 'pointer' }}
                />
              </div>
              <aside className="route-summary-panel">
                <div className="route-summary-title">
                  <span className="route-summary-icon"><FaRoute aria-hidden="true" /></span>
                  <strong>Resumo da Rota</strong>
                </div>
                <div className="route-summary-list">
                  <div className="route-summary-item route-summary-origin">
                    <span className="route-summary-dot" />
                    <div>
                      <strong>Origem</strong>
                      <p>{novaRota.origem || 'NÃ£o definida'}</p>
                    </div>
                  </div>
                  <div className="route-summary-item route-summary-destination">
                    <span className="route-summary-dot" />
                    <div>
                      <strong>Destino</strong>
                      <p>{novaRota.destino || 'NÃ£o definido'}</p>
                    </div>
                  </div>
                  <div className="route-summary-item route-summary-stops">
                    <span className="route-summary-dot" />
                    <div>
                      <strong>Pontos de Parada</strong>
                      <p>{novaRota.pontosParada ? novaRota.pontosParada.split(/[\n,;]/).filter(p => p.trim()).length : 0} ponto(s)</p>
                    </div>
                  </div>
                  <div className="route-summary-item route-summary-route">
                    <span className="route-summary-dot" />
                    <div>
                      <strong>Pontos da Rota</strong>
                      <p>{novaRota.pontos.filter(p => p.lat && p.lng).length} ponto(s)</p>
                    </div>
                  </div>
                </div>
                <div className="route-summary-divider" />
                <div className="route-summary-metrics">
                  <div>
                    <FaRoad aria-hidden="true" />
                    <span>DistÃ¢ncia Estimada</span>
                    <strong>{novaRota.distancia || 0} km</strong>
                  </div>
                  <div>
                    <FaClock aria-hidden="true" />
                    <span>Tempo Estimado</span>
                    <strong>{novaRota.tempoEstimado || 0} horas</strong>
                  </div>
                </div>
                <div className="route-summary-divider" />
                <div className="route-summary-color">
                  <span style={{ background: novaRota.cor || '#2196f3' }} />
                  <div>
                    <strong>Cor da Rota</strong>
                    <p>{novaRota.cor || '#2196f3'}</p>
                  </div>
                </div>
              </aside>
            </div>
            <div className="modal-footer route-modal-footer">
              <button className="btn-secondary" onClick={() => {
                setShowCadastroRota(false);
                setRotaEmEdicao(null);
                setNovaRota(criarNovaRotaVazia());
              }}>
                <FaTimes aria-hidden="true" />
                Cancelar
              </button>
              <button className="btn-primary" onClick={async () => {
                try {
                  const pontosSincronizados = await sincronizarOrigemDestinoNosPontos();
                  const pontosParaSalvar = montarPontosIdaVolta(pontosSincronizados, novaRota.tipoRota === 'ida_volta');

                  const pontosValidados = pontosParaSalvar
                    .filter(p => p.lat && p.lng)
                    .map(p => ({ lat: parseFloat(p.lat), lng: parseFloat(p.lng) }))
                    .filter(p => !isNaN(p.lat) && !isNaN(p.lng));

                  const dadosRota = {
                    ...novaRota,
                    cor: novaRota.cor || '#2196f3',
                    pontosParada: novaRota.poligonosParada || [],
                    tipoRota: novaRota.tipoRota,
                    pontos: pontosValidados,
                    pontosDetalhados:
                      Array.isArray(novaRota.pontosDetalhados) && novaRota.pontosDetalhados.length >= 2
                        ? novaRota.pontosDetalhados
                        : pontosValidados,
                  };
                  
                  if (rotaEmEdicao) {
                    await axios.put(`/api/monitoramento/rotas/${rotaEmEdicao}`, dadosRota);
                  } else {
                    await axios.post('/api/monitoramento/rotas', dadosRota);
                  }
                  
                  setShowCadastroRota(false);
                  setRotaEmEdicao(null);
                  setNovaRota(criarNovaRotaVazia());
                  fetchDados();
                } catch (err) {
                  console.error('Erro ao salvar rota:', err);
                  alert(rotaEmEdicao ? 'Erro ao atualizar rota!' : 'Erro ao cadastrar rota!');
                }
              }}>
                <FaSave aria-hidden="true" />
                Salvar Rota
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Cadastro de Operação */}
      {showCadastroOperacao && (
        <div className="modal-overlay" onClick={() => setShowCadastroOperacao(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <div className="modal-header">
              <h3>{isGerencialAccess ? 'Editar Veículos da Operação' : operacaoEmEdicao ? 'Editar Operação' : 'Cadastrar Operação'}</h3>
              <button className="close-btn" onClick={() => setShowCadastroOperacao(false)}>×</button>
            </div>
            <div className="modal-body">
              {!isGerencialAccess && (
              <div className="form-group">
                <label>Nome da Operação:</label>
                <input 
                  type="text" 
                  value={novaOperacao.nome} 
                  onChange={e => setNovaOperacao({...novaOperacao, nome: e.target.value})} 
                  placeholder="Ex: Volks, Brose, etc." 
                />
              </div>
              )}
              {!isGerencialAccess && (
              <div className="form-group">
                <label>Descrição (opcional):</label>
                <textarea 
                  value={novaOperacao.descricao} 
                  onChange={e => setNovaOperacao({...novaOperacao, descricao: e.target.value})} 
                  placeholder="Descrição da operação..."
                  rows={3}
                  style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                />
              </div>
              )}
              {!isGerencialAccess && (
              <div className="form-group">
                <label>Setor da Operação:</label>
                <select
                  value={novaOperacao.setorOperacao || ''}
                  onChange={e => setNovaOperacao({ ...novaOperacao, setorOperacao: e.target.value })}
                  style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd' }}
                >
                  <option value="">Selecione o setor</option>
                  {setoresOperacionais.map(setor => (
                    <option key={setor} value={setor}>{setor}</option>
                  ))}
                </select>
              </div>
              )}
              
              {/* Novos campos de Polígonos */}
              {!isGerencialAccess && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label style={{ fontSize: 16, fontWeight: 600, color: '#333', marginBottom: 8, display: 'block' }}>
                    📍 Polígono Origem
                  </label>
                  <select
                    value={novaOperacao.poligonoOrigem || ''}
                    onChange={e => setNovaOperacao({...novaOperacao, poligonoOrigem: e.target.value ? Number(e.target.value) : undefined})}
                    style={{ 
                      width: '100%', 
                      padding: '10px 14px', 
                      borderRadius: 6, 
                      border: '2px solid #e0e0e0',
                      fontSize: 14,
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Selecione o polígono de origem</option>
                    {poligonos
                      .filter(p => p.ativa !== false)
                      .map(poligono => (
                        <option key={poligono.id} value={poligono.id}>
                          {poligono.nome} ({poligono.tipo})
                        </option>
                      ))
                    }
                  </select>
                </div>
                
                <div className="form-group">
                  <label style={{ fontSize: 16, fontWeight: 600, color: '#333', marginBottom: 8, display: 'block' }}>
                    🎯 Polígono Destino
                  </label>
                  <select
                    value={novaOperacao.poligonoDestino || ''}
                    onChange={e => setNovaOperacao({...novaOperacao, poligonoDestino: e.target.value ? Number(e.target.value) : undefined})}
                    style={{ 
                      width: '100%', 
                      padding: '10px 14px', 
                      borderRadius: 6, 
                      border: '2px solid #e0e0e0',
                      fontSize: 14,
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Selecione o polígono de destino</option>
                    {poligonos
                      .filter(p => p.ativa !== false && p.id !== novaOperacao.poligonoOrigem)
                      .map(poligono => (
                        <option key={poligono.id} value={poligono.id}>
                          {poligono.nome} ({poligono.tipo})
                        </option>
                      ))
                    }
                  </select>
                </div>
              </div>
              )}
              
              {!isGerencialAccess && (
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <label style={{ fontSize: 16, fontWeight: 600, color: '#333', margin: 0 }}>🗺️ Rotas da Operação</label>
                  <span style={{ fontSize: 14, color: '#2196f3', fontWeight: 500 }}>
                    {novaOperacao.rotas.length} {novaOperacao.rotas.length === 1 ? 'selecionada' : 'selecionadas'}
                  </span>
                </div>
                <input 
                  type="text" 
                  placeholder="🔍 Buscar rota por nome, origem ou destino..."
                  value={filtroRotasModal}
                  onChange={e => setFiltroRotasModal(e.target.value)}
                  style={{ 
                    padding: '10px 14px', 
                    borderRadius: 6, 
                    border: '2px solid #e0e0e0',
                    fontSize: 14,
                    width: '100%',
                    marginBottom: 12,
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={e => e.target.style.borderColor = '#2196f3'}
                  onBlur={e => e.target.style.borderColor = '#e0e0e0'}
                />
                <div style={{ 
                  maxHeight: 280, 
                  overflowY: 'auto', 
                  border: '2px solid #e0e0e0', 
                  borderRadius: 8, 
                  padding: 12, 
                  background: '#f8f9fa' 
                }}>
                  {rotas.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
                      <div style={{ fontSize: 64, marginBottom: 16 }}>🗺️</div>
                      <div style={{ fontSize: 16 }}>Nenhuma rota cadastrada</div>
                      <button 
                        className="btn-primary" 
                        onClick={() => {
                          setShowListaOperacoes(false);
                          setShowCadastroOperacao(true);
                        }}
                        style={{ marginTop: 20 }}
                      >
                        + Cadastrar Primeira Rota
                      </button>
                    </div>
                  ) : rotas
                      .filter(rota => 
                        filtroRotasModal === '' ||
                        rota.nome.toLowerCase().includes(filtroRotasModal.toLowerCase()) ||
                        rota.origem.toLowerCase().includes(filtroRotasModal.toLowerCase()) ||
                        rota.destino.toLowerCase().includes(filtroRotasModal.toLowerCase())
                      )
                      .map(rota => {
                        const isSelected = novaOperacao.rotas.includes(rota.id);
                        return (
                          <label
                            key={rota.id}
                            htmlFor={`rota-${rota.id}`}
                            style={{ 
                              display: 'block',
                              background: isSelected ? '#e3f2fd' : 'white',
                              padding: '14px 16px',
                              marginBottom: 10,
                              borderRadius: 8,
                              border: isSelected ? '2px solid #2196f3' : '2px solid #e0e0e0',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              boxShadow: isSelected ? '0 2px 8px rgba(33, 150, 243, 0.2)' : '0 1px 3px rgba(0,0,0,0.05)'
                            }}
                            onMouseEnter={e => {
                              if (!isSelected) {
                                e.currentTarget.style.borderColor = '#2196f3';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(33, 150, 243, 0.15)';
                              }
                            }}
                            onMouseLeave={e => {
                              if (!isSelected) {
                                e.currentTarget.style.borderColor = '#e0e0e0';
                                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                              }
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                              <input 
                                type="checkbox" 
                                id={`rota-${rota.id}`}
                                checked={isSelected}
                                onChange={() => toggleRota(rota.id)}
                                onClick={(e) => e.stopPropagation()}
                                style={{ marginTop: 4, width: 18, height: 18, cursor: 'pointer' }}
                              />
                              <div style={{ flex: 1 }}>
                                <div style={{ 
                                  fontSize: 15, 
                                  fontWeight: 600, 
                                  color: isSelected ? '#1976d2' : '#333',
                                  marginBottom: 6
                                }}>
                                  {rota.nome}
                                </div>
                                <div style={{ 
                                  fontSize: 13, 
                                  color: '#666',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6
                                }}>
                                  <span style={{ color: '#4caf50', fontWeight: 600 }}>📍</span>
                                  <span>{rota.origem}</span>
                                  <span style={{ color: '#2196f3', fontWeight: 600 }}>→</span>
                                  <span>{rota.destino}</span>
                                </div>
                              </div>
                            </div>
                          </label>
                        );
                      })
                  }
                </div>
              </div>
              )}
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <label style={{ fontSize: 16, fontWeight: 600, color: '#333', margin: 0 }}>🚛 Veículos da Operação</label>
                  <span style={{ fontSize: 14, color: '#2196f3', fontWeight: 500 }}>
                    {novaOperacao.veiculos.length} {novaOperacao.veiculos.length === 1 ? 'selecionado' : 'selecionados'}
                  </span>
                </div>
                <input 
                  type="text" 
                  placeholder="🔍 Buscar veículo por placa ou motorista..."
                  value={filtroVeiculosModal}
                  onChange={e => setFiltroVeiculosModal(e.target.value)}
                  style={{ 
                    padding: '10px 14px', 
                    borderRadius: 6, 
                    border: '2px solid #e0e0e0',
                    fontSize: 14,
                    width: '100%',
                    marginBottom: 12,
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={e => e.target.style.borderColor = '#2196f3'}
                  onBlur={e => e.target.style.borderColor = '#e0e0e0'}
                />
                <div style={{ 
                  maxHeight: 280, 
                  overflowY: 'auto', 
                  border: '2px solid #e0e0e0', 
                  borderRadius: 8, 
                  padding: 12, 
                  background: '#f8f9fa' 
                }}>
                  {veiculos.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                      <div style={{ fontSize: 48, marginBottom: 12 }}>🚛</div>
                      <div style={{ fontSize: 14 }}>Nenhum veículo disponível</div>
                    </div>
                  ) : veiculosDisponiveisEdicaoOperacao
                      .filter(veiculo => 
                        filtroVeiculosModal === '' ||
                        veiculo.placa.toLowerCase().includes(filtroVeiculosModal.toLowerCase()) ||
                        (veiculo.motorista && veiculo.motorista.toLowerCase().includes(filtroVeiculosModal.toLowerCase()))
                      )
                      .map(veiculo => {
                        const isSelected = novaOperacao.veiculos.includes(veiculo.placa);
                        return (
                          <label
                            key={veiculo.placa}
                            htmlFor={`veiculo-${veiculo.placa}`}
                            style={{ 
                              display: 'block',
                              background: isSelected ? '#e3f2fd' : 'white',
                              padding: '14px 16px',
                              marginBottom: 10,
                              borderRadius: 8,
                              border: isSelected ? '2px solid #2196f3' : '2px solid #e0e0e0',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              boxShadow: isSelected ? '0 2px 8px rgba(33, 150, 243, 0.2)' : '0 1px 3px rgba(0,0,0,0.05)'
                            }}
                            onMouseEnter={e => {
                              if (!isSelected) {
                                e.currentTarget.style.borderColor = '#2196f3';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(33, 150, 243, 0.15)';
                              }
                            }}
                            onMouseLeave={e => {
                              if (!isSelected) {
                                e.currentTarget.style.borderColor = '#e0e0e0';
                                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                              }
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <input 
                                type="checkbox" 
                                id={`veiculo-${veiculo.placa}`}
                                checked={isSelected}
                                onChange={() => {
                                  setNovaOperacao(prev => ({
                                    ...prev,
                                    veiculos: prev.veiculos.includes(veiculo.placa) 
                                      ? prev.veiculos.filter(placa => placa !== veiculo.placa)
                                      : [...prev.veiculos, veiculo.placa]
                                  }));
                                }}
                                style={{ width: 18, height: 18, cursor: 'pointer' }}
                              />
                              <div style={{ flex: 1 }}>
                                <div style={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 10,
                                  marginBottom: 4
                                }}>
                                  <span style={{
                                    background: isSelected ? '#1976d2' : '#2196f3',
                                    color: 'white',
                                    padding: '4px 12px',
                                    borderRadius: 4,
                                    fontSize: 14,
                                    fontWeight: 600,
                                    letterSpacing: '0.5px'
                                  }}>
                                    {veiculo.placa}
                                  </span>
                                </div>
                                <div style={{ 
                                  fontSize: 13, 
                                  color: '#666',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6
                                }}>
                                  <span>👤</span>
                                  <span>{veiculo.motorista || 'Sem informação'}</span>
                                </div>
                              </div>
                            </div>
                          </label>
                        );
                      })
                  }
                </div>
              </div>
              {!isGerencialAccess && (
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input 
                    type="checkbox" 
                    checked={novaOperacao.ativa}
                    onChange={() => setNovaOperacao({...novaOperacao, ativa: !novaOperacao.ativa})}
                    style={{ marginRight: 8 }}
                  />
                  Operação Ativa
                </label>
              </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => {
                setShowCadastroOperacao(false);
                setOperacaoEmEdicao(null);
                setNovaOperacao({ nome: '', descricao: '', setorOperacao: '', rotas: [], veiculos: [], poligonoOrigem: undefined, poligonoDestino: undefined, ativa: true });
                setFiltroRotasModal('');
                setFiltroVeiculosModal('');
              }}>
                Cancelar
              </button>
              <button className="btn-primary" onClick={async () => {
                try {
                  if (isGerencialAccess && !operacaoEmEdicao) {
                    alert('Acesso gerencial pode apenas editar veículos de operações existentes.');
                    return;
                  }
                  if (!novaOperacao.nome.trim()) {
                    alert('Por favor, informe o nome da operação!');
                    return;
                  }
                  if (!(novaOperacao.setorOperacao || '').trim()) {
                    alert('Por favor, selecione o setor da operação!');
                    return;
                  }
                  
                  // Sanitizar dados antes de enviar
                  const dadosOperacao = {
                    nome: novaOperacao.nome.trim(),
                    descricao: novaOperacao.descricao?.trim() || '',
                    setorOperacao: novaOperacao.setorOperacao,
                    rotas: Array.isArray(novaOperacao.rotas) ? novaOperacao.rotas : [],
                    veiculos: Array.isArray(novaOperacao.veiculos) ? novaOperacao.veiculos : [],
                    poligonoOrigem: novaOperacao.poligonoOrigem,
                    poligonoDestino: novaOperacao.poligonoDestino,
                    ativa: Boolean(novaOperacao.ativa)
                  };
                  
                  console.log('Enviando operação:', dadosOperacao);
                  
                  if (operacaoEmEdicao) {
                    await axios.put(`/api/monitoramento/operacoes/${operacaoEmEdicao}`, dadosOperacao);
                    alert(isGerencialAccess ? 'Veículos da operação atualizados com sucesso!' : 'Operação atualizada com sucesso!');
                  } else {
                    await axios.post('/api/monitoramento/operacoes', dadosOperacao);
                    alert('Operação cadastrada com sucesso!');
                  }
                  
                  setShowCadastroOperacao(false);
                  setOperacaoEmEdicao(null);
                  setNovaOperacao({ 
                    nome: '', 
                    descricao: '', 
                    setorOperacao: '',
                    rotas: [], 
                    veiculos: [], 
                    poligonoOrigem: undefined,
                    poligonoDestino: undefined,
                    ativa: true 
                  });
                  setFiltroRotasModal('');
                  setFiltroVeiculosModal('');
                  fetchDados();
                } catch (err) {
                  console.error('Erro ao salvar operação:', err);
                  console.error('Dados que causaram erro:', novaOperacao);
                  alert(operacaoEmEdicao ? (isGerencialAccess ? 'Erro ao atualizar veículos da operação!' : 'Erro ao atualizar operação!') : 'Erro ao cadastrar operação!');
                }
              }}>
                {isGerencialAccess ? 'Salvar Veículos' : operacaoEmEdicao ? 'Atualizar Operação' : 'Salvar Operação'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Lista/Gerenciar Operações */}
      {showListaOperacoes && (
        <div className="modal-overlay" onClick={() => setShowListaOperacoes(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 900, maxHeight: '80vh' }}>
            <div className="modal-header">
              <h3>🏭 Gerenciar Operações</h3>
              <button className="close-btn" onClick={() => setShowListaOperacoes(false)}>×</button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto', maxHeight: 'calc(80vh - 150px)' }}>
              {operacoesDisponiveis.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
                  <div style={{ fontSize: 64, marginBottom: 16 }}>🏭</div>
                  <div style={{ fontSize: 16 }}>Nenhuma operação cadastrada</div>
                  {!isGerencialAccess && (
                    <button 
                      className="btn-primary" 
                      onClick={() => {
                        setShowListaOperacoes(false);
                        setShowCadastroOperacao(true);
                      }}
                      style={{ marginTop: 20 }}
                    >
                      + Cadastrar Primeira Operação
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {operacoesDisponiveis.map(operacao => (
                    <div 
                      key={operacao.id}
                      style={{
                        border: '2px solid #e0e0e0',
                        borderRadius: 8,
                        padding: 20,
                        background: operacao.ativa ? 'white' : '#f5f5f5'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                            <h4 style={{ margin: 0, fontSize: 18, color: '#333' }}>{operacao.nome}</h4>
                            {!isGerencialAccess && operacao.ativa ? (
                              <span style={{ background: '#4caf50', color: 'white', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>
                                ✓ Ativa
                              </span>
                            ) : !isGerencialAccess ? (
                              <span style={{ background: '#999', color: 'white', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>
                                ✕ Inativa
                              </span>
                            ) : null}
                          </div>
                          {!isGerencialAccess && operacao.descricao && (
                            <p style={{ margin: '8px 0', color: '#666', fontSize: 14 }}>{operacao.descricao}</p>
                          )}
                          {operacao.setorOperacao && (
                            <p style={{ margin: '6px 0', color: '#444', fontSize: 13 }}>
                              <strong>Setor:</strong> {operacao.setorOperacao}
                            </p>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button 
                            className="btn-secondary"
                            onClick={() => {
                              setOperacaoEmEdicao(operacao.id!);
                              setNovaOperacao({
                                nome: operacao.nome,
                                descricao: operacao.descricao || '',
                                setorOperacao: operacao.setorOperacao || '',
                                rotas: operacao.rotas,
                                veiculos: operacao.veiculos,
                                poligonoOrigem: operacao.poligonoOrigem,
                                poligonoDestino: operacao.poligonoDestino,
                                ativa: operacao.ativa
                              });
                              setShowListaOperacoes(false);
                              setShowCadastroOperacao(true);
                            }}
                            style={{ fontSize: 12, padding: '6px 12px' }}
                          >
                            {isGerencialAccess ? '✏️ Editar Veículos' : '✏️ Editar'}
                          </button>
                          {!isGerencialAccess && (
                            <button 
                              className="btn-secondary"
                              onClick={async () => {
                                if (window.confirm(`Deseja realmente excluir a operação "${operacao.nome}"?`)) {
                                  try {
                                    await axios.delete(`/api/monitoramento/operacoes/${operacao.id}`);
                                    fetchDados();
                                    alert('Operação excluída com sucesso!');
                                  } catch (err) {
                                    alert('Erro ao excluir operação!');
                                  }
                                }
                              }}
                              style={{ fontSize: 12, padding: '6px 12px', background: '#f44336', color: 'white' }}
                            >
                              🗑️ Excluir
                            </button>
                          )}
                        </div>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: isGerencialAccess ? '1fr' : '1fr 1fr 1fr', gap: 16, marginTop: 16 }}>
                        {!isGerencialAccess && (
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#666', marginBottom: 8 }}>
                            🗺️ Rotas ({operacao.rotas.length})
                          </div>
                          <div style={{ background: '#f8f9fa', padding: 10, borderRadius: 6, maxHeight: 120, overflowY: 'auto' }}>
                            {operacao.rotas.length === 0 ? (
                              <span style={{ fontSize: 12, color: '#999' }}>Nenhuma rota vinculada</span>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {operacao.rotas.map(rotaId => {
                                  const rota = rotas.find(r => r.id === rotaId);
                                  return rota ? (
                                    <div key={rotaId} style={{ fontSize: 12, color: '#333', padding: '4px 8px', background: 'white', borderRadius: 4 }}>
                                      {rota.nome}
                                    </div>
                                  ) : null;
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                        )}
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#666', marginBottom: 8 }}>
                            🚛 Veículos ({operacao.veiculos.length})
                          </div>
                          <div style={{ background: '#f8f9fa', padding: 10, borderRadius: 6, maxHeight: 120, overflowY: 'auto' }}>
                            {operacao.veiculos.length === 0 ? (
                              <span style={{ fontSize: 12, color: '#999' }}>Nenhum veículo vinculado</span>
                            ) : (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {operacao.veiculos.map(placa => {
                                  const veiculo = veiculos.find(v => v.placa === placa);
                                  return veiculo ? (
                                    <span key={placa} style={{ 
                                      fontSize: 11, 
                                      background: '#2196f3', 
                                      color: 'white', 
                                      padding: '3px 8px', 
                                      borderRadius: 4,
                                      fontWeight: 600
                                    }}>
                                      {veiculo.placa}
                                    </span>
                                  ) : null;
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                        {!isGerencialAccess && (
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#666', marginBottom: 8 }}>
                            📍 Polígono Origem/Destino
                          </div>
                          <div style={{ background: '#f8f9fa', padding: 10, borderRadius: 6, minHeight: 80 }}>
                            {!operacao.poligonoOrigem && !operacao.poligonoDestino ? (
                              <span style={{ fontSize: 12, color: '#999' }}>Nenhum polígono vinculado</span>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {operacao.poligonoOrigem && (
                                  <div style={{ fontSize: 12 }}>
                                    <span style={{ color: '#4caf50', fontWeight: 600 }}>📍 Origem:</span>{' '}
                                    <span style={{ background: 'white', padding: '2px 8px', borderRadius: 4 }}>
                                      {poligonos.find(p => p.id === operacao.poligonoOrigem)?.nome || `ID ${operacao.poligonoOrigem}`}
                                    </span>
                                  </div>
                                )}
                                {operacao.poligonoDestino && (
                                  <div style={{ fontSize: 12 }}>
                                    <span style={{ color: '#f44336', fontWeight: 600 }}>🎯 Destino:</span>{' '}
                                    <span style={{ background: 'white', padding: '2px 8px', borderRadius: 4 }}>
                                      {poligonos.find(p => p.id === operacao.poligonoDestino)?.nome || `ID ${operacao.poligonoDestino}`}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowListaOperacoes(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {showModalDeparaPlacas && (
        <div className="modal-overlay" onClick={() => setShowModalDeparaPlacas(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 900 }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>🔁 Depara de Placas</h3>
              <button className="close-btn" onClick={() => setShowModalDeparaPlacas(false)}>×</button>
            </div>

            <div className="modal-body">
              <p style={{ marginTop: 0, color: '#555' }}>
                Use para corrigir placas vindas do rastreador. Exemplo: DBO4246 -&gt; FKS1D64.
              </p>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                <input
                  type="text"
                  placeholder="Placa origem (rastreador)"
                  value={deparaOrigem}
                  onChange={e => setDeparaOrigem(e.target.value.toUpperCase())}
                  maxLength={10}
                  style={{ flex: 1, minWidth: 220, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                />
                <input
                  type="text"
                  placeholder="Placa destino (correta)"
                  value={deparaDestino}
                  onChange={e => setDeparaDestino(e.target.value.toUpperCase())}
                  maxLength={10}
                  style={{ flex: 1, minWidth: 220, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                />
                <button
                  className="btn-primary"
                  onClick={salvarDeparaPlacas}
                  disabled={salvandoDepara}
                  style={{ minWidth: 160 }}
                >
                  {salvandoDepara ? 'Salvando...' : 'Salvar Depara'}
                </button>
              </div>

              <div style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e6e6e6' }}>Origem</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e6e6e6' }}>Destino</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e6e6e6' }}>Atualizacao</th>
                      <th style={{ textAlign: 'right', padding: '10px 12px', borderBottom: '1px solid #e6e6e6' }}>Acao</th>
                    </tr>
                  </thead>
                  <tbody>
                    {carregandoDepara && (
                      <tr>
                        <td colSpan={4} style={{ padding: 12, color: '#666' }}>Carregando deparas...</td>
                      </tr>
                    )}
                    {!carregandoDepara && deparaPlacas.length === 0 && (
                      <tr>
                        <td colSpan={4} style={{ padding: 12, color: '#666' }}>Nenhum depara cadastrado.</td>
                      </tr>
                    )}
                    {!carregandoDepara && deparaPlacas.map(item => (
                      <tr key={item.cd_depara}>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f1f1', fontWeight: 600 }}>{item.nm_placa_origem}</td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f1f1', fontWeight: 600 }}>{item.nm_placa_destino}</td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f1f1', color: '#666' }}>
                          {item.dt_atualizacao ? formatarDataSemTimezone(item.dt_atualizacao) : '-'}
                        </td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f1f1', textAlign: 'right' }}>
                          <button
                            className="btn-secondary"
                            style={{ background: '#f44336', color: 'white' }}
                            onClick={() => excluirDeparaPlacas(item.cd_depara)}
                          >
                            Excluir
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowModalDeparaPlacas(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Proximidade */}
      {showModalProximidade && (
        <div className="modal-overlay" onClick={() => veiculosMaisProximos.length === 0 && setShowModalProximidade(false)}>
          <div className="modal-content modal-fullscreen" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <h3>🔍 Buscar Veículos Mais Próximos</h3>
              <button 
                className="modal-close" 
                onClick={limparProximidade}
                style={{
                  background: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  fontSize: 24,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  fontWeight: 700
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = '#d32f2f';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = '#f44336';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body" style={{ flex: 1, overflowY: 'auto', padding: '40px' }}>
              {/* Seção de Busca */}
              <div style={{ 
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ebf0 100%)',
                padding: '40px',
                borderRadius: 16,
                marginBottom: 30,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }}>
                <label style={{ 
                  fontWeight: 700, 
                  marginBottom: 20, 
                  display: 'block' , 
                  fontSize: 20,
                  color: '#333',
                  textAlign: 'center'
                }}>
                  🚛 Digite a placa do veículo de referência
                </label>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: 20,
                  maxWidth: 800,
                  margin: '0 auto'
                }}>
                  <input
                    type="text"
                    value={placaProximidade}
                    onChange={e => setPlacaProximidade(e.target.value.toUpperCase())}
                    placeholder="ABC1234"
                    maxLength={7}
                    autoFocus
                    style={{
                      padding: '24px 32px',
                      border: '3px solid #2196f3',
                      borderRadius: 12,
                      fontSize: 32,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '4px',
                      textAlign: 'center',
                      outline: 'none',
                      transition: 'all 0.3s',
                      color: '#1565c0',
                      background: '#fff',
                      boxShadow: '0 4px 12px rgba(33, 150, 243, 0.15)',
                      fontFamily: 'monospace'
                    }}
                    onKeyPress={e => e.key === 'Enter' && buscarVeiculoMaisProximo()}
                    onFocus={e => {
                      e.target.style.borderColor = '#1565c0';
                      e.target.style.boxShadow = '0 8px 24px rgba(33, 150, 243, 0.3)';
                      e.target.style.transform = 'scale(1.02)';
                    }}
                    onBlur={e => {
                      e.target.style.borderColor = '#2196f3';
                      e.target.style.boxShadow = '0 4px 12px rgba(33, 150, 243, 0.15)';
                      e.target.style.transform = 'scale(1)';
                    }}
                  />
                  <button
                    className="btn-primary"
                    onClick={buscarVeiculoMaisProximo}
                    disabled={!placaProximidade.trim()}
                    style={{ 
                      padding: '20px 48px',
                      fontSize: 20,
                      fontWeight: 700,
                      borderRadius: 12,
                      background: placaProximidade.trim() 
                        ? 'linear-gradient(135deg, #2196f3 0%, #1565c0 100%)' 
                        : '#ccc',
                      border: 'none',
                      color: 'white',
                      cursor: placaProximidade.trim() ? 'pointer' : 'not-allowed',
                      transition: 'all 0.3s',
                      boxShadow: placaProximidade.trim() 
                        ? '0 4px 16px rgba(33, 150, 243, 0.4)' 
                        : 'none',
                      transform: 'scale(1)'
                    }}
                    onMouseEnter={e => {
                      if (placaProximidade.trim()) {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(33, 150, 243, 0.5)';
                      }
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = placaProximidade.trim() 
                        ? '0 4px 16px rgba(33, 150, 243, 0.4)' 
                        : 'none';
                    }}
                  >
                    🔍 Pesquisar Veículos Próximos
                  </button>
                </div>
              </div>

              {veiculoReferencia && veiculosMaisProximos.length > 0 && (
                <div style={{ marginTop: 24 }}>
                  <h4 style={{ margin: 0, marginBottom: 16, color: '#2e7d32', fontSize: 16 }}>
                    ✅ {veiculosMaisProximos.length} veículo{veiculosMaisProximos.length > 1 ? 's' : ''} encontrado{veiculosMaisProximos.length > 1 ? 's' : ''}!
                  </h4>
                  
                  {/* Card do Veículo de Referência */}
                  <div style={{ 
                    background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)', 
                    padding: 20, 
                    borderRadius: 12, 
                    marginBottom: 20,
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)'
                  }}>
                    <div style={{ fontSize: 13, marginBottom: 6, opacity: 0.9 }}>📍 Veículo de Referência</div>
                    <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 10 }}>{veiculoReferencia.placa}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, fontSize: 14 }}>
                      <div>
                        <div style={{ opacity: 0.8, fontSize: 12 }}>Motorista</div>
                        <div style={{ fontWeight: 600 }}>{veiculoReferencia.motorista || 'N/A'}</div>
                      </div>
                      <div>
                        <div style={{ opacity: 0.8, fontSize: 12 }}>Status</div>
                        <div style={{ fontWeight: 600 }}>{getStatusLabel(veiculoReferencia.status)}</div>
                      </div>
                      <div>
                        <div style={{ opacity: 0.8, fontSize: 12 }}>Sentido</div>
                        <div style={{ fontWeight: 600 }}>
                          {veiculoReferencia.sentidoRota === 'ida' ? '➡️ Ida' : 
                           veiculoReferencia.sentidoRota === 'volta' ? '⬅️ Volta' : '❓ Desconhecido'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Lista dos 3 Veículos Mais Próximos */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {veiculosMaisProximos.map((item, index) => (
                      <div 
                        key={item.veiculo.id}
                        style={{ 
                          background: 'white', 
                          padding: 18, 
                          borderRadius: 10, 
                          border: `3px solid ${index === 0 ? '#4caf50' : index === 1 ? '#ff9800' : '#9c27b0'}`,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          transition: 'all 0.2s',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                          {/* Posição */}
                          <div style={{ 
                            width: 50, 
                            height: 50, 
                            borderRadius: '50%', 
                            background: index === 0 ? '#4caf50' : index === 1 ? '#ff9800' : '#9c27b0',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 24,
                            fontWeight: 700,
                            flexShrink: 0
                          }}>
                            {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                          </div>

                          {/* Info do Veículo */}
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                              <div style={{ fontSize: 20, fontWeight: 700, color: '#333' }}>
                                {item.veiculo.placa}
                              </div>
                              <div style={{ 
                                background: '#e3f2fd', 
                                color: '#1976d2', 
                                padding: '4px 12px', 
                                borderRadius: 20,
                                fontSize: 13,
                                fontWeight: 600,
                                letterSpacing: '0.5px'
                              }}>
                                ⬅️ Volta
                              </div>
                              <div style={{ 
                                background: '#fff3e0', 
                                color: '#f57c00', 
                                padding: '4px 12px', 
                                borderRadius: 20,
                                fontSize: 14,
                                fontWeight: 700
                              }}>
                                📏 {item.distancia.toFixed(2)} km
                              </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, fontSize: 13, color: '#666' }}>
                              <div>
                                <strong>Motorista:</strong> {item.veiculo.motorista || 'N/A'}
                              </div>
                              <div>
                                <strong>Telefone:</strong> {item.veiculo.telefones && item.veiculo.telefones.length > 0 ? item.veiculo.telefones[0] : 'N/A'}
                              </div>
                              <div>
                                <strong>Status:</strong> {getStatusLabel(item.veiculo.status)}
                              </div>
                              <div>
                                <strong>Tempo Parado:</strong>{' '}
                                {(item.veiculo.status === 'parado' || (item.veiculo.status === 'fora_de_rota' && Number(item.veiculo.velocidade || 0) === 0)) ? (
                                  <span style={{ color: '#f57c00', fontWeight: 700 }}>
                                    {item.veiculo.tempoParado 
                                      ? item.veiculo.tempoParado >= 60 
                                        ? `${Math.floor(item.veiculo.tempoParado / 60)}h ${item.veiculo.tempoParado % 60}min`
                                        : `${item.veiculo.tempoParado} min`
                                      : '0 min'}
                                  </span>
                                ) : (
                                  '-'
                                )}
                              </div>
                              <div>
                                <strong>Velocidade:</strong> {item.veiculo.velocidade} km/h
                              </div>
                            </div>
                            {item.veiculo.localizacao?.endereco && (
                              <div style={{ marginTop: 8, fontSize: 12, color: '#888', fontStyle: 'italic' }}>
                                📍 {item.veiculo.localizacao.endereco}
                              </div>
                            )}
                          </div>

                          {/* Botão Ver no Mapa */}
                          <button
                            className="btn-primary"
                            onClick={() => {
                              setVeiculoSelecionado(item.veiculo);
                              setShowModalProximidade(false);
                            }}
                            style={{ 
                              padding: '10px 20px',
                              fontSize: 14,
                              whiteSpace: 'nowrap',
                              minWidth: 120
                            }}
                          >
                            📍 Ver no Mapa
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
                    <button
                      className="btn-secondary"
                      onClick={() => {
                        setPlacaProximidade('');
                        setVeiculoReferencia(null);
                        setVeiculosMaisProximos([]);
                      }}
                      style={{ flex: 1, padding: '12px' }}
                    >
                      🔄 Nova Busca
                    </button>
                  </div>
                </div>
              )}

              {veiculoReferencia && veiculosMaisProximos.length === 0 && (
                <div style={{ marginTop: 20, padding: 20, background: '#fff3e0', borderRadius: 8, border: '2px solid #ff9800' }}>
                  <p style={{ margin: 0, color: '#e65100', fontSize: 15, textAlign: 'center' }}>
                    ⚠️ Nenhum veículo com sentido "Volta" encontrado no momento.
                  </p>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ flexShrink: 0 }}>
              <button className="btn-secondary" onClick={limparProximidade} style={{ padding: '12px 32px', fontSize: 16 }}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default MonitoramentoModule;
